/**
 * Procedural world generation — docs/05-content-plan.md §1, docs/01 §3.
 *
 * ONE GENERATOR, TWO WORLDS. Blended and Layered are not two systems; they are
 * two settings of `cohesion`. Everything downstream reads cohesion and nothing
 * reads `mode`:
 *
 *   biome set size    high cohesion -> 2-3 palettes for the whole run
 *   region size       high cohesion -> small regions, so palettes interleave
 *   room-level blend  high cohesion -> a room may take its neighbour's palette
 *   contamination     low  cohesion -> 15-25% foreign materials and enemies
 *
 * That is the affordance that makes shipping two world structures affordable,
 * and it is the reason there is exactly one `generateChunk` below.
 *
 * DETERMINISM. A chunk is a pure function of (seed, cx, cy). Nothing reads
 * generation order, nothing carries state between chunks, and every jittered
 * quantity comes from `hash2` keyed on the coordinate that *owns* it — a
 * corridor's crossing point is keyed on the shared edge, so both chunks compute
 * the same number without talking. Chunks therefore regenerate byte-identically
 * after eviction, which is what makes the save format a seed plus a diff log.
 *
 * MACRO LAYOUT is the herringbone Wang tile layer in rooms.ts. This file owns
 * the cave noise, the corridor router, materialisation, and the structural
 * annotation.
 */

import { Rng, hash2, hash2f } from '../core/rng';
import { fbm2, ridge2, smoothstep } from '../core/math';
import { MAT, registerMaterials } from '../sim/materialData';
import { Flag, M, VOID } from '../sim/materials';
import {
  CELLS_PER_CHUNK,
  CHUNK_SHIFT,
  CHUNK_SIZE,
  type Chunk,
  type ChunkGenerator,
} from '../sim/world';
import { MAIN_PATH, SIDE_CONTENT, surfacePalette, type BiomeDef } from './biomes';
import { checkWorldCoord } from './coordGuard';
import {
  GREAT_TREE,
  TERRACE,
  TREE_POOL,
} from './greatTree';
import {
  MOUNTAIN_BOUNDS,
  mountainGround,
} from './mountain';
import { LANDMARKS, stampLandmarkDress, stampLandmarkKinds } from './landmarks';
import {
  NEUTRAL_WORLD_MODIFIERS,
  isNeutral,
  type WorldGenModifiers,
} from '../meta/worldModifiers';
import {
  BLOCKS_PER_CHUNK,
  BLOCK_SHIFT,
  BLOCK_SIZE,
  Struct,
  StructureMap,
  type Anchor,
  type RoomKind,
} from './structure';
import {
  SANCTUM_APPROACH,
  SANCTUM_IN_H,
  SANCTUM_SHELL,
  SANCTUM_STEP,
  allSanctums,
  sanctumSpanY,
  sanctumsOverlapping,
  type Sanctum,
} from './sanctum';
import {
  REST_APPROACH,
  REST_DOOR_H,
  REST_IN_H,
  REST_PER_BAND,
  REST_SHELL,
  restNodesInBand,
  type RestNode,
} from './restNode';
import {
  allSetPieces,
  rolePoursIntoAir,
  setPieceAt,
  setPiecesOverlapping,
  type PieceKind,
  type PieceRole,
  type PlacedSetPiece,
} from './setPiece';
import {
  EdgeType,
  Kind,
  RU,
  RU_SHIFT,
  TileOrient,
  buildTile,
  chooseVariant,
  crossHY,
  crossVX,
  decodeTile,
  edgeH,
  edgePassable,
  edgeV,
  enclosureFor,
  isPathUnit,
  rowForced,
  colForced,
  spineKeepXFor,
  spineKeepYFor,
  tileSeedFor,
  makeTileCtx,
  makeTileRef,
  nodeX,
  nodeY,
  pinnedSlotKey,
  type EdgeTypeId,
  type RoomArchetype,
  type TileCtx,
  type TileRef,
} from './rooms';

export interface WorldGenConfig {
  seed: number;
  mode: 'blended' | 'layered';
  /** 0..1. The one knob. See the header. */
  cohesion: number;
  biomes: BiomeDef[];
  /** How fast the biome roster advances with depth. 0 = flat world. */
  depthBias?: number;
  /**
   * Player-chosen world modifiers (issue #7). Omitted means
   * `NEUTRAL_WORLD_MODIFIERS`, and `isNeutral` short-circuits every code path
   * below, so a run with no modifiers costs exactly what it did before.
   *
   * Every field is a *multiplier on an authored biome number*, never an
   * absolute. Biomes disagree about deposits, liquid and hazard by an order of
   * magnitude — Sunken Vault's hazard density is 0.02 against the Rotwood's
   * 0.4 — so an absolute would flatten twelve authored identities into one,
   * while a multiplier scales each biome's own character.
   */
  modifiers?: WorldGenModifiers;
  /**
   * Skip `routeCorridors` entirely. **A control, not a mode**, and the only
   * reason it is a config field rather than a local edit is that issue #57's
   * whole argument rests on it.
   *
   * Commit `b8a4199` rendered one seed and window with the router removed and
   * the world came out as large, curved, organic cave with almost no straight
   * edge in it; put the router back and the same window is a stack of
   * horizontal bars. That frame is the issue's evidence, and until this flag
   * existed it could only be reproduced by editing `generateChunk` — which
   * means it could not be *asserted*, and `tests/boundaryShape.test.ts` now
   * asserts something important about it. See the footer of that file.
   *
   * A world built this way is not connected and must never be played: the
   * corridor network is what `structure.ts` proves. Nothing outside a test may
   * set it.
   */
  noRouter?: boolean;
  /**
   * Override the mega lattice's hollow share — what fraction of
   * `megaFeatures`' lattice cells draw a *cavern* rather than a *mass*.
   * Omitted means the shipped alternation rule, which is what every played
   * world uses.
   *
   * **This is the world's volume dial, and it exists as a field because it is
   * the only one large enough to matter.** `tests/routerBudget.test.ts`
   * measures the decomposition: the base cave carve opens 9.9 % of the world,
   * the mega lattice takes that to 24.3 %, the room tiles to 26.3 %, and
   * `routeCorridors` to 58.1 %. The corridor router is **two thirds of every
   * open cell in the game**, and issue #57's whole remaining half is a proposal
   * to make it sparser. Nothing else in the generator is within a factor of
   * three of being able to pay for that — the cave field cannot, and the
   * measurement that says so is in the same file.
   *
   * Set as a **share** rather than as the lattice rule, and that costs
   * something worth naming: the default is an alternation on `(gx + 2gy) mod 3`
   * precisely so that three caverns never land in a row (see `oneMega`), and an
   * independent per-cell coin gives that up. So a fractional value here is a
   * measurement instrument, not a shipping configuration. **1 and 0 are exact**
   * — every cell hollow, or every cell a mass — and those are the two the test
   * asserts against.
   */
  megaHollow?: number;
  /**
   * Override `chooseVariant`'s archetype roll at named slots — issue #184's WFC
   * spike. Keyed by `pinnedSlotKey(ux, uy, orient)` (`rooms.ts`), valued with the
   * archetype the spike's solver collapsed that slot to. Omitted means every
   * slot rolls the hash path exactly as it always has — the same "control, not
   * a mode" shape `noRouter` and `megaHollow` already have above, for the same
   * reason: this is how `src/world/wfcSpike.ts`'s measurement harness renders a
   * real, playable-shaped world through the WFC-chosen archetypes to feed
   * `tests/worldSweep.test.ts`'s monotony metrics, without WFC ever touching a
   * world nothing built this way asked for. An entry illegal for its own slot's
   * signature is ignored by `chooseVariant` and that slot rolls the hash path,
   * so a stale or malformed override degrades to today's behaviour rather than
   * corrupting the world.
   */
  archetypeOverride?: ReadonlyMap<string, RoomArchetype>;
}

/** Canonical cohesion for each mode — callers may override. */
export const MODE_COHESION: Record<WorldGenConfig['mode'], number> = {
  blended: 0.85,
  layered: 0.2,
};

// Coarse noise lattice: sample every COARSE cells and bilinear-interpolate.
// Cave features are 40+ cells across, so 4-cell sampling is visually lossless
// and turns 16384 fbm evaluations per chunk into 1089.
const COARSE = 4;
const COARSE_SHIFT = 2;
const CG = (CHUNK_SIZE >> COARSE_SHIFT) + 1; // 33
const CG2 = CG * CG;

/** Global cave frequency. Must NOT vary per biome or caves would not cross borders. */
const CAVE_FREQ = 1 / 96;
/**
 * How much wider than tall a cave feature is — **per band**, from the band
 * biome's `terrain.verticality`. Issue #63.
 *
 * This was the module constant 2.6, and it was the single reason every band in
 * the game read as the same horizontal layering. `docs/17` §1.3 correlated every
 * authored per-biome parameter against measured outcomes over eight bands and
 * found all |r| <= 0.47; `verticality` in particular is authored across a 4x
 * range, is named for exactly this axis, and correlated **-0.20** with measured
 * horizontality, because the only thing reading it was `chooseVariant`'s
 * archetype weighting — 1/96th of a screen.
 *
 * The effect size is measured, on the carve alone, with solid fraction pinned at
 * 0.40 by bisection so it is shape and not density (`docs/17` §1.3):
 *
 *     aniso  0.6 -> H/V 0.71   1.0 -> 0.98   2.0 -> 1.79
 *            2.6 -> 2.34 (the old constant)  3.4 -> 2.98
 *
 * Monotone, and it brackets the finished world's measured 1.56-2.21. Mapping
 * `verticality`'s authored 0.20-0.80 onto 3.2-1.0 therefore buys a ~3x spread in
 * horizontality where the spread was exactly 1.0x. Cinder March (0.20) keeps its
 * flat galleries; Verdigris Spire (0.80) becomes isotropic chimneys — which is
 * also what Terraria does by default, its vertical-lock flag being opt-in per
 * call and used exactly once in the whole generator (`docs/20` §6.2).
 *
 * **Why 1.0 and not lower.** The constant was raised from 1.4 to 2.6 because an
 * isotropic carve produces blobs, and blobs gave 5-cell platforms under a
 * 10-cell-wide player (issue #1 §1). What fixed that was the ledge field, which
 * authors floors on top of the carve rather than finding them in it — so
 * isotropy is affordable now in a way it was not then. Below 1.0 it would not
 * be: a chimney field with no horizontal extent has nothing for a shelf to sit
 * across.
 */
const CAVE_ANISO_FLAT = 3.2;
const CAVE_ANISO_TALL = 1.0;
/** The authored range of `terrain.verticality` the two above are mapped onto. */
const VERTICALITY_LO = 0.2;
const VERTICALITY_HI = 0.8;

/** `terrain.verticality` -> the cave field's vertical compression. */
function caveAnisoFor(verticality: number): number {
  const t = clamp01((verticality - VERTICALITY_LO) / (VERTICALITY_HI - VERTICALITY_LO));
  return CAVE_ANISO_FLAT + (CAVE_ANISO_TALL - CAVE_ANISO_FLAT) * t;
}

/**
 * The span on which a *material* decision changes, in cells.
 *
 * Every material choice that is not carried by a noise field — which accent a
 * region's veins are made of, and whether a region is contaminated by a
 * neighbour biome — is keyed on this lattice rather than on the 8x8 palette
 * block. **Since #143 the region's *bulk rock* is keyed on it too** (see
 * `hostRockOf`), which is what makes this the span on which "what is the ground
 * made of here" has an answer at all. Both of the original two used to be per
 * block, and both were therefore drawn at one quarter of the player's width:
 *
 *   - `blkAccent` was a uniform draw from the biome's accent list every 8
 *     cells, inside an ore blob that was 18 cells across. So a single blob had
 *     its material re-rolled two or three times *inside itself* and emerged as
 *     two or three materials — confetti where a vein should be (`docs/17` §1.5
 *     cause 3, the strongest single contributor it identifies).
 *   - cross-contamination fired per 8x8 block at 0.21, which is ~425
 *     contaminated blocks on a 480x270 screen. Its own comment says "a foreign
 *     material should read as an intrusion, not as a different biome"; at
 *     one-in-five 8-cell blocks it read as neither, it read as noise.
 *
 * 96 is larger than everything the player is measured against — 9.6
 * player-widths, 4.4 player-heights, two shelf periods, one and a half
 * herringbone room units — and it puts **3** contamination events on a screen
 * instead of 425. `docs/21` §3.2 asks for material coherence over 22 cells;
 * this is the span at which "what material is this" is allowed to have a
 * different answer at all.
 *
 * **Why not 192, which is `docs/19`'s Noita-derived 130-320?** Re-measured at
 * 193 (prime, so it phase-locks with nothing) over `tests/paletteStructure`'s
 * 288 windows, and the answer is still no — **but for none of the reasons this
 * comment used to give, and the old ones are recorded here as a warning about
 * quoting a guard instead of re-running it.**
 *
 * The refusal used to be *"`exp(H)` drops to 4.80, outside `docs/21` §3.1's
 * hold at 5-7"*. **That reason is dead.** `ffcddef` re-derived the floor from
 * Healey's ceiling-read-upside-down to **2.5** (`docs/24` §3a), and at 193 the
 * roster reads 4.47 — nearly two whole materials of margin over the live floor
 * and comfortably under the 7 ceiling. Anyone re-reading this comment before
 * that re-derivation would have refused 193 for a bar that no longer exists.
 *
 * What it actually buys, and it is real: `distinct` **12.2 -> 11.1**, and
 * `collapse` falls in eleven biomes of twelve (`nanite_reef` 1.28 -> 1.29 is
 * the exception). That is `docs/24` §4's own coherence result, arriving from
 * the material-span side.
 *
 * What it actually costs, measured rather than argued — **nine assertions in
 * `tests/paletteStructure.test.ts` go red**:
 *
 *   - **`BULK_TO_ACCENT` reopens.** `hollow_choir`'s bulk-to-vein ratio falls
 *     under 3.3. That band is *closed*, and it was closed by #114's
 *     concentration pass; a wider span puts more of a screen into one accent,
 *     which is the same arithmetic §5a is built on running backwards.
 *   - **`SIMULTANEOUS` gains a biome.** `nanite_reef` goes 4.00 -> 4.1.
 *   - **`SHELL_OVER_FIVE` gains `verdigris_spire`**, and `sunken_vault` lands
 *     on `occupiedBins` **4.50**, exactly the QUILT cut, which trips the guard
 *     that exists to stop a classification being decided by a coin flip.
 *
 * So: three of `docs/24`'s bands perturbed, one of them a closed one, to buy a
 * coherence result that has no band of its own. #57's decided plan asked
 * whether that trade changes once layout varies per biome; it does not, because
 * the per-biome layout lever available (`rooms.ts` `ENCLOSURE_STRENGTH`) is
 * itself a measured null at 0.01 of separation. Re-measured, not re-applied.
 *
 * The Noita number remains a target for the *grain* — `docs/21` §3.5 says
 * exactly this — and the grain here is ORE_WAVELENGTH's job, not this
 * constant's.
 *
 * It is a pure function of world position, so chunks still agree without
 * talking and a chunk still regenerates byte-identically.
 */
export const MATERIAL_SPAN = 96;

/**
 * Wavelength of the ore field, in cells — the size of one vein.
 *
 * Was 18, against a 10x22 player: an "ore vein" was smaller than the body
 * standing in front of it, and `docs/19`'s target for the scale of an authored
 * feature is 100-300. Terraria's ore pass is the model (`docs/20` §3): 30
 * `TileRunner` calls that write **zero** `.active` bits, so a vein has no
 * silhouette of its own at all — it repaints stone in place and inherits the
 * rock's shape. Ours does the same thing, and now at the same size.
 *
 * The field's marginal distribution does not depend on its wavelength, so
 * widening it changes how big a vein is and not how much of the rock is vein.
 * That second question is `terrain.oreDensity`'s, below.
 */
const ORE_WAVELENGTH = 160;

/**
 * Strata wavelength, as a multiple of the biome's `noiseScale`.
 *
 * The third material field at a frequency below the body, and the one
 * `docs/17` §1.5 cause 3 lists fourth: the primary/secondary split was drawn at
 * `noiseScale * 0.6` vertically, which is **12-37 cells** across the roster —
 * so a wall face changed rock every 15 cells or so, under a 22-cell player, and
 * every enclosed cavity was lined in both materials whatever the shell did.
 *
 * 2.4 puts the vertical wavelength at 48-149, above the body and above the
 * shelf pitch. The 5:1 horizontal anisotropy is kept exactly as it was — strata
 * are wider than they are tall, which is the whole reason the field is
 * anisotropic — so the horizontal figure moves from 60-186 to 240-744.
 *
 * This is a *material* field. It picks which rock a solid cell is made of and
 * feeds nothing else — `hostRockOf` is its only reader — so it is not one of
 * the cave-shape constants (`CAVE_FREQ`, `CAVE_ANISO`, the shelf lattice) that
 * decide where the rock is.
 *
 * **It is no longer sampled per cell.** #143 moved the read to one sample per
 * `MATERIAL_SPAN` region — see `hostRockOf` — so these two numbers now set how
 * far apart two *regions* have to be before they may be made of different rock,
 * rather than how often a wall face is allowed to change material. The
 * wavelengths above are unchanged and still the reason the constants are what
 * they are: quantising a 48-149 cell wavelength onto a 96-cell lattice is only
 * sound because `strataScale` (1.6 at the shipped cohesion) puts the realised
 * vertical figure at 77-238 and the horizontal one at 384-1190, i.e. at or above
 * the lattice on the short axis and four to twelve regions on the long one.
 */
const STRATA_X = 12;
const STRATA_Y = 2.4;

/**
 * How far the host lattice is pushed off its own grid lines, in cells.
 *
 * A region-scale material decision read straight off `floor(x / 96)` puts every
 * contact between two rocks on a multiple of 96, and the eye finds that
 * instantly: rendered, `the_long_hour` and `coldforge` came out as rectangular
 * patchwork with axis-aligned seams. That is #57's own warning — *"the
 * herringbone layer cannot hide a seam we draw ourselves"* — arriving in the
 * material layer.
 *
 * `docs/19` §8.4 asks for exactly this and puts a number on it from Noita's
 * source: `BIOME_EDGE_NOISE_EXTENT = 42` against a 512-cell region pitch,
 * i.e. **8.2 %**. Ours is 48 against a 96-cell pitch, which is far more in
 * relative terms and about the same in absolute cells, and it is the absolute
 * figure that matters because it is compared against the size of the player and
 * against the 8-cell block the warp is evaluated per.
 *
 * The warp's own wavelength is four regions, so a contact meanders over roughly
 * a screen's width rather than wobbling per block — a jitter at the lattice's
 * own frequency would replace a visible grid with visible noise.
 *
 * `WARP_AMP` (90) does the same job for band borders on a much larger pitch and
 * is the precedent for the technique here.
 */
const HOST_WARP = MATERIAL_SPAN * 2;

/** Cells per room-level palette interleave in Blended mode. */
const BLEND_CELL = 48;
/** How far band borders are pushed off the ruler line, in cells. */
const WARP_AMP = 90;

/**
 * A coordinate far outside any world the player can reach, but small enough
 * that the noise field still evaluates on it. Stands in for an infinity — see
 * `finiteCoord`.
 */
const COORD_LIMIT = 1 << 24;

/**
 * Map a `number` onto the subset of it the world field is defined over.
 *
 * `NaN` becomes the origin because it carries no position to preserve; an
 * infinity keeps its sign and becomes merely enormous, so every clamp
 * downstream still resolves it to the end of the world it points at. See
 * `biomeIndexAt`, issue #71.
 */
function finiteCoord(v: number): number {
  if (Number.isFinite(v)) return v;
  return v > 0 ? COORD_LIMIT : v < 0 ? -COORD_LIMIT : 0;
}

// ------------------------------------------------------------------ the shape
//
// Issue #1 §4.1. The world has an authored macro shape: an outdoor surface you
// spawn on, one visible way down, and below y = 0 a vertical stack of bands.
// Everything in this block is a pure function of world position and seed, which
// is what lets a chunk generate its slice of it without ever asking a
// neighbour.

/**
 * Cells per vertical band. 8 bands at 900 is a 20-30 minute run — long enough
 * for a build to develop, short enough to retry freely (issue #1 decisions).
 */
export const BAND_HEIGHT = 900;

/** Nominal ground line of the authored surface. Band 0 begins at y = 0. */
export const SURFACE_Y = -40;
/** The run starts here. Fixed, because the surface around it is authored. */
export const SPAWN_X = 0;

/** The descent mouth: a lined shaft east of spawn, `X0..X1` inclusive. */
export const DESCENT_X0 = 58;
export const DESCENT_X1 = 94;
/** Where the authored shaft hands over to natural caves. */
export const DESCENT_BOTTOM = 300;
/** Vertical pitch of the shaft's landings. Well under `jumpHeight` (54). */
const DESCENT_STEP = 22;

/**
 * The pool, and where the west of the surface stops — issue #140.
 *
 * Both used to be numbers this file owned and nothing tested: the pond sat at
 * x = -260 in the middle of the grove and the cliff climbed from x = -640,
 * which was the west refusal by default rather than by design. `docs/27` §2
 * makes the tree the refusal and the pool the reward, so the pool moved under
 * the tree (`greatTree.ts` `TREE_POOL`) and the cliff moved west of it. There
 * is exactly one body of water on the surface and it is inside the landmark.
 */
const POND_CX = TREE_POOL.cx;
const POND_RX = TREE_POOL.rx;
const POND_DEPTH = TREE_POOL.depth;

/**
 * Where the ground starts climbing into the west cliff.
 *
 * West of the Great Tree, so the tree stands clear of it and the two refusals
 * stack the way `docs/27` §2 wants them to: a landmark you cannot climb past,
 * with a wall behind it you cannot walk past.
 */
const CLIFF_X = -1200;

/**
 * How much surface relief a column is allowed, 0 across the Great Tree's
 * terrace and 1 outside it, eased over `TERRACE.ease`.
 *
 * A free function rather than a method because it reads no seed and no field:
 * that is the whole claim `docs/27` §3 makes about the authored frame, and a
 * method would let a future edit quietly make it seed-dependent.
 */
function terraceDamp(worldX: number): number {
  const d = worldX < TERRACE.x0 ? TERRACE.x0 - worldX
    : worldX > TERRACE.x1 ? worldX - TERRACE.x1
    : 0;
  if (d <= 0) return 0;
  if (d >= TERRACE.ease) return 1;
  return smoothstep(d / TERRACE.ease);
}

/**
 * Ledge field — issue #1 phase 1D, "the single highest-impact change".
 *
 * Median walkable run was 5-7 cells against a 10-cell-wide player, because the
 * carve is a noise threshold and a noise threshold has no notion of "floor".
 * So floors are authored on top of it as a periodic field rather than found by
 * scanning: `phase = (y + offset(x,y)) mod SHELF_PITCH < SHELF_T` makes a slab.
 *
 * A *field* and not a neighbourhood scan for one reason: a scan would have to
 * see past the chunk edge to decide the cells near it, and either it lies at
 * the border (visible seams, runs capped at the chunk) or it reads a neighbour
 * and chunks stop being independent.
 *
 * PITCH - T = 25 is the headroom, and it is chosen against the player: the body
 * is 22 cells tall, plus the three cells of deposit drift that settle on the
 * floor. Anything under that is a crawlspace, which reads as solid rock to the
 * walkability metric and plays like one too. The slab is then as thick as the
 * headroom allows, because slab / (slab + headroom) *is* the world's solid
 * ratio wherever the gallery stack is what the player is standing in.
 *
 * SHELF_STEP is the reason the field is *terraced* rather than smoothly wavy.
 * A shelf that drifts down by one cell every few columns is a ramp, and a ramp
 * has no run of level ground on it at all — the offset is quantised so the
 * shelves are treads and risers, and a tread is what you can stand on.
 */
/**
 * Deck pitch, **per band**, from the same `terrain.verticality` the cave
 * anisotropy comes from. Issue #63, and `docs/17` §3.2 item 2's second half.
 *
 * The pitch used to be the module constant 48, which is why `docs/17` §1.3 could
 * measure **6 of the 48 `y mod 48` residues holding 60.3 % of every floor
 * surface in the game** — 77.4 % in band 1 — and why the anisotropy lever alone
 * would not have been enough: the shelf lattice and the corridor router run
 * *after* the carve, they are both horizontal, and at one global pitch they damp
 * whatever spread the carve buys. Deck spacing of 52 in one band and 84 in
 * another are different places to fight.
 *
 * Two constraints on the numbers, and they are the reason this is a ladder
 * rather than an arbitrary lerp:
 *
 *  - **`GALLERY_H = pitch - slab` must clear the body**, 22 cells plus the three
 *    of deposit drift that settle on a floor. That sets the floor of the range,
 *    not taste.
 *  - **`RU mod pitch != 0`.** `RU` is 64 today and `docs/19` §8.1 proposes 192;
 *    48 divides 192 exactly four times, so the deck lattice and the room lattice
 *    would land in phase, which is worse than either alone. The ladder is
 *    quantised to 4 — the common divisor `docs/17` §3.2 asks for, so adjacent
 *    bands' lattices still meet — and skips any pitch that divides either.
 */
const SHELF_PITCH_FLAT = 42;
const SHELF_PITCH_TALL = 54;
/** Headroom a gallery must leave: the 22-cell body plus 3 cells of drift. */
const SHELF_MIN_HEADROOM = 25;
/**
 * The ceiling on the ladder, and it is a movement constraint rather than a
 * taste one: **the pitch is the height of the step from one deck to the next**,
 * and `docs/02` §3.2's jump clears 54 cells. A pitch above that is a stack the
 * player cannot climb without an ability, and it was measured: at a pitch of 76
 * in band 0 the authored descent handed over to a 57-cell final drop and
 * `tests/worldshape.test.ts` caught it. Anything wider has to buy its
 * architecture somewhere other than here.
 */
const SHELF_PITCH_CEILING = 54;
/** Room-unit lattices a deck pitch may not divide, so the two never phase-lock. */
const LATTICE_PERIODS = [64, 192];
/** The widest pitch the ladder can produce — buffer sizes and range checks. */
const SHELF_PITCH_MAX = SHELF_PITCH_TALL;

/**
 * `terrain.verticality` -> deck pitch, in cells, off both room lattices.
 *
 * Not quantised to a common divisor, which `docs/17` §3.2 asks for so that
 * "adjacent bands' lattices still meet": `deriveArchitecture`'s anchor buys
 * that outright and exactly, by starting each band's lattice where the band
 * above's next slab would have fallen. A quantum would only cost resolution —
 * at a step of 4 over a 16-cell range there are five distinct pitches for
 * twelve authored biomes, and two biomes a designer wrote 0.62 and 0.68 for
 * would come out identical.
 */
export function shelfPitchFor(verticality: number): number {
  const t = clamp01((verticality - VERTICALITY_LO) / (VERTICALITY_HI - VERTICALITY_LO));
  let p = Math.round(SHELF_PITCH_FLAT + (SHELF_PITCH_TALL - SHELF_PITCH_FLAT) * t);
  while (LATTICE_PERIODS.some((n) => n % p === 0)) p += 1;
  return Math.min(p, SHELF_PITCH_CEILING);
}

/**
 * Slab thickness for a pitch.
 *
 * The **headroom** is what is held constant, not the slab share: a gallery is
 * the volume a 22-cell body moves through and 25 is what that costs, in every
 * band. So the whole of the pitch difference lands on the rock between two
 * decks, which is the quantity `grainY` measures — 19 cells in the Cinder March
 * against 29 in the Verdigris Spire, bracketing the 23 that shipped.
 */
function shelfSlabFor(pitch: number): number {
  return pitch - SHELF_MIN_HEADROOM;
}

const SHELF_STEP = 3;
/**
 * The terrace field is sampled on a 32x64-cell world grid rather than per cell.
 * That is not only cheaper (8 taps a chunk instead of 1089): it is what lets
 * the corridor router ask for the same answer the ledge pass will give, at any
 * world position, without either of them owning a buffer the other reads.
 */
const SHELF_QX = 32;
const SHELF_QY = 64;
const SHELF_COLS = CHUNK_SIZE / SHELF_QX;
/**
 * Above this the ledge field yields and leaves the cave open, which is what
 * makes the stack descendable without a single ability. The hole field's
 * vertical frequency is deliberately ~1/3 of its horizontal one, so gaps line
 * up across two or three levels into chimneys rather than scattering.
 */
const SHELF_HOLE_T = 0.63;

/**
 * Vertical shaft half-widths, and the reason horizontal runs no longer have a
 * radius at all.
 *
 * There was one radius for both axes, 6 for an OPEN edge and 4 for a DOOR. So
 * the router's corridors were 13 and 9 cells tall — against a body 22 cells
 * tall. **No corridor in the game could be walked through.** The spine the
 * redundancy verifier proves is connected was a crawlspace the player does not
 * fit in, and that is invisible to a block-granularity connectivity check
 * because the blocks really are open.
 *
 * Simply making them taller was not enough either: a constant-y tube carved
 * through noise has no floor of its own, and widening it to fit the player made
 * the router carve away most of the ledge field on its way past. So a
 * horizontal run is now a *gallery* — it is carved against the same terrace
 * lattice the ledges use, one period tall with the period's slab as its floor.
 * Corridors and shelves became the same structure, and neither can erase the
 * other. A shaft still needs a width, and 21 is set by its landings rather than
 * by the body: a 10-cell player falls down 15 happily, but a landing is a floor,
 * and a floor shorter than about 20 cells is not one you can fight on.
 */
const CORR_V_OPEN = 11;
const CORR_V_DOOR = 11;
/** Half-width of the junction chamber at each routing node. */
const JUNCTION_R = 13;
/**
 * Landing pitch in vertical corridors, and a **per-shaft phase** on it.
 * Issue #63, and the constant the measurement found on its own.
 *
 * It was 26, and 26 is what a Fourier scan of the floor histogram reports as
 * the world's *strongest* vertical periodicity — ahead of the deck lattice, in
 * every band, because a landing comb carries no terrace offset at all and its
 * teeth are therefore phase-locked across the entire world. Every shaft in the
 * game put its rungs on the same rows.
 *
 * The phase is a hash of the shaft's own world x, so two shafts no longer
 * agree and the comb stops being global — while both chunks a shaft passes
 * through still place identical landings, because neither the step nor the
 * phase knows anything about the segment it was asked for.
 *
 * **Not per band, and that is measured rather than an omission.** Driving it
 * from `verticality` was tried both ways round. Sparse landings in the tall
 * bands cost `tests/worldshape.test.ts` its 0.38 solid floor and its 60-cell
 * fighting run in band 5; dense landings there cost the vertical grain the
 * deck pitch had just bought, because a rung is a horizontal floor. It fights
 * the deck pitch for the same axis, and the deck pitch is the better lever.
 * 24 rather than 26 pays back the standable floor the wider pitches cost, and
 * stays well under the 54-cell jump height in both directions.
 */
const SHAFT_STEP = 24;

// Hoisted out of the `Kind` namespace object: these are compared 16384 times
// per chunk and a property load per comparison is measurable at that count.
/**
 * Span keyed for one glow-vein decision, in cells — issue #31.
 *
 * 32 rather than the deposits pass's 64: 32 is exactly four light tiles
 * (`LIGHT_TILE` is 8), and with the field's 0.72 per-tile falloff a cluster has
 * decayed to about a quarter of its value by the time the next span's cluster
 * picks up. Wider and the room is a series of dark stretches between lamps;
 * narrower and it is a flat wash with no reason to walk toward anything.
 */
const GLOW_SHIFT = 5;
const GLOW_SPAN = 1 << GLOW_SHIFT;

/**
 * How far a set piece's through-corridor reaches past the stamp on each side.
 *
 * Long enough to be sure of meeting carved volume rather than ending inside the
 * slab the noise put next to the piece — 64 is half a chunk, and the gallery
 * pitch the router works on is 48.
 */
const SET_PIECE_APPROACH = 64;

/**
 * The mega-feature lattice — one large object per 184 x 184 cells.
 *
 * 184 x 184 is 33 856 cells against a screen's 129 600, so the lattice runs at
 * **3.8 features per screen**, of which one in three is a cavern: **1.28 caverns
 * per screen**, against Terraria's measured 1.046 runners per screen (`docs/20`
 * §2.2). One in three rather than one in two because the cavern is the
 * expensive family — it is protected from every later pass and therefore
 * permanent, while a mass is rock the corridor router is still allowed through.
 *
 * The pitch is deliberately not a multiple of `RU` (64), of any deck pitch
 * (42-54), or of `MATERIAL_SPAN` (96): 184 = 2^3 x 23, so it shares only 8 with
 * the room lattice and phases against everything else. A mega feature landing
 * on the room lattice every time would reintroduce at 184 cells exactly the
 * periodicity `docs/17` §1.3 measured at 48.
 *
 * **The value is the top of a measured range, not a preference.** Denser was
 * tried at 168, 152 and 136 and each is better on every statistic this pass
 * exists for — at 136, `C(128)` reaches 0.072-0.093 against 0.025-0.033 here.
 * Each also breaks something else that is separately guarded: 168 costs
 * `tests/worldshape.test.ts` its 20-cell median walkable run and
 * `tests/materialGrain.test.ts` its 450-unit screen, 152 costs the 0.38 solid
 * floor as well, and 136 leaves one probe at 38.1 % against that floor. The
 * ceiling on this constant is the point where four other contracts start
 * failing, and `tests/worldVoids.test.ts` records what that costs.
 */
const MEGA_PITCH = 184;
/**
 * Spine half-length and peak radius.
 *
 * Terraria's cavern-layer `TileRunner` runs 50-299 tiles at O6-19 (`docs/20`
 * §3.1, §3.3). At the x6 Starbound scale that is 300-1800 long by 36-114 wide;
 * we take the short end of the length — 180-340 cells, i.e. 0.4-0.7 screens —
 * because our whole run is 6.6 screens wide and a 1800-cell object would be a
 * quarter of it.
 *
 * **The radius floor of 24 is a body measurement, not a taste one.** A circle
 * of radius r admits a 35-cell-tall box `2 * sqrt(r^2 - 17.5^2)` wide, so r = 22
 * is the smallest circle a 25x35 `foreman_of_the_ninth_flue` fits inside at all
 * and r = 24 is the smallest that admits the 25x41 `the_hungry_wall`. Below 24
 * the pass would draw caverns that still cannot hold the bodies it exists to
 * make room for (issue #68).
 */
const MEGA_HALF_MIN = 90;
const MEGA_HALF_MAX = 170;
const MEGA_R_MIN = 24;
const MEGA_R_MAX = 32;
/**
 * The rind: the cavern is drawn inside a mass on the same spine, this much
 * fatter. **The most important number in this block**, and the reason it is a
 * rind rather than a second family of features on the same lattice.
 *
 * The first two versions drew voids and masses as *separate* lattice cells —
 * first by an independent coin, then alternating. Both are balanced on average
 * and neither is balanced locally, and local is what is measured:
 * `tests/worldshape.test.ts` probes a **360 x 260** window, which is smaller
 * than one of these features, sixty-four times, and requires every probe to sit
 * inside 0.38-0.70 solid. A probe that lands inside a cavern sees a cavern and
 * nothing else. Alternation with a 12 % swap rate still left one probe at
 * **35.7 %** and another at **69.9 %**, against a baseline range of 40-66.
 *
 * Pairing them on one spine fixes it by construction: **any window that
 * contains a cavern contains that cavern's own rock.** It is also what a cave
 * looks like. `docs/21` §3.3(b) asks for exactly this and calls it the cheaper
 * half of cohesion — *"the region has a continuous edge that differs from what
 * is on either side of it"* — and `docs/20` §5.3 lists a block-and-wall pair as
 * one of the five things that make a mini-biome read as a place rather than as
 * a recolour.
 */
const MEGA_RIND_T = 30;
/**
 * How often the lattice's cavern/mass rotation is swapped, so the mix is
 * locally balanced without reading as a repeating pattern. The rotation itself
 * is `(gx + 2gy) mod 3`, which puts a mass on both lattice neighbours of every
 * cavern before the swap disorders it.
 */
const MEGA_SWAP = 0.12;
/**
 * How much fatter a *standalone* mass is drawn than a cavern's bore.
 *
 * Zero, and the zero is measured. A mass is uniform bulk rock with no rooms, no
 * decks and no shell in it, so it concentrates the material distribution: at 20
 * cells of growth `tests/materialGrain.test.ts` reported `exp(H)` at 4.87 and at
 * 6 it reported 4.93, both against a floor of 5. The masses are what the grain
 * result is made of and the pass cannot do without them, so what gives is their
 * *size* rather than their number.
 */
const MEGA_MASS_T = 0;
/**
 * **How big one feature is, per biome, from `terrain.roomSizeBias`.** Issue #57.
 *
 * #57's diagnosis is one sentence — *"`CAVE_FREQ`, `CAVE_ANISO`, `SHELF_PITCH`
 * and every corridor constant are module-level, so twelve biomes differ in
 * colour and in nothing else"* — and its stated fix is to make the shape
 * constants per-biome data. This is that fix applied to the **one layer that
 * has room to take it**, and which layer that is was decided by measurement
 * rather than by preference:
 *
 *  - The **corridor router** is the dominant shape-maker (the note at the foot
 *    of `tests/worldVoids.test.ts` measures it), but it is also what
 *    `structure.ts` proves connected. Varying it per biome varies connectivity
 *    per biome, and `tests/setpiece.test.ts` lost two anchor pairs' redundancy
 *    the last time that was tried.
 *  - The **deck pitch** is already per-biome (`shelfPitchFor`) and is capped at
 *    54 by `docs/02` §3.2's jump height, so it has 12 cells of range left.
 *  - The **mega features** are protected from `ledges`, the room tiler and most
 *    of the router by `P_MEGA`/`P_MASS` already. Nothing downstream depends on
 *    their size, which is exactly why their size can be authored.
 *
 * `roomSizeBias` is 0.50-0.90 over the roster and was being read only by the
 * room interiors, so this is a second reader for an authored field rather than
 * a thirteenth knob. `rotwood_reach` authors the minimum and is therefore
 * generated exactly as it shipped — the control, and the reason
 * `tests/worldSeparation.test.ts` can show band 0 unmoved beside seven bands
 * that moved.
 *
 * **2.1 is the top of a measured range, not a preference.** At 2.4 the same
 * pass reports `grainY` separation 1.20 against 1.08 here, and costs
 * `tests/worldshape.test.ts` its flammable-deposit bias ratio (1.0133 against a
 * 1.015 floor); at 2.6 it costs the 0.38 solid floor outright, with a 360x260
 * probe reading 0.349. The ceiling on this constant is where another contract
 * starts failing, which is the same rule `MEGA_PITCH` above is set by.
 */
const MEGA_SCALE_LO = 1.0;
const MEGA_SCALE_HI = 2.1;
/**
 * How much of a feature's growth its **cavern bore** takes, against its rock.
 *
 * The whole of it, and the pass is unshippable. `tests/worldshape.test.ts`
 * probes a 360x260 window 64 times and requires every probe inside 0.38-0.70
 * solid; a cavern whose bore scales with the feature grows its open area as the
 * square of the scale, and at scale 1.7 with the bore taking all of it a single
 * probe read **0.3756**. That is objection 2 in `tests/worldVoids.test.ts`'s
 * falsification note, reproduced exactly.
 *
 * At 0.35 the outer extent still takes the full scale — so the *feature* is as
 * large as the separation statistic needs, and `grainY` is measured on the
 * solid mask, which does not care whether a large object is rock or air — while
 * the bore grows by only a third of it. The worst probe over 48 windows moves
 * from 0.3756 to **0.409**, which is better than the 0.388 the pass had before
 * any of this. A bigger feature in this world is mostly bigger in its rock.
 */
const MEGA_BORE_SHARE = 0.35;
/** The authored range of `terrain.roomSizeBias` the scale is mapped onto. */
const ROOMSIZE_LO = 0.5;
const ROOMSIZE_HI = 0.9;

/** `terrain.roomSizeBias` -> the mega feature's linear scale. */
function megaScaleFor(roomSizeBias: number): number {
  const t = clamp01((roomSizeBias - ROOMSIZE_LO) / (ROOMSIZE_HI - ROOMSIZE_LO));
  return MEGA_SCALE_LO + (MEGA_SCALE_HI - MEGA_SCALE_LO) * t;
}

/**
 * Declared bbox of a feature about its centre. Nothing may exceed it.
 *
 * At `MEGA_SCALE_HI`, not at the scale of the biome being asked about: a chunk
 * rejects a lattice cell on this box *before* it knows which biome the feature's
 * centre is in, and a box that shrank per biome would clip the features of a
 * large-featured band whenever a small-featured band's chunk was the one asking.
 */
const MEGA_REACH =
  MEGA_HALF_MAX
  + Math.ceil((MEGA_R_MAX + Math.max(MEGA_RIND_T, MEGA_MASS_T)) * MEGA_SCALE_HI);
/** Radians of jitter on the per-run direction, either side of the axis. */
const MEGA_ANGLE_JITTER = 1.1;
/** Sideways wander of the spine, as a fraction of the peak radius. */
const MEGA_WOBBLE = 0.85;
/**
 * Exponent of the radius taper `r(t) = rMax * (1 - t^2)^k`.
 *
 * `docs/20` §9.1 asks for "a radius profile tapering to 0", and the exponent is
 * what decides whether that is a lens or a tube with rounded ends. At 0.45 the
 * radius is still 48 % of peak at 90 % of the way out, so the run reads as a
 * passage of roughly constant bore that closes at the tips — which is what
 * `TileRunner` produces, since its radius is re-drawn per step around a mean
 * rather than swept.
 */
const MEGA_TAPER = 0.45;
/**
 * How much shorter an *upright* run is drawn than a level one.
 *
 * Two reasons, and the first is the movement one. A 360-cell vertical shaft is
 * a fall nothing survives and a climb nothing makes: `docs/02` §3.2's jump
 * clears 54 cells, so a chimney taller than a few landings apart is a one-way
 * trip whatever is put in it. The second is measured: band 5 is the Verdigris
 * Spire at `verticality` 0.80, so four runs in five there are upright, and at
 * full length they were taller than `tests/worldshape.test.ts`'s 260-cell probe
 * window — one probe read 37 % solid because the window was *inside* one.
 */
const MEGA_UPRIGHT = 0.8;
/**
 * Thickness of the cavern's own wall, in cells: the innermost `MEGA_SKIN` of the
 * rind is the biome's `palette.shell` rather than its bulk rock.
 *
 * `docs/21` §3.3 makes this half of its testable definition of cohesion —
 * *"the region has a continuous edge that differs from what is on either side of
 * it"* — and §2.3 argues it is the cheaper half, because Alucard proves a thing
 * can read as one object with a completely incoherent interior as long as it has
 * an outline. `docs/20` §5.3 says the same from the other direction: a block/wall
 * pair is one of the five things that make a marble cave a place rather than a
 * recolour, and it is the one we could not express at all.
 *
 * It also pays for itself on a measure that had nothing to do with the intent.
 * `tests/materialGrain.test.ts` holds `exp(H)` over material area shares in
 * Healey's 5-7 band; the mega masses are uniform bulk, which concentrates the
 * distribution, and the pass without a skin measured **4.87** against a floor of
 * 5. A shell lining is a *minority* material added in proportion to cavern
 * perimeter, which is exactly what an evenness measure rewards.
 */
const MEGA_SKIN = 6;
/**
 * How much the glow rate rises to pay for the volume the mega features add.
 *
 * The rule is already in `blockPalettes` and this is the second application of
 * it: **`terrain.glowDensity` is a rate per *area* and light is a property of
 * the *volume* a player stands in**, so a world with more open space behind the
 * same wall area is lit less by the same number. Issue #31 tuned the rates
 * against a world whose only open volume was 25-cell galleries; the mega pass
 * adds caverns two to three times that height, and `tests/lighting.test.ts`
 * measured the consequence exactly — band 7's median playable luminance at
 * 0.577 against a 0.58 floor, with the other seven bands clear and the same
 * test passing with the pass disabled.
 *
 * Global rather than local, and that is measured rather than lazy. A local
 * multiplier on faces bordering a cavern was tried first, at 1.9 and then at
 * 4.0, and moved the reported median by **nothing at all to three decimals** —
 * because "49 % legible" means the median sits exactly at the crossover, so
 * lighting a few more walls moves the tail and not the middle. The quantity
 * that changed is the world's open volume, and it changed everywhere.
 */
const MEGA_GLOW = 1.2;
/** Half-length of a glow seam along a face, and along a mega cavern's wall. */
const GLOW_REACH = 8;
const GLOW_REACH_MEGA = 15;
/** Thickness of a one-way landing inside a mega void, in cells. */
const MEGA_LANDING_T = 3;

/** Protection levels on the per-cell `protect` buffer. */
const P_SHAFT = 2;
/**
 * A mega void. Read by `ledges`, `carveGallery` and the room tiler, all of which
 * must leave it alone — `docs/20` §9.2's masking requirement, and the reason a
 * 400-cell cavern does not acquire eight shelf decks.
 */
export const P_MEGA = 3;
/**
 * A mega mass. Also off limits to the room tiler, and for a reason the first
 * measurement forced: with the mass left unprotected the pair was **not**
 * solid-neutral. A void is protected and therefore permanent, while a mass was
 * being carved straight back out by `placeTiles` and the router, so the two
 * families at equal frequency still cost eight points of solid fraction and
 * band 1 landed on `tests/worldshape.test.ts`'s 0.38 floor. Protecting both
 * makes the trade symmetric, which is the only version of it that can be run at
 * the coverage `grainY` needs.
 *
 * It is a *different* level from `P_MEGA` because the corridor router must treat
 * the two oppositely: a gallery crossing a void leaves a one-way ledge, and a
 * gallery crossing a mass lays a real floor — a tunnel through rock is exactly
 * what a mass is for.
 */
export const P_MASS = 4;

// ---------------------------------------------------------------- roughness
//
// **Boundary roughness — issue #57's second half.**
//
// Every pass above decides *where* the rock is. None of them decides *how the
// edge is drawn*, and until this existed the answer was "with a rasteriser", so
// a deck underside was flat for as far as the terrace bucket ran and a shaft
// wall was a straight line for hundreds of cells. `tests/boundaryShape.test.ts`
// measures it: **29.1 % of the boundary lay in perfectly straight runs of 24
// cells or more, and 137 hard 90-degree corners landed on every screen**,
// against 0.4 % and 0.9 for `docs/20` §3.1's `TileRunner` and 0 for a field of
// plain discs. The world was 45 % of the way from the eroded reference to a
// control made of literal rectangles.
//
// **Read the foot of `tests/boundaryShape.test.ts` before treating this as the
// answer to #57.** It is not. It is the part of the answer that fits inside the
// budget three other guarded properties leave, and the frames say the budget is
// too small to move a human's impression — which the same file identifies, with
// frames, as `routeCorridors`' business rather than this one's.
//
// `docs/20` states both the diagnosis and the remedy in one sentence (§3.1
// point 3): *"the radius is re-jittered +/-15 % for every single tile test…
// **Terraria jitters the shape per cell and holds the material constant per
// feature; we do the opposite**"*, and §6.5 records that Terraria spends a whole
// late pass, `Smooth World`, on nothing but making the silhouette
// non-axis-aligned.
//
// **Why this is a jitter at each rasteriser's edge test and not a post-pass.**
// The natural implementation is morphological — displace the finished mask along
// its normal — and that is what `tests/boundaryMetrics.ts` `erodeMask` does,
// because a test can see the whole window. A chunk cannot. A morphological pass
// reads three cells past the chunk border, and every pass in this file writes
// only inside its own chunk, so the choice would be an apron (touching the
// clipping in eleven rasterisers) or a 128-cell seam where the displacement
// stops — which is a *new* lattice artefact, at exactly the scale of the one
// being removed. Sampling a positional field at the edge test costs nothing, is
// chunk-pure by construction, and is what Terraria does anyway.

/**
 * Wavelength of the roughness field, in cells. **Thirteen, and it is a budget
 * rather than a taste.**
 *
 * Prime, so it is coprime with every lattice in the world — the deck pitches
 * 42-54, `RU` 64, `MEGA_PITCH` 184, `MATERIAL_SPAN` 96, `SHELF_QX` 32,
 * `SHAFT_STEP` 24 — and the roughness cannot acquire a period of its own and
 * reintroduce at its own scale the thing it was added to remove. Bilinear with a
 * smoothstep, so the field's iso-lines are curved rather than being a grid.
 *
 * It started at 5, which roughens harder, and 5 does not fit. **Boundary
 * roughness trades directly against material coherence**, and the exchange rate
 * is steep: at a 5-cell wavelength an amplitude of *two* already takes
 * `tests/materialGrain.test.ts`'s `unitCount` past its bar in `meatworks`, and
 * at 21 the same file's rock-only `facePurity` goes instead. Every wiggle is a
 * protrusion, every protrusion can resolve to its own material, and that file's
 * ceiling is 480 units a screen against a measured 460 — less than half a
 * standard deviation of slack, before this change existed. Longer wavelength at
 * the same amplitude buys the same straight-run break for fewer new units, and
 * 13 is the only value in {5, 9, 13, 21} at which the whole of `materialGrain`
 * still passes.
 */
const ROUGH_G = 13;
/**
 * How far a surface may be displaced, in cells. **Three, and neither the size
 * nor the direction is free.**
 *
 * *Direction.* A gallery's headroom is `SHELF_PITCH - SHELF_SLAB`, which
 * `SHELF_MIN_HEADROOM` pins at exactly 25 in every band: the 22-cell body plus
 * the three cells of deposit drift that settle on a floor. There is no slack in
 * it at all, so a ceiling that dips even one cell eats the movement contract
 * `tests/worldshape.test.ts` guards — its median walkable run of 20 and its
 * longest of 60. **Ceilings therefore only ever lift, and treads are never
 * touched at all.**
 *
 * *Size.* Five fails `materialGrain`'s rock-only `facePurity`; the rest of the
 * lifted volume is what `tests/worldshape.test.ts`'s 0.38 solid floor pays for,
 * and at 5 with walls moving too, band 5 seed 42 reads 38 % exactly.
 */
const ROUGH_A = 3;

/**
 * The displacement field, in [-1, 1]. Pure in `(x, y, seed)`, which is what
 * makes every caller below chunk-independent.
 *
 * Exported because `tests/boundaryMetrics.ts` roughens its control against it.
 * A transcribed copy there would be a second definition of the shape the metric
 * was validated on, and the validation would stop being about this pass.
 */
export function roughAt(x: number, y: number, seed: number): number {
  const gx = Math.floor(x / ROUGH_G);
  const gy = Math.floor(y / ROUGH_G);
  const fx = x / ROUGH_G - gx;
  const fy = y / ROUGH_G - gy;
  const sx = fx * fx * (3 - 2 * fx);
  const sy = fy * fy * (3 - 2 * fy);
  const c00 = hash2f(gx, gy, seed ^ 0x0f0ba7) * 2 - 1;
  const c10 = hash2f(gx + 1, gy, seed ^ 0x0f0ba7) * 2 - 1;
  const c01 = hash2f(gx, gy + 1, seed ^ 0x0f0ba7) * 2 - 1;
  const c11 = hash2f(gx + 1, gy + 1, seed ^ 0x0f0ba7) * 2 - 1;
  const a = c00 + (c10 - c00) * sx;
  const b = c01 + (c11 - c01) * sx;
  return a + (b - a) * sy;
}

/**
 * The field as a whole number of cells, 0..`amp`, roughly uniform over it.
 *
 * Always non-negative: every caller displaces in one direction only, and which
 * direction is the caller's business. A signed jitter would be the natural
 * thing and it is not available — see `ROUGH_A`.
 */
function roughStep(x: number, y: number, seed: number, amp: number): number {
  const v = Math.floor((roughAt(x, y, seed) + 1) * 0.5 * (amp + 1));
  return v < 0 ? 0 : v > amp ? amp : v;
}

/** Map a set piece's declared cell class onto the generator's kind grid. */
function kindOfPiece(k: PieceKind): number {
  return k === 'open' ? K_OPEN : k === 'shell' ? K_SHELL : k === 'platform' ? K_PLATFORM : K_SOLID;
}

const K_SOLID = Kind.SOLID;
const K_OPEN = Kind.OPEN;
const K_SHELL = Kind.SHELL;
const K_PLATFORM = Kind.PLATFORM;
const K_GATE = Kind.GATE;
const K_VEIN = Kind.VEIN;
const K_GLOW = Kind.GLOW;

export class WorldGen {
  seed: number;
  readonly mode: WorldGenConfig['mode'];
  /** See `WorldGenConfig.noRouter`. A control for issue #57, never a mode. */
  readonly noRouter: boolean;
  /** See `WorldGenConfig.megaHollow`. Negative means "use the lattice rule". */
  readonly megaHollow: number;
  /** See `WorldGenConfig.archetypeOverride`. `undefined` means the hash path everywhere. */
  readonly archetypeOverride: ReadonlyMap<string, RoomArchetype> | undefined;
  readonly cohesion: number;
  readonly depthBias: number;
  readonly biomes: BiomeDef[];
  readonly modifiers: WorldGenModifiers;

  readonly structure = new StructureMap();
  readonly anchors: Anchor[] = [];

  /** The ChunkGenerator handed to CellWorld. */
  readonly generator: ChunkGenerator;

  /** Diagnostics — how many times the connectivity repair pass had to act. */
  repairs = 0;

  // ---- derived cohesion parameters
  /** Indices into `biomes`, the main path top to bottom. One entry per band. */
  private bandOrder: number[] = [];

  // ---- per-band architecture (issue #63). One entry per band, derived from
  // the band biome's `terrain.verticality`. See `deriveArchitecture`.
  private bandAniso = new Float64Array(1);
  /** Cumulative y-warp at each band's top, so the cave field has no seam. */
  private bandWarpBase = new Float64Array(1);
  private bandPitch = new Int32Array(1);
  private bandSlab = new Int32Array(1);
  /** World y each band's deck lattice starts a period at — see `deriveArchitecture`. */
  private bandAnchor = new Int32Array(1);
  /** Indices into `biomes`, the lateral wings. May be empty. */
  private sideOrder: number[] = [];
  /** |x| beyond which a band hands over to its side biome. */
  private sideX = 0;
  /** Cells either side of a band boundary where palettes interleave. */
  private bandBlend = 0;
  private contamination = 0;
  /** Multiplier on the strata wavelength, from `cohesion`. See `deriveCohesion`. */
  private strataScale = 1;
  /** True when the modifier struct would change nothing. Skips all of it. */
  private plainWorld = true;
  /** `modifiers.voidMaterials` resolved to ids once, not per cell. */
  private voidMats = new Int32Array(0);
  /** False for flat test worlds (`depthBias: 0`) — the whole world is band 0. */
  private banded = true;

  // ---- id bookkeeping
  private idToIndex = new Map<string, number>();
  private neighbourIdx: number[][] = [];

  // ---- per-chunk scratch. Allocated once; the generator never allocates.
  private kind = new Uint8Array(CELLS_PER_CHUNK);
  private protect = new Uint8Array(CELLS_PER_CHUNK);
  private nCave = new Float32Array(CG2);
  private nOre = new Float32Array(CG2);
  private nThresh = new Float32Array(CG2);
  private nHole = new Float32Array(CG2);
  private shelfOff = new Int32Array(SHELF_COLS * (CHUNK_SIZE / SHELF_QY));

  private blkBiome = new Int32Array(BLOCKS_PER_CHUNK * BLOCKS_PER_CHUNK);
  private blkPrimary = new Int32Array(256);
  private blkSecondary = new Int32Array(256);
  private blkFill = new Int32Array(256);
  private blkLiquid = new Int32Array(256);
  private blkLiquid2 = new Int32Array(256);
  private blkHazard = new Int32Array(256);
  private blkPlatform = new Int32Array(256);
  private blkAccent = new Int32Array(256);
  private blkGlow = new Int32Array(256);
  private blkGlowDensity = new Float32Array(256);
  private blkShell = new Int32Array(256);
  /**
   * **The host rock: the one material this block's region's bulk is made of.**
   *
   * See `hostRockOf`. One of `blkPrimary`/`blkSecondary`, decided once per
   * `MATERIAL_SPAN` region rather than once per cell, and the only thing
   * `materialise` writes for a `Kind.SOLID` cell.
   */
  private blkHost = new Int32Array(256);
  /**
   * **What `Kind.VEIN` is made of here: the block biome's first *solid* accent.**
   *
   * Not `blkAccent`, and the difference is the whole reason this table exists.
   * `blkAccent` is the material the *ore pass* paints, and a third of the
   * roster's accents are gas, liquid, sludge or plasma — METHANE, MERCURY,
   * MOLTEN_IRON, BIO_SLUDGE, RADIANT_MATTER — which is why that pass halves
   * their share (`veinShare`) and paints them only where the rock **encloses**
   * them. A drawing has no such escape: the author put a seam on the page and
   * the seam has to be there, facing whatever air the drawing left beside it, in
   * all twelve biomes. So the class takes the biome's first accent that can hold
   * its own shape, and `tests/tileVocabulary.test.ts` asserts every biome has
   * one rather than trusting the roster to keep having one.
   *
   * Deliberately **not** contaminated, for the reason `blkGlow` is not: a room
   * is cut into the seam that makes its biome what it is, and a neighbour's
   * mineral arriving in the one drawn mass of a room reads as the wrong room
   * rather than as an intrusion. Contamination stays where it was — on the
   * *scatter*, which is where an intrusion is legible as one.
   */
  private blkVein = new Int32Array(256);
  /** Per-block ore threshold, from the block biome's `terrain.oreDensity`. */
  private blkOreThresh = new Float32Array(256);
  private blkTemp = new Int32Array(256);
  private blkDeposit = new Float32Array(256);
  /** Per-block volatile substitute for `blkFill`, under `volatileFillBias`. */
  private blkVolatile = new Int32Array(256);

  // Deferred shaft landings — (y, x0, x1) triples in world coords, already
  // clipped to the chunk. Applied after every gallery, so a gallery's slab can
  // never bury the landing that makes the shaft climbable.
  private landReq = new Int32Array(768);
  private landCount = 0;

  private cntOpen = new Int32Array(256);
  private cntShell = new Int32Array(256);
  private cntGate = new Int32Array(256);
  private cntFloor = new Int32Array(256);
  private pathBlocks = new Uint8Array(256);

  private rng = new Rng(1);
  private tileRng = new Rng(2);
  private tileRef: TileRef = makeTileRef();
  private tileCtx: TileCtx;
  private builtTiles = new Int32Array(12); // ux,uy,orient triples, deduped
  private builtCount = 0;

  private baseX = 0;
  private baseY = 0;

  /** chunkKey -> index into `anchors`, so eviction+regen doesn't duplicate. */
  private anchorIndex = new Map<number, number>();

  constructor(cfg: WorldGenConfig) {
    registerMaterials();
    this.seed = cfg.seed | 0;
    this.mode = cfg.mode;
    this.noRouter = cfg.noRouter === true;
    this.megaHollow = cfg.megaHollow === undefined ? -1 : clamp01(cfg.megaHollow);
    this.archetypeOverride = cfg.archetypeOverride;
    this.cohesion = clamp01(cfg.cohesion);
    this.depthBias = cfg.depthBias ?? 0.35;
    this.biomes = cfg.biomes;
    // `biomeAt` promises a `BiomeDef` at every coordinate and every caller is
    // typed to believe it (#71). With nothing in the roster there is no such
    // value to return, so the roster is refused here — loudly, at construction
    // — rather than answered with an `undefined` a thousand cells later.
    if (this.biomes.length === 0) throw new Error('WorldGen: biome roster is empty');
    this.modifiers = cfg.modifiers ?? NEUTRAL_WORLD_MODIFIERS;
    this.tileCtx = makeTileCtx(this.kind, this.protect);

    for (let i = 0; i < this.biomes.length; i++) this.idToIndex.set(this.biomes[i].id, i);
    for (const b of this.biomes) {
      const list: number[] = [];
      for (const n of b.neighbours) {
        const k = this.idToIndex.get(n);
        if (k !== undefined) list.push(k);
      }
      // A biome with no declared neighbours contaminates with itself, which is
      // the correct no-op rather than a crash.
      if (list.length === 0) list.push(this.idToIndex.get(b.id) ?? 0);
      this.neighbourIdx.push(list);
    }

    this.deriveCohesion();
    this.deriveModifiers();
    this.generator = (chunk, cx, cy) => this.generateChunk(chunk, cx, cy);
  }

  /**
   * Point the generator at a new seed.
   *
   * Every chunk is a pure function of `(seed, cx, cy)`, so a new world is a new
   * seed plus a cleared chunk cache — the caller must also call
   * `CellWorld.clear()`, or the old world's resident chunks answer for the new
   * one. Without this the second run of a session was byte-for-byte the first:
   * `Game` advanced its seed on death and nothing downstream ever read it, so
   * you respawned in the same cave with the same rooms and the same enemies.
   */
  reseed(seed: number, modifiers?: WorldGenModifiers): void {
    this.seed = seed | 0;
    this.anchors.length = 0;
    this.structure.clear();
    this.repairs = 0;
    // Modifiers are chosen per run, not per profile, so a reseed is exactly the
    // moment a new set takes effect. Passing nothing keeps the current set,
    // because the common reseed — dying and starting again — must not silently
    // strip the challenge the player opted into.
    if (modifiers !== undefined) {
      (this as { modifiers: WorldGenModifiers }).modifiers = modifiers;
      this.deriveModifiers();
    }
    this.deriveCohesion();
  }

  /**
   * Resolve the modifier struct into the two things the hot paths need: a flag
   * that turns all of it off, and the void material list as ids.
   *
   * Ids and not names, because `deposits()` runs over 16384 cells and a string
   * lookup per substitution would put a hash map in the one loop this file's
   * header says has no property lookups in it.
   */
  private deriveModifiers(): void {
    const m = this.modifiers;
    this.plainWorld = isNeutral(m);
    if (m.voidBleed <= 0 || m.voidMaterials.length === 0) {
      this.voidMats = new Int32Array(0);
      return;
    }
    const ids: number[] = [];
    for (const name of m.voidMaterials) {
      const id = (MAT as Record<string, number>)[name];
      // A modifier naming a material that no longer exists costs the player
      // that material, not the run — the same rule `resolveWorldModifiers`
      // applies to unknown ids.
      if (id !== undefined) ids.push(id);
    }
    this.voidMats = Int32Array.from(ids);
  }

  // ------------------------------------------------------------- cohesion

  private deriveCohesion(): void {
    const c = this.cohesion;

    // The spine. Resolving MAIN_PATH against the supplied roster rather than
    // assuming it means a caller can hand us any subset — single-biome test
    // worlds included — and still get an ordered descent.
    const main: number[] = [];
    for (const id of MAIN_PATH) {
      const k = this.idToIndex.get(id);
      if (k !== undefined) main.push(k);
    }
    if (main.length === 0) {
      for (let i = 0; i < this.biomes.length; i++) main.push(i);
      main.sort((a, b) => this.biomes[a].index - this.biomes[b].index);
    }
    this.bandOrder = main;

    const side: number[] = [];
    for (const id of SIDE_CONTENT) {
      const k = this.idToIndex.get(id);
      if (k !== undefined) side.push(k);
    }
    this.sideOrder = side;

    // `depthBias: 0` used to mean "flat world" and still does; with bands that
    // is the whole world wearing band 0, which is what the single-biome
    // material fixtures rely on.
    this.banded = this.depthBias > 0;

    // Cohesion no longer picks the roster — depth does. What it buys now is
    // how *sharp* the stack is. Blended smears each band boundary into a
    // ~100-cell transition and pulls the side wings in close, so palettes
    // interleave and you notice you are somewhere else fifty rooms late.
    // Layered keeps hard borders and pushes the wings out, so a band reads as
    // one place (docs/05 §1).
    this.bandBlend = Math.max(0, c - 0.5) * 2 * 140;
    this.sideX = Math.round(lerp(1600, 800, c));

    // Cross-contamination is unchanged and remains the Layered-mode mechanism:
    // docs/05 §1 asks for 15-25% at low cohesion.
    this.contamination = 0.25 - 0.2 * c;

    // Strata wavelength — the only reach `cohesion` was given, and the reason
    // it is not more than that is a measurement rather than caution.
    //
    // `docs/21` §2.2 swept this knob end to end and measured it changing the
    // world's material coherence length by **6 %**; §4.3's verdict was "either
    // make it mean something or delete it". Four levers were tried and measured
    // on the way to this line — the strata wavelength, the strata threshold, the
    // vein area share, and the loose-deposit density — and together they get the
    // rock face purity to move 9.3 % and the median material run 9.4 % across
    // the knob's full range, roughly one and a half times what `docs/21`
    // measured, which is not what "make it mean something" means.
    //
    // **And all four spend the same currency.** `exp(H)` over material area
    // shares falls from 5.03 to 3.08 across the sweep, outside `docs/21` §3.1's
    // "hold at 5-7" and below Alucard's 6.46, because *every* way of making a
    // region more coherent works by removing the materials that were only a few
    // cells across. That is the same objection §2.2 raises against `distinct
    // materials per screen` as a metric, arriving from the other direction, and
    // it is general: coherence and effective material count are in tension, and
    // the 5-7 hold caps what this knob can buy before it is buying it out of the
    // palette. See issue #63's report.
    //
    // So what stays is the one lever that coarsens rather than deletes, at the
    // strength that keeps `exp(H)` inside the hold: the primary/secondary split
    // is the material field the shell cannot hide, and this widens it by up to
    // 2.2x rather than thresholding the secondary rock out of existence.
    this.strataScale = 1 + c * 1.2;

    this.deriveArchitecture();
  }

  /**
   * The per-band architecture table — issue #63.
   *
   * Both quantities are **integrated rather than sampled**, and that is the
   * whole trick. A cave field evaluated as `fbm2(x, y * aniso)` with a
   * per-band `aniso` would jump phase at every band line — at y = 3600 the
   * sample point moves from 1385 to 1125 when aniso goes 2.6 -> 3.2 — which is a
   * hard seam straight across the world, exactly the discontinuity `docs/17`
   * §3.2 warns about. Warping y by the *integral* of the anisotropy instead
   * keeps the field continuous and only changes its slope, so a cave still
   * crosses the border and is merely stretched differently on the two sides.
   *
   * The deck lattice needs the same property and gets it a cheaper way: each
   * band's lattice is **anchored where the band above's next slab would have
   * been**, so a pitch change is a riser of exactly zero. Without that anchor a
   * band line could fall a few cells into a period and leave one gallery, the
   * width of the world, too short to stand up in.
   *
   * With one biome, or with every biome declaring the same `verticality`, both
   * reduce exactly to the module constants they replaced.
   */
  private deriveArchitecture(): void {
    const nb = Math.max(1, this.bandOrder.length);
    this.bandAniso = new Float64Array(nb);
    this.bandWarpBase = new Float64Array(nb);
    this.bandPitch = new Int32Array(nb);
    this.bandSlab = new Int32Array(nb);
    this.bandAnchor = new Int32Array(nb);
    for (let k = 0; k < nb; k++) {
      const t = this.biomes[this.bandOrder[k]].terrain;
      this.bandAniso[k] = caveAnisoFor(t.verticality);
      this.bandPitch[k] = shelfPitchFor(t.verticality);
      this.bandSlab[k] = shelfSlabFor(this.bandPitch[k]);
      if (k > 0) {
        this.bandWarpBase[k] = this.bandWarpBase[k - 1] + this.bandAniso[k - 1] * BAND_HEIGHT;
        const yk = k * BAND_HEIGHT;
        const p = this.bandPitch[k - 1];
        const r = (((yk - this.bandAnchor[k - 1]) % p) + p) % p;
        this.bandAnchor[k] = r === 0 ? yk : yk + (p - r);
      }
    }
  }

  /** Which band's architecture applies at a world y. Clamped, like the palette. */
  private archBand(worldY: number): number {
    const nb = this.bandAniso.length;
    if (!this.banded || nb === 1) return 0;
    const k = Math.floor(worldY / BAND_HEIGHT);
    return k < 0 ? 0 : k >= nb ? nb - 1 : k;
  }

  /**
   * The cave field's y coordinate: `integral of aniso(y) dy`, continuous and
   * monotone. Identical to `y * 2.6` when every band agrees.
   */
  private caveWarpY(worldY: number): number {
    const k = this.archBand(worldY);
    return this.bandWarpBase[k] + this.bandAniso[k] * (worldY - k * BAND_HEIGHT);
  }

  /** Deck pitch in cells at a world y. */
  private shelfPitchAt(worldY: number): number {
    return this.bandPitch[this.archBand(worldY)];
  }

  /** Slab thickness in cells at a world y. */
  private shelfSlabAt(worldY: number): number {
    return this.bandSlab[this.archBand(worldY)];
  }

  /** Cells into the current deck period at a world y, before the terrace offset. */
  private shelfPhaseAt(worldY: number, offset: number): number {
    const k = this.archBand(worldY);
    const p = this.bandPitch[k];
    const q = worldY - this.bandAnchor[k] + offset;
    return ((q % p) + p) % p;
  }

  // ---------------------------------------------------------- biome field

  /**
   * Index into `this.biomes`. Pure function of world position and seed.
   *
   * Was a 2D warped lattice whose hash dominated a weak depth bias, so biomes
   * were a patchwork in X *and* Y and you crossed four of them in a hundred
   * cells (issue #1 §3 cause 3). Now `y` alone picks the band and `x` only
   * decides whether you have walked far enough sideways to leave the spine.
   *
   * **Total, and it has to be** — issue #71. Every caller is typed to receive a
   * `BiomeDef` from `biomeAt`, so every caller dereferences the result: the
   * backdrop reads `.parallax`, `blockPalettes` reads `.palette` 256 times per
   * chunk, `enemiesAt` reads `.enemies`. There is nowhere for an `undefined` to
   * be handled, and adding a check at one of them would only convert a crash
   * into a silently wrong biome at the others. The input guard below, plus the
   * constructor's refusal of an empty roster, are what make the declared return
   * type true rather than aspirational.
   */
  biomeIndexAt(worldX: number, worldY: number): number {
    // #76: the sanitiser below makes this total, which is #71's deliberate fix
    // and stays. This says it happened. A non-finite coordinate now resolves to
    // a perfectly ordinary biome — quieter than the crash it replaced, and
    // strictly worse, because nothing downstream can tell it apart from a real
    // place. Above the early return, so an unbanded world is witnessed too.
    checkWorldCoord(worldX, worldY, 'biomeIndexAt');

    const nb = this.bandOrder.length;
    if (!this.banded || nb === 1) return this.bandOrder[0];

    // `NaN` and `±Infinity` inhabit `number`, and a coordinate is read straight
    // off the camera — `renderX` is `x + shakeX + kickX` over three separately
    // written accumulators, any of which going non-finite for one frame lands
    // here. The warp below turns any of them into a `NaN` `wy`, which then
    // survives the band clamp — `NaN` is neither `< 0` nor `>= nb` — and
    // indexes `bandOrder` off its domain. That `undefined` is the crash #71
    // reported.
    //
    // Sanitising the *inputs* rather than the derived band keeps the repair at
    // the one place the world stops being a function of position, and leaves
    // both clamps below meaning exactly what they say: an infinity keeps its
    // sign, so `y = +Infinity` still lands in the deepest band rather than
    // being silently teleported to the surface, and only `NaN` — which has no
    // position to preserve — becomes the origin.
    worldX = finiteCoord(worldX);
    worldY = finiteCoord(worldY);

    // Domain warp: without it band borders are visibly straight ruler lines,
    // which is the same failure mode the herringbone layer exists to avoid one
    // scale down. Amplitude stays well under half a band so the warp cannot
    // swap the order of two bands along any column.
    const wx =
      worldX + (fbm2(worldX * 0.0011, worldY * 0.0011, 2, 2, 0.5, this.seed ^ 0x91) - 0.5) * WARP_AMP;
    const wy =
      worldY + (fbm2(worldX * 0.0011 + 37.5, worldY * 0.0011 - 11.25, 2, 2, 0.5, this.seed ^ 0x92) - 0.5) * WARP_AMP;

    // The surface wears band 0's palette, and below the last band the deepest
    // one continues — neither is a place the run is supposed to end.
    let band = Math.floor(wy / BAND_HEIGHT);
    if (band < 0) band = 0;
    else if (band >= nb) band = nb - 1;

    const ns = this.sideOrder.length;
    if (ns > 0 && (wx > this.sideX || wx < -this.sideX)) {
      const s = Math.floor((band * ns) / nb);
      return this.sideOrder[s < ns ? s : ns - 1];
    }

    if (this.bandBlend > 0) {
      const d = wy - band * BAND_HEIGHT;
      const dir = d < this.bandBlend ? -1 : d > BAND_HEIGHT - this.bandBlend ? 1 : 0;
      if (dir !== 0) {
        // Probability falls to zero at the edge of the margin, so the two
        // palettes dovetail instead of meeting at a second hard line.
        const t = dir < 0 ? 1 - d / this.bandBlend : 1 - (BAND_HEIGHT - d) / this.bandBlend;
        const bx = Math.floor(wx / BLEND_CELL);
        const by = Math.floor(wy / BLEND_CELL);
        if (hash2f(bx, by, this.seed ^ 0x77c1) < t * 0.55) {
          const n2 = band + dir;
          if (n2 >= 0 && n2 < nb) return this.bandOrder[n2];
        }
      }
    }
    return this.bandOrder[band];
  }

  /** Band index at a depth, clamped to the spine. Diagnostics and tests. */
  bandAt(worldY: number): number {
    const nb = this.bandOrder.length;
    const b = Math.floor(worldY / BAND_HEIGHT);
    return b < 0 ? 0 : b >= nb ? nb - 1 : b;
  }

  /** The main path, top to bottom, as biome ids. */
  mainPath(): string[] {
    return this.bandOrder.map((i) => this.biomes[i].id);
  }

  biomeAt(worldX: number, worldY: number): BiomeDef {
    return this.biomes[this.biomeIndexAt(worldX, worldY)];
  }

  /**
   * Enemy pool at a point, including cross-contamination. A Layered-mode
   * region draws 15-25% of its roster from adjacent regions — that is what
   * makes a cyberpunk drone in the crypt a *signal* rather than noise.
   */
  enemiesAt(worldX: number, worldY: number): string[] {
    const idx = this.biomeIndexAt(worldX, worldY);
    const b = this.biomes[idx];
    const out = b.enemies.slice();
    const foreign = this.neighbourIdx[idx];
    const take = Math.round(b.enemies.length * this.contamination);
    for (let i = 0; i < take; i++) {
      const src = this.biomes[foreign[hash2(worldX, worldY + i, this.seed ^ 0xe1) % foreign.length]];
      out.push(src.enemies[hash2(worldX + i, worldY, this.seed ^ 0xe2) % src.enemies.length]);
    }
    return out;
  }

  // ------------------------------------------------------ surface geometry

  /**
   * Height of the outdoor ground at a column, in world cells (+y is down).
   *
   * Relief is damped to nothing within 150 cells of spawn and eased in over the
   * next 190, so the first screen is a flat, legible terrace and the landscape
   * still has shape by the time you have walked anywhere. Past `CLIFF_X` the
   * ground climbs into a cliff: docs/08 §2's "teach without text" — west is
   * interesting and a soft dead end, east is the way down.
   *
   * The relief is terraced for the same reason the ledge field is: a smooth
   * heightfield has no level ground on it, and level ground is the thing the
   * player is short of.
   *
   * **`TERRACE` is the second damped interval and it is issue #140's** — the
   * ground the Great Tree stands on. `greatTree.ts` states why in one line: a
   * landmark whose base y is a constant is a landmark that is the same in every
   * world, and that is what lets the surface be persistent while the descent
   * stays seeded (`docs/27` §3). The two dampers combine with a `min`, so the
   * seed still owns every column outside both.
   */
  surfaceHeight(worldX: number): number {
    /*
     * **The mountain is not damped, it is substituted — issue #140.**
     *
     * `TERRACE` above is `docs/27` §7 item 2 as the Great Tree needed it: the
     * tree *stands on* the heightfield, so the heightfield is damped flat under
     * its feet and the seed still owns the column, it just has nothing left to
     * say. The mountain **is** the heightfield for the 1 064 columns it spans,
     * so damping would be the wrong shape — an authored profile added on top of
     * a damped-but-still-present relief is a landmark that can disagree with the
     * ground it is made of, and there is no ease to get wrong if there is
     * nothing underneath to ease into.
     *
     * The seam either side is deliberate rather than smoothed: the west one is
     * the cave's lip (`docs/27` §13 gate 1) and the east one is the desert
     * mesa's face, which is `CLIFF_X` mirrored. `mountain.ts` reads no seed, so
     * this branch is what makes the whole east surface seed-invariant.
     */
    const mg = mountainGround(worldX);
    if (mg !== null) return mg;

    const ax = worldX < 0 ? -worldX : worldX;
    const tSpawn = ax <= 150 ? 0 : ax >= 340 ? 1 : smoothstep((ax - 150) / 190);
    const t = Math.min(tSpawn, terraceDamp(worldX));
    const r = fbm2(worldX * 0.0038, 0.5, 3, 2, 0.5, this.seed ^ 0x5c1fa0e) - 0.5;
    let h = SURFACE_Y + Math.round((r * 56 * t) / SHELF_STEP) * SHELF_STEP;
    if (worldX < CLIFF_X) h -= Math.min(240, (CLIFF_X - worldX) * 0.85);
    return Math.round(h);
  }

  /** How far the grove pond cuts below the ground line at a column. */
  private pondDepth(worldX: number): number {
    const d = (worldX - POND_CX) / POND_RX;
    const q = 1 - d * d;
    return q <= 0 ? 0 : Math.round(POND_DEPTH * Math.sqrt(q));
  }

  // ---------------------------------------------------------- spawn point

  /**
   * A point on the authored surface with the feet one cell above solid ground.
   *
   * Was a lattice coordinate with no ground check (issue #1 §3 cause 4), which
   * is why six of eight seeds had no floor at all within 80 cells below spawn
   * and why the altar had to grow a terrace to patch it.
   */
  spawnPoint(): { x: number; y: number } {
    // halfH is 11, so feet land at `ground - 1`.
    return { x: SPAWN_X, y: this.surfaceHeight(SPAWN_X) - 12 };
  }

  /** Centre of the descent mouth, at the surface. Visible from spawn. */
  descentMouth(): { x: number; y: number } {
    const x = (DESCENT_X0 + DESCENT_X1) >> 1;
    return { x, y: this.surfaceHeight(x) };
  }

  // ------------------------------------------------ sanctums and rest nodes
  //
  // Both are queried rather than stored: the geometry is a pure function of
  // (seed, band), so there is no table to keep in sync with the chunk cache and
  // no way for an evicted chunk to lose one.

  /** Every sanctum on the spine, top to bottom. One per band boundary (#1 §4E). */
  sanctums(): Sanctum[] {
    return allSanctums(this.seed, this.bandOrder.length, BAND_HEIGHT);
  }

  /** The optional workbenches inside one band (#6). Off the spine, both flanks. */
  restNodes(band: number): RestNode[] {
    return restNodesInBand(this.seed, band, BAND_HEIGHT, (x, y) => this.shelfFloorAt(x, y));
  }

  allRestNodes(): RestNode[] {
    const out: RestNode[] = [];
    for (let b = 0; b < this.bandOrder.length; b++) out.push(...this.restNodes(b));
    return out;
  }

  /**
   * What kind of room a chunk is — issue #25.
   *
   * Chunk-granular because that is what the map draws: `game.ts` calls
   * `enterRoom(run, `${cx},${cy}`)`, so a room *is* a chunk as far as the room
   * graph is concerned. A sanctum spans several chunks, and only the one
   * holding its centre is named for it — otherwise a single checkpoint would
   * draw as a four-cell smear and the "exactly one per band" reading the player
   * navigates by would be lost.
   *
   * Every branch below is a thing generation actually put there. Nothing here
   * may be added without a generator that places it.
   */
  roomKindAt(cx: number, cy: number): RoomKind {
    const y0 = cy << CHUNK_SHIFT;
    const y1 = y0 + CHUNK_SIZE - 1;
    for (const s of sanctumsOverlapping(this.seed, this.bandOrder.length, BAND_HEIGHT, y0, y1)) {
      if (s.floorY >= y0 && s.floorY <= y1 && (s.x >> CHUNK_SHIFT) === cx) return 'sanctum';
    }
    for (const n of this.restNodesNear(y0, y1)) {
      if (n.floorY >= y0 && n.floorY <= y1 && (n.x >> CHUNK_SHIFT) === cx) return 'rest';
    }
    // The authored surface — sky, grove and the descent mouth — is a place in
    // its own right, and it is the only room a run starts in.
    if (y1 < 0) return 'surface';
    return 'normal';
  }

  /**
   * Spatially adjacent anchor pairs (east and south neighbours in the anchor
   * lattice). This is what "consecutive anchors on the critical path" means —
   * array order is generation order and would be meaningless.
   */
  anchorPairs(): [number, number][] {
    const byChunk = new Map<number, number>();
    for (let i = 0; i < this.anchors.length; i++) {
      const a = this.anchors[i];
      byChunk.set(chunkKeyOf(a.x >> CHUNK_SHIFT, a.y >> CHUNK_SHIFT), i);
    }
    const pairs: [number, number][] = [];
    for (const [key, i] of byChunk) {
      const cx = (key >> 16) - 32768;
      const cy = (key & 0xffff) - 32768;
      const e = byChunk.get(chunkKeyOf(cx + 1, cy));
      if (e !== undefined) pairs.push([i, e]);
      const s = byChunk.get(chunkKeyOf(cx, cy + 1));
      if (s !== undefined) pairs.push([i, s]);
    }
    return pairs;
  }

  /** Anchors that have a full ring of generated neighbours. */
  interiorAnchors(): Anchor[] {
    const present = new Set<number>();
    for (const a of this.anchors) present.add(chunkKeyOf(a.x >> CHUNK_SHIFT, a.y >> CHUNK_SHIFT));
    const out: Anchor[] = [];
    for (const a of this.anchors) {
      const cx = a.x >> CHUNK_SHIFT;
      const cy = a.y >> CHUNK_SHIFT;
      let full = true;
      for (let dy = -1; dy <= 1 && full; dy++) {
        for (let dx = -1; dx <= 1; dx++) {
          if (!present.has(chunkKeyOf(cx + dx, cy + dy))) {
            full = false;
            break;
          }
        }
      }
      if (full) out.push(a);
    }
    return out;
  }

  // ------------------------------------------------------------ generation

  private generateChunk(chunk: Chunk, cx: number, cy: number): void {
    const baseX = cx << CHUNK_SHIFT;
    const baseY = cy << CHUNK_SHIFT;
    this.baseX = baseX;
    this.baseY = baseY;
    this.rng.reseed(hash2(cx, cy, this.seed ^ 0x1f2e3d4c));

    this.blockPalettes(cx, cy);
    // Everything above y = 0 is the authored surface, and it shares nothing
    // with the underground pipeline but materialisation: no cave field, no
    // herringbone tiles, no corridor router. Sky is not cheap terrain, it is a
    // different thing.
    if (baseY + CHUNK_SIZE <= 0) {
      this.surfaceFields(baseX, baseY);
      this.surfaceCarve(baseX, baseY);
      this.grove(baseX);
      // The authored landmarks (issue #181's registry: the Great Tree, the
      // Mountain), each in two halves either side of materialisation: the
      // shape has to be a `kind` so the block tags and the connectivity
      // verifier see it, and the material has to be written afterwards
      // because `materialise` only knows how to paint out of the biome
      // palette. See `landmarks.ts`'s own header — one oracle per landmark,
      // two shared passes. Registration order is stamp order: tree before
      // mountain, matching the pre-registry call order.
      //
      // Before `descentShaft` because the shaft is the one guaranteed route
      // out of the surface and it outranks everything: the mountain's foot
      // *is* the mouth's east wall, and where the two overlap the shaft's
      // lining and its `protect` 2 must be what survives.
      for (const lm of LANDMARKS) stampLandmarkKinds(lm, this.kind, this.protect, this.pathBlocks, baseX, baseY);
      this.descentShaft();
      this.materialise(chunk, baseX, baseY);
      for (const lm of LANDMARKS) stampLandmarkDress(lm, chunk, baseX, baseY);
      // No glow veins above the ground line. The surface is lit by the sky
      // (`world/lighting.ts` `DAYLIGHT`), and a bioluminescent seam in a cliff
      // face at noon is a light nobody can see doing nothing for legibility.
      this.deposits(chunk);
      this.pond(chunk, baseX, baseY);
    } else {
      this.noiseFields(baseX, baseY);
      this.shelfTable(baseX, baseY);
      this.baseCarve();
      // `docs/20` §9.2 slot 4c: after the field and before anything that fits
      // itself to the terrain, because all three of those passes have to be
      // masked by it. Running it later would mean the shelf lattice striping a
      // cavern that already existed, which is the mirror image of the hazard
      // tModLoader warns about and the one this codebase can actually hit.
      this.megaFeatures();
      this.placeTiles(cx, cy);
      if (!this.noRouter) this.routeCorridors(cx, cy);
      this.ledges();
      this.descentShaft();
      // After the router and the ledges so neither can lay a slab through a
      // chamber; before the vaults, which skip protected cells and so cannot
      // be stamped into a sanctum wall.
      this.sanctumChambers();
      this.restChambers();
      // After both, and before the vaults, for the same reason they are: a set
      // piece is authored geometry, so nothing that fits itself around the
      // terrain may be allowed to run over it. Set pieces are kept 300 cells
      // clear of every band line (`setPiece.ts` BAND_MARGIN) so the ordering
      // between this and `sanctumChambers` can never actually be tested.
      this.setPieceChambers();
      this.sealedVaults(cx, cy);
      this.materialise(chunk, baseX, baseY);
      this.glowVeins(chunk);
      this.deposits(chunk);
      this.pools(chunk);
      this.hazardPockets(chunk);
      this.furnish(chunk, baseY);
    }
    this.annotate(cx, cy);
    this.recordAnchor(cx, cy);
  }

  // ---- 1. per-block biome + flattened palettes

  private blockPalettes(cx: number, cy: number): void {
    const baseX = cx << CHUNK_SHIFT;
    const baseY = cy << CHUNK_SHIFT;
    const mDeposit = this.modifiers.depositDensityMul;
    const mVolatile = this.modifiers.volatileFillBias;
    for (let by = 0; by < BLOCKS_PER_CHUNK; by++) {
      for (let bx = 0; bx < BLOCKS_PER_CHUNK; bx++) {
        const b = (by << 4) | bx;
        const wx = baseX + (bx << BLOCK_SHIFT) + 4;
        const wy = baseY + (by << BLOCK_SHIFT) + 4;
        const idx = this.biomeIndexAt(wx, wy);
        this.blkBiome[b] = idx;
        const bio = this.biomes[idx];
        // Band 0 begins at the ground line, not above it — docs/24 §10. The
        // surface declares its own palette (`surfacePalette()`, biomes.ts),
        // so the Great Tree no longer stands on ground made of its own two
        // materials. Everything else about the block — temperature, light,
        // deposit densities, the biome index itself — stays the band's, which
        // is the continuity `MAIN_PATH`'s header used to claim for the
        // palette: the place is the same place, the ground just has a colour
        // of its own.
        const p = wy < 0 ? surfacePalette() : bio.palette;

        // Which *material* this region is made of is decided on the
        // MATERIAL_SPAN lattice, not per 8x8 block — see the constant. Both
        // draws below key on this pair, so a region agrees with itself across
        // chunk borders without any chunk asking a neighbour.
        const sx = Math.floor(wx / MATERIAL_SPAN);
        const sy = Math.floor(wy / MATERIAL_SPAN);

        this.blkPrimary[b] = p.primary;
        this.blkSecondary[b] = p.secondary;
        this.blkFill[b] = p.fill;
        this.blkLiquid[b] = p.liquid;
        this.blkLiquid2[b] = p.liquid2;
        this.blkHazard[b] = p.hazard;
        this.blkPlatform[b] = p.platform;
        this.blkAccent[b] = p.accents[hash2(sx, sy, this.seed ^ 0xacce) % p.accents.length];
        // Deliberately NOT contaminated below. A biome's light is the one thing
        // about it that must stay legible at a glance from across a dark room
        // (#31) — a Rotwood chamber lit by the Cinder March's ember seam would
        // read as the wrong biome before any of its materials were in frame.
        this.blkGlow[b] = p.glow;
        // Same exemption, one slot over, and the same sentence: a drawn seam is
        // the room's identity rather than a vein of something passing through.
        this.blkVein[b] = drawableAccentOf(p);
        // Scaled by the band's deck pitch. `terrain.glowDensity` is a rate per
        // *area*, and issue #31 tuned it against a world whose galleries were
        // all 25 cells tall on a 48-cell pitch. A band with a wider pitch has
        // more open volume behind the same area, so the same rate lights it
        // less: at a pitch of 52 the Cathedral's median playable luminance fell
        // from 0.78 to 0.56, under `LEGIBLE_LUMINANCE`, and
        // `tests/lighting.test.ts` caught it. Light is a property of the volume
        // a player stands in, so the rate follows the volume.
        this.blkGlowDensity[b] =
          bio.terrain.glowDensity * Math.max(1, this.shelfPitchAt(wy) / 48) * MEGA_GLOW;
        this.blkShell[b] = p.shell;
        this.blkTemp[b] = bio.ambientTemp;
        // Clamped: `depositDensity` is compared against a uniform, so a
        // multiplier that pushes it past 1 stops being "more drifts" and
        // becomes "every span is a drift", which is a floor, not a world.
        this.blkDeposit[b] = Math.min(0.95, bio.terrain.depositDensity * mDeposit);
        this.blkVolatile[b] = volatileFillOf(p);

        // Cross-contamination: swap exactly one palette slot for a neighbour
        // biome's. One slot, not all — a foreign material should read as an
        // intrusion, not as a different biome. Keyed on MATERIAL_SPAN for the
        // same reason: an intrusion the size of a footstep is not an intrusion.
        // Never above the ground line: the surface is authored, its palette is
        // a declaration rather than a sample, and a band material intruding
        // into it would put the borrowed colours docs/24 §10 removed straight
        // back, one region at a time.
        const h = hash2(sx, sy, this.seed ^ 0xc0a7);
        if (wy >= 0 && (h & 0xffff) / 65536 < this.contamination) {
          const nb = this.neighbourIdx[idx];
          const src = this.biomes[nb[(h >>> 16) % nb.length]].palette;
          switch ((h >>> 8) & 3) {
            case 0:
              this.blkSecondary[b] = src.secondary;
              break;
            case 1:
              this.blkFill[b] = src.fill;
              break;
            case 2:
              this.blkAccent[b] = src.accents[(h >>> 20) % src.accents.length];
              break;
            default:
              this.blkHazard[b] = src.hazard;
              break;
          }
        }

        // After contamination, because a block wearing a neighbour's accent is
        // veined with *that* material and the two may not be the same phase.
        this.blkOreThresh[b] = oreThreshold(veinShare(bio, this.blkAccent[b]));

        // Also after contamination, and for the same reason: a region wearing a
        // neighbour's `secondary` must be able to be *made of* it.
        this.blkHost[b] = this.hostRockOf(bio, wx, wy, this.blkPrimary[b], this.blkSecondary[b]);

        // Volatile Strata. Substituted per *block*, not per cell: a bank of
        // accelerant is a place you decide how to cross, and a coin flip per
        // cell is a texture nobody can read. Applied after contamination so a
        // biome wearing a neighbour's fill is biased on what it actually has.
        if (mVolatile > 0 && hash2f(wx, wy, this.seed ^ 0x0117e1) < mVolatile) {
          this.blkFill[b] = this.blkVolatile[b];
        }
      }
    }
  }

  /**
   * **The host rock of one `MATERIAL_SPAN` region — issue #143.**
   *
   * This is the whole of the change, and it is one word long: *region*.
   *
   * The strata field decides which of the biome's two bulk rocks a place is made
   * of. It used to be read **per cell**, bilinearly, inside `materialise`, so a
   * wall face changed rock wherever the field crossed 0.55 — and since the field
   * is continuous, the crossing is a curve that wanders through the rock rather
   * than a boundary between two masses. Two materials drawn that way do not read
   * as two places; they read as one speckled place, and with `shell`, `platform`
   * and a vein on top of them a 64-cell patch of the world held **three**
   * materials over 5% of it where the two spaces a human here hand-placed hold
   * one (`tests/paletteStructure.test.ts` §6).
   *
   * Read here instead: **one sample per region, at the region's centre.** The
   * field is unchanged, its wavelength is unchanged, its authored per-biome
   * scale (`noiseScale`, `octaves`) is unchanged — only the rate at which the
   * answer is allowed to change. A stratum boundary is now a region edge, which
   * is a straight line 96 cells long between two solid masses, which is what a
   * geological contact looks like.
   *
   * **`docs/19` §8.3 calls this "fill from biome" and names it the cheapest of
   * the three things worth copying from Noita.** Noita's authored rooms declare
   * a third of their own cells to be *whatever rock the host biome uses*
   * (`#ffffff`, §3.2 — 21-34% of every cave scene, and 36-82% of its outer
   * ring), and that deferral is why a drawn room drops into a generated cave
   * with no seam. The three-way classification the doc asks for — air / host
   * fill / authored material — is already in this generator's `kind[]` array:
   * `Kind.OPEN` is air, `Kind.SHELL`/`PLATFORM`/`GATE` are authored material,
   * and `Kind.SOLID` is the host fill. What was missing was the *resolution*:
   * `Kind.SOLID` resolved to a different material every few cells, so the class
   * that is supposed to be "the rock around here" was not one rock.
   *
   * **It cannot move a single cell of the solid/void mask**, and that is the
   * reason to expect it where #57's three rounds failed. `kind[]` is fully
   * resolved before `materialise` runs and nothing here is a reader of it — the
   * shapes stay exactly where they are and only their material changes.
   * `tests/worldshape.test.ts` asserts that as a same-seed mask identity.
   *
   * DETERMINISM. A pure function of the region index, the block's biome and the
   * seed, so two chunks sharing a region agree without talking and a chunk
   * regenerates byte-identically after eviction — the same property
   * `MATERIAL_SPAN`'s own comment claims for the accent draw.
   *
   * COST. One `ridge2` per 8x8 block, 256 per chunk. It replaces the 1089
   * per-coarse-cell `ridge2` calls `noiseFields` used to spend on `nStrata`, so
   * the pass is cheaper than it was, and `materialise` loses four array reads
   * and three multiplies from the hottest branch in the file.
   */
  private hostRockOf(bio: BiomeDef, wx: number, wy: number, primary: number, secondary: number): number {
    // The lattice is read through a domain warp, so a contact between two rocks
    // is a meander rather than a straight line on a multiple of 96. See
    // HOST_WARP; without it `the_long_hour` and `coldforge` came out as visible
    // rectangular patchwork, which is the quilt again one octave down.
    const w = 1 / (MATERIAL_SPAN * 2);
    const px = wx + (fbm2(wx * w, wy * w, 2, 2, 0.5, this.seed ^ 0x40057a) - 0.5) * HOST_WARP;
    const py = wy + (fbm2(wx * w + 19.5, wy * w - 7.25, 2, 2, 0.5, this.seed ^ 0x40057b) - 0.5)
      * HOST_WARP;
    const half = MATERIAL_SPAN >> 1;
    const hx = Math.floor(px / MATERIAL_SPAN) * MATERIAL_SPAN + half;
    const hy = Math.floor(py / MATERIAL_SPAN) * MATERIAL_SPAN + half;
    const t = bio.terrain;
    const sxk = 1 / (t.noiseScale * STRATA_X * this.strataScale);
    const syk = 1 / (t.noiseScale * STRATA_Y * this.strataScale);
    // 0.55 verbatim from the per-cell read this replaces. The threshold decides
    // how much of the world is `secondary`; moving it here would confound the
    // question this change asks (does bulk change per region or per cell) with
    // the question it does not (how much of each rock there is).
    return ridge2(hx * sxk, hy * syk, t.octaves, this.seed ^ 0x57ba11) > 0.55
      ? secondary
      : primary;
  }

  // ---- 2. coarse noise

  private noiseFields(baseX: number, baseY: number): void {
    const domB = this.biomes[this.blkBiome[(8 << 4) | 8]];
    const t = domB.terrain;
    // Strata and ore scale follow the chunk's dominant biome. A seam in ore
    // frequency across a chunk border is invisible; a seam in *cave shape*
    // would not be, which is why the cave field alone is globally scaled.
    const ok = 1 / ORE_WAVELENGTH;

    for (let gy = 0; gy < CG; gy++) {
      const wy = baseY + (gy << COARSE_SHIFT);
      const rowB = Math.min(15, gy >> 1) << 4;
      // Per row, because the anisotropy is per band and a chunk may straddle one.
      const cwy = this.caveWarpY(wy) * CAVE_FREQ;
      for (let gx = 0; gx < CG; gx++) {
        const wx = baseX + (gx << COARSE_SHIFT);
        const i = gy * CG + gx;

        const f = fbm2(wx * CAVE_FREQ, cwy, 4, 2, 0.5, this.seed ^ 0xcafe01);
        const r = ridge2(wx * CAVE_FREQ * 0.6, wy * CAVE_FREQ * 0.6, 3, this.seed ^ 0x21d907);
        this.nCave[i] = f * 0.75 + r * 0.25;

        this.nOre[i] = fbm2(wx * ok, wy * ok, 3, 2, 0.5, this.seed ^ 0x0be1);

        const bd = this.biomes[this.blkBiome[rowB | Math.min(15, gx >> 1)]].terrain;
        this.nThresh[i] = caveThreshold(bd.caveDensity);

        this.nHole[i] = fbm2(wx * 0.009, wy * 0.0032, 2, 2, 0.5, this.seed ^ 0x401ec7);
      }
    }
  }

  /**
   * Strata and ore only. The surface has no cave field to threshold and no
   * ledges to lay — its floor is a heightfield — so three of the five noise
   * fields would be computed and thrown away.
   */
  private surfaceFields(baseX: number, baseY: number): void {
    const ok = 1 / ORE_WAVELENGTH;
    for (let gy = 0; gy < CG; gy++) {
      const wy = baseY + (gy << COARSE_SHIFT);
      for (let gx = 0; gx < CG; gx++) {
        const wx = baseX + (gx << COARSE_SHIFT);
        const i = gy * CG + gx;
        this.nOre[i] = fbm2(wx * ok, wy * ok, 3, 2, 0.5, this.seed ^ 0x0be1);
      }
    }
  }


  /**
   * Terrace offset of the ledge lattice at a world point.
   *
   * Quantised in both axes, so the answer is constant over a 16x64-cell cell of
   * the world and every caller — the ledge pass, the corridor router, a test —
   * gets the same number for the same place with no shared buffer. That is the
   * property that lets a gallery carved by one chunk line up with a shelf laid
   * by the next.
   *
   * **`SHELF_STEP` is 3, not 7**, and that is the other half of the deck-lattice
   * fix. At +/-15 quantised to 7 the terrace had exactly five reachable offsets,
   * so however wide the world was, every floor in it landed on one of five
   * residues — which is most of why `docs/17` §1.3 could find 6 residues holding
   * 60.3 % of every floor surface in the game. Eleven offsets spread the same
   * floors over eleven.
   *
   * The *amplitude* deliberately stays at the shipped 30. It was tried at a full
   * period and it costs solid fraction: a larger offset makes neighbouring
   * 32-column buckets' galleries stack rather than abut, which opens volume
   * that `tests/worldshape.test.ts` holds at 0.38-0.70 and which band 5 fell
   * through. The residue count is what the lattice metric measures; the
   * amplitude is what the volume budget pays for. They are separable, and only
   * one of them was the defect.
   */
  private shelfOffsetAt(worldX: number, worldY: number): number {
    const v =
      (fbm2((worldX & ~31) * 0.0025, (worldY & ~63) * 0.0008, 3, 2, 0.5, this.seed ^ 0x5e1f2a) -
        0.5) *
      30;
    return Math.round(v / SHELF_STEP) * SHELF_STEP;
  }

  /** World y of the shelf top at or below `worldY` in this column. */
  private shelfFloorAt(worldX: number, worldY: number): number {
    const r = this.shelfPhaseAt(worldY, this.shelfOffsetAt(worldX, worldY));
    return r === 0 ? worldY : worldY + this.shelfPitchAt(worldY) - r;
  }

  private shelfTable(baseX: number, baseY: number): void {
    for (let by = 0; by < CHUNK_SIZE / SHELF_QY; by++) {
      for (let bx = 0; bx < SHELF_COLS; bx++) {
        this.shelfOff[by * SHELF_COLS + bx] =
          this.shelfOffsetAt(baseX + bx * SHELF_QX, baseY + by * SHELF_QY);
      }
    }
  }

  // ---- 3. base cave carve

  private baseCarve(): void {
    const kind = this.kind;
    const nCave = this.nCave;
    const nThresh = this.nThresh;
    this.protect.fill(0);
    for (let ly = 0; ly < CHUNK_SIZE; ly++) {
      const row = ly << CHUNK_SHIFT;
      const gRow = (ly >> COARSE_SHIFT) * CG;
      const fy = (ly & (COARSE - 1)) * (1 / COARSE);
      for (let lx = 0; lx < CHUNK_SIZE; lx++) {
        const j = gRow + (lx >> COARSE_SHIFT);
        const fx = (lx & (COARSE - 1)) * (1 / COARSE);
        const c0 = nCave[j];
        const cTop = c0 + (nCave[j + 1] - c0) * fx;
        const c2 = nCave[j + CG];
        const cBot = c2 + (nCave[j + CG + 1] - c2) * fx;
        const t0 = nThresh[j];
        const tTop = t0 + (nThresh[j + 1] - t0) * fx;
        const t2 = nThresh[j + CG];
        const tBot = t2 + (nThresh[j + CG + 1] - t2) * fx;
        kind[row + lx] =
          cTop + (cBot - cTop) * fy > tTop + (tBot - tTop) * fy ? K_SOLID : K_OPEN;
      }
    }
  }

  // ---- 3b. mega features: the object at the scale of a screen

  /**
   * One large object per screen of area — issue #62, `docs/20` §9.1 item 1 and
   * §9.2 slot 4c.
   *
   * **The finding this implements.** Terraria has *two* large-object rates:
   * cave runners at 0.4-2.5 screens long and **1.05 per screen**, and
   * mini-biomes of 78-198 tiles at 1 per 80-290 screens. `docs/17`'s set-piece
   * hypothesis was reasoning about the second, which is why it correlated
   * beautifully and then died to its own control — a 128x128 stamp at the
   * landmark rate fixes one screen in a hundred. This is the first rate.
   *
   * **Two families, and the second is not decoration.** A void alone spends
   * solid fraction, and `tests/worldshape.test.ts` holds the world at 0.38-0.70
   * solid for a reason: it is rock with rooms cut in it, not a void with debris.
   * So the same lattice draws a *mass* as often as it draws a void — a blob of
   * forced SOLID at the same scale. It pays the void's rock back, and it is the
   * other half of what `docs/21` §3.3 measures: `grainY` is the autocorrelation
   * of the **solid mask**, and a 60-cell slab of uninterrupted rock lengthens it
   * exactly as a 60-cell cavern does. A cavern with nothing solid around it is
   * also not a cavern; it is the average.
   *
   * **Scale.** `docs/20` §2 settles the anchor: Terraria's camera holds 96x26
   * body-lengths against our 48x12, so screen-match says x4 and body-match says
   * x8 and they disagree. Starbound holds 53x12 and both metrics give **x6**, so
   * the tension vanishes. Terraria's cavern-layer runner is 50-299 tiles long
   * and O6-19; at x6 that is 300-1800 by 36-114, which we take at the short end
   * — a spine of 200-420 cells at a peak radius of 20-38 — because our run is
   * 6.6 screens wide and a 1800-cell feature is a quarter of it.
   *
   * **Direction is per run, not per biome** (`docs/20` §9.1 item 7, §6.2).
   * Terraria's anisotropy is a per-call argument, so a gallery run and a chimney
   * run coexist in one biome, drawn from a distribution the biome's
   * `verticality` parameterises. `tests/worldArchitecture.test.ts` records why
   * that distinction matters: driving one global `CAVE_ANISO` per band was
   * swept 0.4 to 5.0 and moved the finished world's H/V by nothing at all.
   *
   * **Chunk purity.** Everything below is a function of the lattice cell and the
   * seed — `docs/20` §8.4's chunk-pure replacement for `StructureMap`. A feature
   * declares its own bbox from its lattice cell, every chunk the bbox touches
   * computes the identical spine, and the iteration order over lattice cells is
   * sorted in both axes, so two features that overlap resolve the same way
   * whichever chunk is asking.
   */
  private megaFeatures(): void {
    const bx = this.baseX;
    const by = this.baseY;
    const g0x = Math.floor((bx - MEGA_REACH - MEGA_PITCH) / MEGA_PITCH);
    const g1x = Math.floor((bx + CHUNK_SIZE + MEGA_REACH) / MEGA_PITCH);
    const g0y = Math.floor((by - MEGA_REACH - MEGA_PITCH) / MEGA_PITCH);
    const g1y = Math.floor((by + CHUNK_SIZE + MEGA_REACH) / MEGA_PITCH);
    for (let gy = g0y; gy <= g1y; gy++) {
      for (let gx = g0x; gx <= g1x; gx++) this.oneMega(gx, gy);
    }
  }

  /** Rasterise the feature owned by one lattice cell, clipped to this chunk. */
  private oneMega(gx: number, gy: number): void {
    const bx = this.baseX;
    const by = this.baseY;
    const s = this.seed;
    const cx = gx * MEGA_PITCH + (hash2(gx, gy, s ^ 0x3ca7e5) % MEGA_PITCH);
    const cy = gy * MEGA_PITCH + (hash2(gx, gy, s ^ 0x71b3d9) % MEGA_PITCH);
    // Cheapest possible rejection, and it runs ~25 times a chunk. The bbox is
    // the declared one — centre +/- MEGA_REACH — never a measured one, so a
    // chunk can reject a feature without rasterising it.
    if (cx + MEGA_REACH < bx || cx - MEGA_REACH >= bx + CHUNK_SIZE) return;
    if (cy + MEGA_REACH < by || cy - MEGA_REACH >= by + CHUNK_SIZE) return;
    // Nothing that reaches the ground line. `megaFeatures` only runs for chunks
    // that are wholly underground, so no cell above y = 0 could be written in
    // any case; rejecting on the *declared bbox* instead means a feature is
    // never half-drawn, which is what keeps the surface's authored heightfield
    // and the descent mouth from acquiring a bite out of them.
    if (cy - MEGA_REACH < 0) return;

    // **Void or mass, alternating on the lattice** — not an independent coin.
    // A per-cell coin is fine on average and terrible per window: a 360-cell
    // probe holds four lattice cells, three voids in a row happen constantly,
    // and `tests/worldshape.test.ts` read 36 % solid against its 0.38 floor on
    // exactly that. Alternation makes every 2x2 block of the lattice two of
    // each; `MEGA_SWAP` puts back enough disorder that no window shows a
    // checkerboard, which is invisible in any case under a full-cell position
    // jitter, a per-run direction and a 2x spread in length.
    //
    // The share this rule produces is **one in three**, not one in two: the 2x2
    // block above is not representative of a mod-3 residue, and the arithmetic
    // that matters is the global one. Two thirds of the mega lattice is
    // therefore forced rock, and `tests/routerBudget.test.ts` measures what
    // that costs a world with no corridor router in it — see
    // `WorldGenConfig.megaHollow`, which is the override this line reads.
    const hShape = hash2(gx, gy, s ^ 0x5d10ac);
    const hollow =
      this.megaHollow < 0
        ? ((((gx + 2 * gy) % 3) + 3) % 3 === 0) !== ((hShape & 0xffff) / 65536 < MEGA_SWAP)
        : (hShape & 0xffff) / 65536 < this.megaHollow;
    const hSize = hash2(gx, gy, s ^ 0x2f8a41);
    // Per-run direction. `verticality` is read at the feature's own centre, and
    // the coin is per feature, so a flat biome still draws the occasional
    // chimney and a tall one still draws the occasional gallery — which is what
    // stops a 512-cell window reading as one thing (`docs/20` §6.2).
    const terr = this.biomeAt(cx, cy).terrain;
    const vert = terr.verticality;
    const scale = megaScaleFor(terr.roomSizeBias);
    const upright = ((hSize >>> 16) % 4096) / 4096 < vert;
    const half = Math.round(
      (MEGA_HALF_MIN + ((hShape >>> 16) % (MEGA_HALF_MAX - MEGA_HALF_MIN + 1))) *
        (upright ? MEGA_UPRIGHT : 1),
    );
    const rBase = MEGA_R_MIN + (hSize % (MEGA_R_MAX - MEGA_R_MIN + 1));
    // The **outer** extent takes the whole scale; the cavern's own bore takes
    // only `MEGA_BORE_SHARE` of it, so a bigger feature is bigger mostly in its
    // rock. The pair has to stay solid-neutral inside a 360x260 window (see
    // `MEGA_RIND_T`), and open area goes as the bore squared.
    const bore = hollow ? rBase * (1 + (scale - 1) * MEGA_BORE_SHARE) : rBase * scale;
    const outer = (rBase + (hollow ? MEGA_RIND_T : MEGA_MASS_T)) * scale;

    const ang =
      (upright ? Math.PI / 2 : 0) + (hash2f(gx, gy, s ^ 0x9e1102) - 0.5) * MEGA_ANGLE_JITTER;
    const dx = Math.cos(ang);
    const dy = Math.sin(ang);
    const phase = hash2f(gx, gy, s ^ 0x4b77c1) * Math.PI * 2;
    const waves = 1 + (hash2(gx, gy, s ^ 0x1de3f0) % 2);
    const amp = bore * MEGA_WOBBLE;

    // Three sweeps of one spine for a cavern — rock, then wall, then hollow —
    // and one for a mass. Sweeps rather than a single interleaved pass, because
    // one pass would let a later step's rind fill an earlier step's cavern
    // wherever the spine wobbles back over itself. The two families are the same
    // object with and without its interior taken out.
    //
    // One disc per cell of arc, so consecutive discs overlap by all but one cell
    // and the tube has no beads. `half` is at most 170, so this is at most 341
    // iterations per sweep, the overwhelming majority of which reject on the
    // disc's own row clip.
    const steps = half << 1;
    for (let pass = 0; pass < (hollow ? 3 : 1); pass++) {
      const rp = pass === 0 ? outer : pass === 1 ? bore + MEGA_SKIN : bore;
      const v = pass === 0 ? K_SOLID : pass === 1 ? K_SHELL : K_OPEN;
      for (let i = 0; i <= steps; i++) {
        const t = -1 + (2 * i) / steps;
        const w = Math.sin(t * Math.PI * waves + phase) * amp;
        const r = rp * Math.pow(Math.max(0, 1 - t * t), MEGA_TAPER);
        if (r < 2) continue;
        this.megaDisc(cx + dx * half * t - dy * w, cy + dy * half * t + dx * w, r, v);
      }
    }
  }

  /**
   * One disc of a mega feature, clipped to the chunk.
   *
   * A void writes `P_MEGA` as well as `K_OPEN`, and that protection is the whole
   * of `docs/20` §9's named hazard: **"a 400-cell cavern acquires eight shelf
   * decks unless the mega-feature masks the shelf and corridor passes."** Ours
   * would have acquired more than eight — `ledges()` fills *every* unprotected
   * open cell that lands on the deck lattice, so before this mask existed no
   * space in the world was taller than `SHELF_MIN_HEADROOM`, which is 25 cells
   * against a `bone_titan` of 25x37 (issue #68).
   *
   * A rind writes `P_MASS`, and never over a cavern. Two features that overlap
   * therefore resolve cavern-first whichever chunk is asking, which is what
   * keeps the pass chunk-pure without needing the lattice to be walked in any
   * particular order for correctness.
   */
  private megaDisc(px: number, py: number, r: number, v: number): void {
    const open = v === K_OPEN;
    const bx = this.baseX;
    const by = this.baseY;
    const y0 = Math.max(by, Math.ceil(py - r));
    const y1 = Math.min(by + CHUNK_SIZE - 1, Math.floor(py + r));
    if (y1 < y0) return;
    const kind = this.kind;
    const prot = this.protect;
    const r2 = r * r;
    for (let y = y0; y <= y1; y++) {
      const d = y - py;
      const span = Math.sqrt(Math.max(0, r2 - d * d));
      const x0 = Math.max(bx, Math.ceil(px - span));
      const x1 = Math.min(bx + CHUNK_SIZE - 1, Math.floor(px + span));
      if (x1 < x0) continue;
      const row = ((y - by) << CHUNK_SHIFT) - bx;
      for (let x = x0; x <= x1; x++) {
        const i = row + x;
        if (open) {
          kind[i] = K_OPEN;
          prot[i] = P_MEGA;
        } else if (prot[i] !== P_MEGA) {
          kind[i] = v;
          prot[i] = P_MASS;
        }
      }
    }
  }

  // ---- 4. herringbone tiles

  private placeTiles(cx: number, cy: number): void {
    this.builtCount = 0;
    const u0x = cx << 1;
    const u0y = cy << 1;
    for (let dy = 0; dy < 2; dy++) {
      for (let dx = 0; dx < 2; dx++) {
        decodeTile(u0x + dx, u0y + dy, this.tileRef);
        const { ux, uy, orient } = this.tileRef;
        if (this.alreadyBuilt(ux, uy, orient)) continue;
        this.markBuilt(ux, uy, orient);
        this.buildOneTile(ux, uy, orient);
      }
    }
  }

  private alreadyBuilt(ux: number, uy: number, orient: number): boolean {
    for (let i = 0; i < this.builtCount; i++) {
      const j = i * 3;
      if (this.builtTiles[j] === ux && this.builtTiles[j + 1] === uy && this.builtTiles[j + 2] === orient) {
        return true;
      }
    }
    return false;
  }

  private markBuilt(ux: number, uy: number, orient: number): void {
    const j = this.builtCount * 3;
    if (j + 2 >= this.builtTiles.length) return;
    this.builtTiles[j] = ux;
    this.builtTiles[j + 1] = uy;
    this.builtTiles[j + 2] = orient;
    this.builtCount++;
  }

  /**
   * The edge field, with the biome's enclosure applied — issue #57.
   *
   * **The enclosure is read at the edge's own midpoint, never at the asking
   * unit's centre**, and that is the whole of what makes this safe. An edge is
   * evaluated from both units it separates, from both chunks it straddles, and
   * from the tile pass and the router in turn; all six askers must be given the
   * same answer or a wall exists from one side and not the other. A pure
   * function of `(rx, ry)` is the only thing that guarantees it, and this is
   * one — `biomeAt` is itself a pure function of world position, so no buffer,
   * no ordering and no chunk state is involved.
   *
   * The cost is one `biomeAt` per edge evaluation, about 56 a chunk against the
   * 16 384-cell materialise loop. It is the same pattern `buildOneTile` already
   * uses for `roomSizeBias`, one line below.
   */
  private edgeHAt(rx: number, ry: number): EdgeTypeId {
    const b = this.biomeAt((rx + 1) << RU_SHIFT, (ry << RU_SHIFT) + (RU >> 1));
    return edgeH(
      rx, ry, this.seed,
      enclosureFor(b.terrain.caveDensity),
      spineKeepYFor(b.terrain.verticality),
    );
  }

  private edgeVAt(rx: number, ry: number): EdgeTypeId {
    const b = this.biomeAt((rx << RU_SHIFT) + (RU >> 1), (ry + 1) << RU_SHIFT);
    return edgeV(
      rx, ry, this.seed,
      enclosureFor(b.terrain.caveDensity),
      spineKeepXFor(b.terrain.verticality),
    );
  }

  /**
   * **Is this horizontal edge in the forced sublattice?** Read at the edge's
   * own midpoint, for the same reason and by the same convention as
   * `edgeHAt` one line above — the router asks from both units the edge
   * separates and from both chunks it straddles, and a `thru` that differed
   * between them would tunnel a mega mass from one side only.
   *
   * This replaces a copy of the rule that used to live here as
   * `(ry & 1) === 0`, duplicating `rooms.ts`. The copy was safe only while both
   * halves were a function of the row alone; nothing asserted that they agreed,
   * and a duplicated invariant that nothing checks is the shape
   * `tests/worldVoids.test.ts` guards `PROTECT_MEGA` against. There is no
   * assertion here because there is no longer anything to assert: one
   * implementation, called from both places.
   */
  private rowForcedAt(rx: number, ry: number): boolean {
    const b = this.biomeAt((rx + 1) << RU_SHIFT, (ry << RU_SHIFT) + (RU >> 1));
    return rowForced(rx, ry, this.seed, spineKeepYFor(b.terrain.verticality));
  }

  /** The same for the vertical edge between (rx,ry) and (rx,ry+1). */
  private colForcedAt(rx: number, ry: number): boolean {
    const b = this.biomeAt((rx << RU_SHIFT) + (RU >> 1), (ry + 1) << RU_SHIFT);
    return colForced(rx, ry, this.seed, spineKeepXFor(b.terrain.verticality));
  }

  private buildOneTile(ux: number, uy: number, orient: number): void {
    const ctx = this.tileCtx;
    const s = this.seed;
    const e = ctx.edges;

    if (orient === TileOrient.H) {
      ctx.x = ux << RU_SHIFT;
      ctx.y = uy << RU_SHIFT;
      ctx.w = RU * 2;
      ctx.h = RU;
      e[0] = this.edgeHAt(ux - 1, uy);
      e[1] = this.edgeHAt(ux + 1, uy);
      e[2] = this.edgeVAt(ux, uy - 1);
      e[3] = this.edgeVAt(ux + 1, uy - 1);
      e[4] = this.edgeVAt(ux, uy);
      e[5] = this.edgeVAt(ux + 1, uy);
    } else {
      ctx.x = ux << RU_SHIFT;
      ctx.y = uy << RU_SHIFT;
      ctx.w = RU;
      ctx.h = RU * 2;
      e[0] = this.edgeVAt(ux, uy - 1);
      e[1] = this.edgeVAt(ux, uy + 1);
      e[2] = this.edgeHAt(ux - 1, uy);
      e[3] = this.edgeHAt(ux - 1, uy + 1);
      e[4] = this.edgeHAt(ux, uy);
      e[5] = this.edgeHAt(ux, uy + 1);
    }

    let openCount = 0;
    for (let i = 0; i < 6; i++) if (edgePassable(e[i])) openCount++;

    ctx.baseX = this.baseX;
    ctx.baseY = this.baseY;
    ctx.orient = orient === TileOrient.H ? TileOrient.H : TileOrient.V;
    ctx.openCount = openCount;
    // `rooms.ts` owns this hash now, because `authoredTileAt` and
    // `pixel tile show` both have to ask which room a slot is without being
    // here. One implementation, called from both places.
    ctx.seed = tileSeedFor(ux, uy, ctx.orient, this.seed);
    ctx.biome = this.biomeAt(ctx.x + (ctx.w >> 1), ctx.y + (ctx.h >> 1));
    // The tile's own stream — a tile straddling two chunks must not depend on
    // which chunk is asking.
    this.tileRng.reseed(ctx.seed);
    ctx.rng = this.tileRng;
    const forced = this.archetypeOverride?.get(pinnedSlotKey(ux, uy, ctx.orient));
    chooseVariant(ctx.biome, ctx.orient, openCount, this.tileRng, ctx.variant, forced);
    buildTile(ctx);
  }

  // ---- 5. corridor router

  private routeCorridors(cx: number, cy: number): void {
    const u0x = cx << 1;
    const u0y = cy << 1;
    const s = this.seed;
    this.landCount = 0;

    // Two passes, shafts before galleries. A gallery lays a slab as its floor,
    // and a slab dropped across a shaft that has not been carved yet would seal
    // the descent — so every vertical passage is claimed first (protect = 2)
    // and the galleries write around it. One pass would make the world's
    // connectivity depend on the order the unit loop happens to visit edges in.
    for (let pass = 0; pass < 2; pass++) {
      for (let dy = 0; dy < 2; dy++) {
        for (let dx = 0; dx < 2; dx++) {
          const rx = u0x + dx;
          const ry = u0y + dy;
          const nx = nodeX(rx, ry, s);
          const ny = nodeY(rx, ry, s);
          const onPath = isPathUnit(rx, ry);
          // **Which corridors may tunnel through a mega mass**, and it is not
          // `onPath`. `rooms.ts` forces every horizontal edge on an even unit
          // row and every vertical edge on an even unit column to be passable,
          // and *that* sublattice — not the even/even path units — is what
          // `structure.ts` proves connected and 2-connected. The corridor
          // joining two path units at rx and rx+2 runs through the unit at
          // rx+1, whose own `isPathUnit` is false; blocking that half against a
          // mass severed it, and `tests/setpiece.test.ts` caught it as two
          // anchor pairs losing their redundancy guarantee on seed 1.
          //
          // `docs/19` §8 warned about exactly this shape of failure: growing
          // the room unit makes the connectivity guarantee worse, Barrett's own
          // is *authored* and "verified graphically" rather than algorithmic,
          // and Noita shipped a hotfix for an impassable Mines.
          //
          // **Per edge, not per unit.** The rule used to be `(ry & 1) === 0`
          // here and again in `rooms.ts`, which was safe only because both
          // copies were a function of the row alone. Now that a biome may thin
          // its own redundant lines, forcedness belongs to the *edge*, and each
          // of the four is asked about the edge it carves.
          const rowForcedE = this.rowForcedAt(rx, ry);
          const rowForcedW = this.rowForcedAt(rx - 1, ry);
          const colForcedS = this.colForcedAt(rx, ry);
          const colForcedN = this.colForcedAt(rx, ry - 1);

          const eE = this.edgeHAt(rx, ry);
          const eW = this.edgeHAt(rx - 1, ry);
          const eS = this.edgeVAt(rx, ry);
          const eN = this.edgeVAt(rx, ry - 1);

          let live = 0;
          if (edgePassable(eE)) live++;
          if (edgePassable(eW)) live++;
          if (edgePassable(eS)) live++;
          if (edgePassable(eN)) live++;

          if (pass === 0) {
            // The vertical leg that drops the node to each horizontal
            // crossing, and the shafts to the units above and below.
            if (edgePassable(eE)) {
              this.carveV(nx, ny, crossHY(rx, ry, s), vRadius(eE), onPath, rowForcedE);
            }
            if (edgePassable(eW)) {
              this.carveV(nx, ny, crossHY(rx - 1, ry, s), vRadius(eW), onPath, rowForcedW);
            }
            if (edgePassable(eS)) {
              this.carveV(crossVX(rx, ry, s), ny, (ry + 1) << RU_SHIFT, vRadius(eS), onPath && colForcedS, colForcedS);
            }
            if (edgePassable(eN)) {
              this.carveV(crossVX(rx, ry - 1, s), ry << RU_SHIFT, ny, vRadius(eN), onPath && colForcedN, colForcedN);
            }
            // Repair pass. The forced-edge sublattice in rooms.ts guarantees
            // every path unit has four live edges, so this should never fire —
            // it exists so the invariant is enforced by code rather than only
            // by argument.
            if (onPath && live < 2) {
              this.repairs++;
              this.carveV(nx, ny, crossHY(rx, ry, s), CORR_V_DOOR, true, true);
              this.carveV(nx, ny, crossHY(rx - 1, ry, s), CORR_V_DOOR, true, true);
            }
            continue;
          }

          // Junction chamber. Without it, four corridors meeting at a point
          // look like a bug rather than a room.
          this.carveGallery(ny, nx - JUNCTION_R, nx + JUNCTION_R, onPath, onPath);

          if (edgePassable(eE)) {
            this.carveGallery(crossHY(rx, ry, s), nx, (rx + 1) << RU_SHIFT, onPath && rowForcedE, rowForcedE);
          }
          if (edgePassable(eW)) {
            this.carveGallery(crossHY(rx - 1, ry, s), rx << RU_SHIFT, nx, onPath && rowForcedW, rowForcedW);
          }
          if (edgePassable(eS)) this.carveGallery(ny, nx, crossVX(rx, ry, s), onPath, colForcedS);
          if (edgePassable(eN)) this.carveGallery(ny, nx, crossVX(rx, ry - 1, s), onPath, colForcedN);
          if (onPath && live < 2) {
            this.carveGallery(crossHY(rx, ry, s), nx, (rx + 1) << RU_SHIFT, true, true);
            this.carveGallery(crossHY(rx - 1, ry, s), rx << RU_SHIFT, nx, true, true);
          }
        }
      }
    }
    this.layLandings();
  }

  private pushLanding(ly: number, x0: number, x1: number): void {
    const j = this.landCount * 3;
    if (x1 < x0 || j + 2 >= this.landReq.length) return;
    this.landReq[j] = ly;
    this.landReq[j + 1] = x0;
    this.landReq[j + 2] = x1;
    this.landCount++;
  }

  /** One-way landings down every shaft. They cannot seal anything. */
  private layLandings(): void {
    const bx = this.baseX;
    const by = this.baseY;
    const kind = this.kind;
    for (let k = 0; k < this.landCount; k++) {
      const j = k * 3;
      const ly = this.landReq[j];
      const x0 = this.landReq[j + 1];
      const x1 = this.landReq[j + 2];
      for (let d = 0; d < 3; d++) {
        const y = ly + d;
        if (y < by || y >= by + CHUNK_SIZE) continue;
        const row = ((y - by) << CHUNK_SHIFT) - bx;
        // Not into a mass the shaft was refused: a landing hanging in solid
        // rock is a one-way floor nobody can reach from either side.
        for (let x = x0; x <= x1; x++) {
          if (this.protect[row + x] !== P_MASS) kind[row + x] = K_PLATFORM;
        }
      }
    }
  }

  /**
   * Carve a horizontal gallery: one shelf period tall, with that period's slab
   * as its floor, following the terrace lattice column by column.
   *
   * This is what replaced the constant-y tube. Because the floor is the ledge
   * lattice rather than whatever the noise left under the tube, a gallery is
   * walkable end to end by construction, it steps up and down with the shelves
   * around it instead of cutting across them, and a ledge laid later can never
   * land inside it and halve its headroom.
   */
  private carveGallery(
    wy: number,
    wxa: number,
    wxb: number,
    path: boolean,
    thru: boolean,
  ): void {
    const bx = this.baseX;
    const by = this.baseY;
    if (wy < by - SHELF_PITCH_MAX - CHUNK_SIZE || wy > by + CHUNK_SIZE + SHELF_PITCH_MAX) return;
    let x0 = wxa < wxb ? wxa : wxb;
    let x1 = wxa < wxb ? wxb : wxa;
    if (x0 < bx) x0 = bx;
    if (x1 > bx + CHUNK_SIZE - 1) x1 = bx + CHUNK_SIZE - 1;
    if (x1 < x0) return;

    const kind = this.kind;
    const prot = this.protect;
    const yLo = by;
    const yHi = by + CHUNK_SIZE - 1;
    // The band's own deck geometry: a gallery is one period tall less its slab.
    const slabT = this.shelfSlabAt(wy);
    const galleryH = this.shelfPitchAt(wy) - slabT;

    // The terrace offset is constant over 32 columns, so the shelf floor is
    // resolved once per bucket rather than once per column.
    let bucket = -0x7fffffff;
    let floorY = 0;
    const pitch = this.shelfPitchAt(wy);
    for (let x = x0; x <= x1; x++) {
      const b = x >> 5;
      if (b !== bucket) {
        bucket = b;
        floorY = this.shelfFloorAt(x, wy);
      }
      // The gallery's ceiling *is* the underside of the deck one period up, so
      // it is read off the field at that deck's top row — the same key
      // `ledges()` uses. Keyed any other way the two would disagree wherever a
      // gallery runs into a shelf, and the junction would acquire a step of up
      // to `ROUGH_A` that neither pass intended.
      const lift = roughStep(x, floorY - pitch, this.seed, ROUGH_A);
      // And the underside of the gallery's own floor slab, one period down.
      const drop = roughStep(x, floorY, this.seed, ROUGH_A);
      for (let y = floorY - galleryH - lift; y < floorY; y++) {
        if (y < yLo || y > yHi) continue;
        const i = ((y - by) << CHUNK_SHIFT) + (x - bx);
        // A mega mass is rock the router goes *around* — unless this is the
        // critical path, in which case it goes through. See the note on
        // `P_MASS`: this one condition is the whole of the grain result.
        if (prot[i] === P_MASS && !thru) continue;
        kind[i] = K_OPEN;
        // A mega void's protection has to survive a gallery running through it,
        // or the ledge pass gets the cavern back. A mass that has just been
        // carved open is no longer a mass, so it drops to the soft level.
        if (prot[i] !== P_SHAFT && prot[i] !== P_MEGA) prot[i] = 1;
      }
      for (let y = floorY; y < floorY + slabT - drop; y++) {
        if (y < yLo || y > yHi) continue;
        const i = ((y - by) << CHUNK_SHIFT) + (x - bx);
        if (prot[i] === P_MASS && !thru) continue;
        // Where the floor crosses a shaft it becomes a grating rather than a
        // hole. A hole chopped every gallery in two at each routing node —
        // the shafts are 17 wide and the galleries only run 64 — while a
        // one-way floor is walked across, dropped through and climbed back up.
        //
        // A mega void takes the same treatment, and it is the difference
        // between `docs/20` §9's hazard and its fix: a gallery crossing a
        // 400-cell cavern would otherwise lay a full slab across it. What it
        // leaves instead is a one-way ledge at the height the gallery arrives
        // at, which is a landing inside the cavern rather than a floor over it.
        if (prot[i] === P_SHAFT || prot[i] === P_MEGA) {
          if (y === floorY) kind[i] = K_PLATFORM;
          continue;
        }
        kind[i] = K_SOLID;
        prot[i] = 1;
      }
    }

    if (path) {
      const lby = (floorY - 2 - by) >> BLOCK_SHIFT;
      if (lby >= 0 && lby < BLOCKS_PER_CHUNK) {
        const base = lby << 4;
        for (let x = x0; x <= x1; x++) this.pathBlocks[base | ((x - bx) >> BLOCK_SHIFT)] = 1;
      }
    }
  }

  private carveV(
    wx: number,
    wya: number,
    wyb: number,
    r: number,
    path: boolean,
    thru: boolean,
  ): void {
    const bx = this.baseX;
    const by = this.baseY;
    let y0 = wya < wyb ? wya : wyb;
    let y1 = wya < wyb ? wyb : wya;
    if (y0 < by) y0 = by;
    if (y1 > by + CHUNK_SIZE - 1) y1 = by + CHUNK_SIZE - 1;
    if (y1 < y0) return;
    // ---------------------------------------------------------------------
    // **A shaft wall is the one surface roughness cannot reach, and it is the
    // one that would matter most. Measured, not assumed.**
    //
    // A shaft is 23 cells wide and hundreds tall, so its two walls are the
    // longest perfectly straight edges in the world, and they are what the hard
    // 90-degree corner count is made of: displacing them by +/-2 cells took
    // `tests/boundaryShape.test.ts`'s corner rate from **137 to 38 per screen**,
    // by far the largest single movement anything in this change produced.
    //
    // Both directions cost a separately-guarded property, and both were run:
    //
    //   inward  — narrowing 23 to 19 took `tests/worldshape.test.ts`'s median
    //             walkable run to 19 against its 20 (a rung spans the shaft,
    //             and rungs are most of what there is to stand on down there),
    //             and took `assembler_prime` 25x33 and `verdigris_castellan`
    //             17x31 from three placements to **zero** in
    //             `tests/spawnClearance.test.ts`.
    //   outward — widening 23 to 27 costs open volume, and at +/-2 it takes
    //             `tests/materialGrain.test.ts`'s `unitCount` and rock-only
    //             `facePurity` past their bars in two biomes. That ceiling is
    //             480 units a screen against a *measured* 460 in `meatworks`
    //             before any of this, i.e. under half a standard deviation of
    //             slack, and `docs/21` §3.1 asks for 150.
    //
    // So the lever exists, it is large, and it is spent. See the note at the
    // foot of `tests/boundaryShape.test.ts`.
    const lo = bx;
    const hi = bx + CHUNK_SIZE - 1;
    let x0 = wx - r;
    let x1 = wx + r;
    if (x0 < lo) x0 = lo;
    if (x1 > hi) x1 = hi;
    if (x1 < x0) return;
    const kind = this.kind;
    const prot = this.protect;
    for (let y = y0; y <= y1; y++) {
      const row = ((y - by) << CHUNK_SHIFT) - bx;
      for (let x = x0; x <= x1; x++) {
        // Same rule as `carveGallery`: a shaft tunnels through a mega mass only
        // where it is the routed spine. The forced-edge sublattice in
        // `rooms.ts` is exactly the set of units for which `path` is true, so
        // every guaranteed route survives and only the redundant ones detour.
        if (prot[row + x] === P_MASS && !thru) continue;
        kind[row + x] = K_OPEN;
        // 2, not 1: a shaft is the one thing a gallery's floor may not cross.
        prot[row + x] = 2;
      }
    }
    if (path) {
      const lbx = (wx - bx) >> BLOCK_SHIFT;
      if (lbx >= 0 && lbx < BLOCKS_PER_CHUNK) {
        for (let y = y0; y <= y1; y++) this.pathBlocks[(((y - by) >> BLOCK_SHIFT) << 4) | lbx] = 1;
      }
    }
    // Landings are keyed on absolute world y, not on the segment, so the two
    // chunks a shaft passes through place the same ones in the same places.
    //
    // Full width, and one-way. A half-width shelf leaves a gap the 10-cell body
    // has to be aimed at; a one-way floor across the whole shaft is a rung —
    // press down to descend, jump to climb, and liquids pour through it either
    // way because `M.solid` is 0 for both platform materials.
    // Absolute rows, so the two chunks a shaft crosses place the same landings:
    // the step is a function of the band and the phase a function of the
    // shaft's x, and neither knows anything about the segment it is asked for.
    const phase = hash2(wx, 0, this.seed ^ 0x1a4d15) % SHAFT_STEP;
    for (let wy = y0; wy <= y1 - 3; wy++) {
      if ((((wy - phase) % SHAFT_STEP) + SHAFT_STEP) % SHAFT_STEP === 0) {
        this.pushLanding(wy, x0, x1);
      }
    }
  }

  // ---- 5b. ledges

  /**
   * Lay the floors — issue #1 phase 1.
   *
   * Runs last, after the room tiles and the corridor router, and skips every
   * protected cell. By then the galleries have already put their own floors on
   * this same lattice, so what is left for this pass is the noise voids and the
   * room interiors — and because both layers share the lattice, a shelf laid
   * here lines up with the gallery it runs into.
   *
   * A shelf is a floor rather than a ceiling because the slab sits at the
   * *bottom* of its period: the 25 cells above it are the gallery.
   */
  private ledges(): void {
    const kind = this.kind;
    const prot = this.protect;
    const off = this.shelfOff;
    const nHole = this.nHole;
    const baseY = this.baseY;

    for (let ly = 0; ly < CHUNK_SIZE; ly++) {
      const row = ly << CHUNK_SHIFT;
      const gRow = (ly >> COARSE_SHIFT) * CG;
      const oRow = (ly >> 6) * SHELF_COLS;
      const wy = baseY + ly;
      // Per row, not per chunk: pitch and slab are the band's now, and a chunk
      // 128 cells tall can straddle one band line of the 900-cell stack.
      const k = this.archBand(wy);
      const pitch = this.bandPitch[k];
      const slabT = this.bandSlab[k];
      const q0 = wy - this.bandAnchor[k];

      for (let lx = 0; lx < CHUNK_SIZE; lx++) {
        const i = row + lx;
        if (kind[i] !== K_OPEN) continue;
        if (prot[i] === P_MEGA) {
          // The one thing that *is* laid inside a mega void, and it is a
          // movement requirement rather than a shape one. A 400-cell cavern
          // with no floor in it is open volume the player falls through and
          // cannot climb: `tests/worldshape.test.ts` holds standable floor at
          // 5 % of open volume and the median walkable run at 20 cells, and the
          // unfurnished cavern failed both.
          //
          // A *landing*, not a deck: three cells thick against the deck's
          // 17-29, one-way so it is dropped through and jumped back up, and
          // present in only two 32-cell buckets in five, staggered by row. So
          // three columns in five keep the cavern's full vertical span — which
          // is what `grainY` measures and what a 41-cell body needs — while
          // every point in it is within one deck pitch of somewhere to stand.
          // The pitch is the band's, so the landing is under the 54-cell jump
          // (`docs/02` §3.2) by construction.
          const ph = (((q0 + off[oRow + (lx >> 5)]) % pitch) + pitch) % pitch;
          if (ph >= MEGA_LANDING_T) continue;
          // Keyed on the *deck row*, not on `wy`, so all three cells of one
          // landing agree; and on a 64-cell bucket, so a landing is a ledge
          // rather than a dotted line.
          if (hash2((this.baseX + lx) >> 6, wy - ph, this.seed ^ 0x1a9d05) % 5 < 3) {
            kind[i] = K_PLATFORM;
          }
          continue;
        }
        if (prot[i] !== 0) continue;
        // Nearest tap for the hole field — a gap's edge does not need
        // sub-4-cell placement, and this is 16384 iterations.
        if (nHole[gRow + (lx >> COARSE_SHIFT)] > SHELF_HOLE_T) continue;
        const q = q0 + off[oRow + (lx >> 5)];
        const ph = ((q % pitch) + pitch) % pitch;
        if (ph >= slabT) continue;
        // **The deck's underside is eroded and its tread is not**, which is the
        // whole shape of what roughness can do here. `ph === 0` is the surface
        // the player stands on and `ph === slabT - 1` is the ceiling of the
        // gallery below; only the second is free. The field is read at the
        // deck's own top row rather than at this cell, so all `ROUGH_A` cells of
        // one column lift together — read per cell, the field's y-variation
        // would leave a floating cell wherever it crossed the threshold twice
        // inside one slab.
        if (ph >= slabT - ROUGH_A
          && ph >= slabT - roughStep(this.baseX + lx, wy - ph, this.seed, ROUGH_A)) {
          continue;
        }
        kind[i] = K_SOLID;
      }
    }
  }

  // ---- 5c. the surface

  /**
   * Sky above the ground line, ground below it, and a basin where the pond is.
   *
   * There was no surface at all: terrain was homogeneous in every direction
   * including up, so the first screen of a run told the player nothing about
   * where they were or which way the game went (issue #1 §3 cause 2).
   */
  private surfaceCarve(baseX: number, baseY: number): void {
    const kind = this.kind;
    this.protect.fill(0);
    for (let lx = 0; lx < CHUNK_SIZE; lx++) {
      const wx = baseX + lx;
      const gh = this.surfaceHeight(wx);
      const bed = gh + this.pondDepth(wx);
      for (let ly = 0; ly < CHUNK_SIZE; ly++) {
        const wy = baseY + ly;
        // A three-cell crust of SHELL over the bulk. The bulk resolves through
        // the ore field, and a biome's accent list is allowed to contain gases
        // and liquids (Rotwood's is FUNGAL_MAT / MUD / METHANE), so without the
        // crust the ground you spawn on is a coin-flip on the ore noise.
        kind[(ly << CHUNK_SHIFT) | lx] =
          wy < bed ? K_OPEN : wy < bed + 3 ? K_SHELL : K_SOLID;
      }
      // The ground line is a route the generator guarantees, so it outranks
      // the block heuristics in `annotate`.
      const lby = (gh - 4 - baseY) >> BLOCK_SHIFT;
      if (lby >= 0 && lby < BLOCKS_PER_CHUNK) this.pathBlocks[(lby << 4) | (lx >> BLOCK_SHIFT)] = 1;
    }
  }

  /**
   * The grove — trees west of spawn, thickening toward the cliff.
   *
   * Deliberately only to the west. Noita's opening reads in ten seconds
   * without a word of text because the two directions are not symmetrical:
   * west is a pond and a stand of trees and a wall, east is a hole in the
   * ground. docs/08 §2 makes the same argument about the stripped prologue.
   *
   * ==== THE GROVE IS ONE-WAY, AND IT USED TO BE SOLID — issue #140 =========
   *
   * #140's own table calls the forest "parallax, not traversable —
   * scene-setting", and it was neither: twelve `K_SOLID` trunks 5-7 cells wide
   * and **34-67 cells tall**, planted directly on the walking line, spaced 44
   * apart from x = -210 westward. A trunk taller than the 56-cell apex is not
   * something you climb over, so **the grove was a palisade**: measured, no
   * body could walk further west than x = -196 in any world this game has ever
   * generated, and #140 asks for a landmark seven hundred cells past it.
   *
   * `K_PLATFORM` is the whole fix, and it is the honest translation of "in the
   * background" into a world with no depth: a one-way cell blocks from above
   * and from nowhere else, so you walk straight through a trunk, you can stand
   * on a canopy, and the forest still draws. It also gets the grove its own
   * colour for free — `materialise` paints a platform out of `palette.platform`,
   * which in the Rotwood Reach is `SHELF_FUNGUS`, the same sage green the Great
   * Tree's own foliage is made of.
   *
   * `tests/greatTree.test.ts` `has a dry site you can walk to from spawn with
   * no ability` is what holds this; it fails on the first grove trunk the
   * moment this goes back to `K_SOLID`.
   */
  private grove(baseX: number): void {
    for (let k = 0; k < 12; k++) {
      const h = hash2(k, 0, this.seed ^ 0x7e33a1);
      const tx = -210 - k * 44 + (h % 21) - 10;
      if (tx < baseX - 48 || tx >= baseX + CHUNK_SIZE + 48) continue;
      const g = this.surfaceHeight(tx);
      const th = 34 + ((h >>> 8) % 34);
      const hw = 2 + ((h >>> 16) % 2);
      this.worldRect(tx - hw, g - th, hw * 2 + 1, th, K_PLATFORM);
      this.worldEllipse(tx, g - th - 6, 13 + ((h >>> 20) % 10), 8 + ((h >>> 24) % 7), K_PLATFORM);
    }
  }

  /**
   * The descent — issue #1 phase 3B.
   *
   * A lined shaft 37 cells wide with alternating landings every 22. The rim
   * stands 16 cells proud of the ground so the mouth is visible from spawn
   * rather than being a hole you find by falling into it.
   *
   * **You can drop the whole way down and climb the whole way back with no
   * traversal ability at all — but not for the reason this comment used to
   * give.** It credited the landings: 22 is well inside the jump height, so the
   * ascent read as a ladder. Issue #106 drove a body up it and watched what it
   * actually did, and the climb is mostly a **wall-jump chain between the two
   * linings**. Wall jump is base kit, so the promise is kept; what keeps it is
   * that the linings are 37 cells apart. A sheer wall taller than 54 cells is
   * unclimbable — measured, 1 cell of gain at h = 60 — so narrowing the shaft,
   * or moving the linings past wall-jump reach, breaks the ascent **silently**,
   * with every landing still exactly where it was.
   *
   * So the number to be careful with here is the width, and the guarantee does
   * not live in this file. It is `tests/traversal.test.ts`, `and climbs back
   * out of it with no traversal ability` — a real collider, zero abilities, the
   * deepest landing to the surface on all sixteen seed/mode pairs. The width
   * itself is pinned in `tests/worldshape.test.ts`'s descent guarantee, which
   * also now checks every rung against the jump envelope a body standing on it
   * actually has rather than against a bare 54 (#105).
   *
   * Protected end to end, so neither the ledge field nor a liquid pool can
   * close the one route out of the surface.
   */
  private descentShaft(): void {
    const bx = this.baseX;
    const by = this.baseY;
    const x0 = DESCENT_X0 - 5;
    const x1 = DESCENT_X1 + 5;
    if (bx > x1 || bx + CHUNK_SIZE <= x0) return;
    const top = SURFACE_Y - 16;
    if (by > DESCENT_BOTTOM || by + CHUNK_SIZE <= top) return;

    const kind = this.kind;
    const prot = this.protect;
    const ly0 = Math.max(0, top - by);
    const ly1 = Math.min(CHUNK_SIZE - 1, DESCENT_BOTTOM - by);
    const lx0 = Math.max(0, x0 - bx);
    const lx1 = Math.min(CHUNK_SIZE - 1, x1 - bx);

    for (let ly = ly0; ly <= ly1; ly++) {
      const wy = by + ly;
      const row = ly << CHUNK_SHIFT;
      for (let lx = lx0; lx <= lx1; lx++) {
        const wx = bx + lx;
        const inside = wx >= DESCENT_X0 && wx <= DESCENT_X1;
        // The rim only exists above the ground; below it the lining is the
        // shaft wall. Above ground outside the rim stays sky.
        // Cheap test first: only the fourteen rows of rim above the nominal
        // ground line can need the heightfield at all.
        if (!inside && wy < SURFACE_Y - 2 && wy < this.surfaceHeight(wx)) {
          if (wx < DESCENT_X0 - 3 || wx > DESCENT_X1 + 3) continue;
        }
        kind[row + lx] = inside ? K_OPEN : K_SHELL;
        prot[row + lx] = inside ? 2 : 1;
      }
      const lby = ly >> BLOCK_SHIFT;
      for (let lx = lx0; lx <= lx1; lx++) this.pathBlocks[(lby << 4) | (lx >> BLOCK_SHIFT)] = 1;
    }

    // Landings. One-way, so descending is a drop and ascending is a jump
    // through the same cells rather than a wall from below.
    const lw = 24;
    let side = 0;
    for (let wy = SURFACE_Y + DESCENT_STEP; wy <= DESCENT_BOTTOM - 4; wy += DESCENT_STEP) {
      const ly = wy - by;
      side ^= 1;
      if (ly < 0 || ly + 2 >= CHUNK_SIZE) continue;
      const px0 = side ? DESCENT_X0 : DESCENT_X1 - lw + 1;
      for (let d = 0; d < 3; d++) {
        const row = (ly + d) << CHUNK_SHIFT;
        for (let wx = px0; wx < px0 + lw; wx++) {
          const lx = wx - bx;
          if (lx < 0 || lx >= CHUNK_SIZE) continue;
          kind[row + lx] = K_PLATFORM;
        }
      }
    }

    // Hand-over: a gallery at the bottom so the shaft empties into the band
    // rather than into whatever the noise happened to put there.
    if (by <= DESCENT_BOTTOM && by + CHUNK_SIZE > DESCENT_BOTTOM) {
      this.carveGallery(DESCENT_BOTTOM - 8, DESCENT_X0 - 90, DESCENT_X1 + 90, true, true);
    }
  }

  // ------------------------------------------------ sanctums and rest nodes
  //
  // Both are stamped AFTER the corridor router and the ledge pass and BEFORE
  // the sealed vaults, which is the only ordering that works: the router and
  // the ledges must not be able to lay a slab through a chamber, and the vault
  // placer already refuses to stamp over protected cells, so protecting the
  // chambers here is what keeps a vault from being carved into a sanctum wall.

  /** Clipped world-space rect, writing kind and protection together. */
  private stampRect(x: number, y: number, w: number, h: number, v: number, p: number): void {
    const bx = this.baseX;
    const by = this.baseY;
    const x0 = Math.max(0, x - bx);
    const x1 = Math.min(CHUNK_SIZE - 1, x + w - 1 - bx);
    const y0 = Math.max(0, y - by);
    const y1 = Math.min(CHUNK_SIZE - 1, y + h - 1 - by);
    if (x1 < x0 || y1 < y0) return;
    const kind = this.kind;
    const prot = this.protect;
    for (let ly = y0; ly <= y1; ly++) {
      const row = ly << CHUNK_SHIFT;
      for (let lx = x0; lx <= x1; lx++) {
        kind[row + lx] = v;
        prot[row + lx] = p;
      }
    }
  }

  /**
   * Mark the critical path over a world rect, at *block* granularity, counting
   * only blocks the rect fully contains.
   *
   * Fully contained and not merely touched, because a block that is six cells
   * of adamant and two cells of chamber is a wall the player cannot walk
   * through, and telling the connectivity verifier otherwise would make it
   * certify a route through the one thing in the game that has no route.
   */
  private markPathRect(x: number, y: number, w: number, h: number): void {
    const bx = this.baseX;
    const by = this.baseY;
    const b0x = (x + BLOCK_SIZE - 1) >> BLOCK_SHIFT;
    const b1x = (x + w) >> BLOCK_SHIFT;
    const b0y = (y + BLOCK_SIZE - 1) >> BLOCK_SHIFT;
    const b1y = (y + h) >> BLOCK_SHIFT;
    const cb0x = bx >> BLOCK_SHIFT;
    const cb0y = by >> BLOCK_SHIFT;
    for (let b = b0y; b < b1y; b++) {
      const ly = b - cb0y;
      if (ly < 0 || ly >= BLOCKS_PER_CHUNK) continue;
      for (let a = b0x; a < b1x; a++) {
        const lx = a - cb0x;
        if (lx < 0 || lx >= BLOCKS_PER_CHUNK) continue;
        this.pathBlocks[(ly << 4) | lx] = 1;
      }
    }
  }

  /**
   * Rungs down a shaft, keyed on absolute world y.
   *
   * Absolute, not relative to the shaft's own top, because two chunks generate
   * two halves of the same shaft and have to place the same rungs in the same
   * places without talking. One-way, so descending is a drop and climbing is a
   * jump through the same cells rather than a wall from below.
   */
  private rungs(x0: number, w: number, yTop: number, yLast: number): void {
    for (let wy = Math.ceil(yTop / SANCTUM_STEP) * SANCTUM_STEP; wy <= yLast; wy += SANCTUM_STEP) {
      this.stampRect(x0, wy, w, 3, K_PLATFORM, 2);
    }
    // ...plus one at the very end, off the modular sequence.
    //
    // Absolute keying is what makes two chunks agree on a shaft's rungs, and
    // the price of it is that where the sequence lands relative to the chamber
    // is an accident of the boundary depth. On the worst alignment that leaves
    // a 54-cell gap between the last rung and the one inside the chamber —
    // exactly the jump height, i.e. a climb that is possible on a perfect frame
    // and not otherwise. A fixed rung at the mouth removes the accident.
    this.stampRect(x0, yLast, w, 3, K_PLATFORM, 2);
  }

  /**
   * The sanctums this chunk holds a slice of — issue #1 phase 4.
   *
   * The chamber straddles its band line, so crossing into a band and entering
   * its sanctum are one act. Shell is `Kind.GATE`, which materialises as
   * ADAMANT_SEAL: `indestructible`, and `abilities.ts` `blocksTranslocation`
   * already refuses to Blink or Phase Drift through it. docs/01 §3.4 — "if you
   * can melt it, it was never a gate."
   */
  private sanctumChambers(): void {
    const by = this.baseY;
    const list = sanctumsOverlapping(
      this.seed, this.bandOrder.length, BAND_HEIGHT, by, by + CHUNK_SIZE - 1,
    );
    for (const s of list) this.carveSanctum(s);
  }

  private carveSanctum(s: Sanctum): void {
    const w = s.x1 - s.x0 + 1;
    const gap = s.entryX1 - s.entryX0 + 1;
    const shellTop = s.ceilY - SANCTUM_SHELL + 1;
    const shellH = s.floorY + SANCTUM_SHELL - shellTop;
    const [top, bottom] = sanctumSpanY(s);

    // The solid block first, then the room hollowed out of it. Carving the
    // shell as a ring would leave the corners to arithmetic; this way the seal
    // is closed by construction and only the two apertures open it.
    this.stampRect(s.x0 - SANCTUM_SHELL, shellTop, w + SANCTUM_SHELL * 2, shellH, Kind.GATE, 2);
    this.stampRect(s.x0, s.floorY - SANCTUM_IN_H, w, SANCTUM_IN_H, K_OPEN, 2);

    // In through the ceiling, out through the floor. Both apertures continue as
    // shafts, so the chamber is reachable from above and passable to below
    // without either end depending on what the noise left next to it.
    this.stampRect(s.entryX0, top, gap, s.floorY - SANCTUM_IN_H - top, K_OPEN, 2);
    this.stampRect(s.exitX0, s.floorY, gap, bottom - s.floorY + 1, K_OPEN, 2);
    this.rungs(s.entryX0, gap, top, shellTop - 3);
    this.rungs(s.exitX0, gap, s.floorY + SANCTUM_SHELL, bottom - 3);
    // The first rung out of the chamber sits immediately under the floor, so
    // leaving is a step down and coming back is a step up.
    this.stampRect(s.exitX0, s.floorY + SANCTUM_SHELL, gap, 3, K_PLATFORM, 2);

    // One rung inside the chamber, under the way in. Without it the only way
    // back out of a 48-cell room through a ceiling 6 cells thick is a 54-cell
    // jump landing on the exact cell — technically possible and not something
    // to ask of a player who has just fallen in.
    this.stampRect(s.entryX0 - 6, s.floorY - SANCTUM_STEP, gap + 12, 3, K_PLATFORM, 2);

    // The pool is a basin cut into the slab rather than a puddle on it, so it
    // still holds after the player has walked through it, and the last row of
    // seal is left underneath so it cannot drain into the band below.
    this.stampRect(s.pool.x - s.pool.halfW, s.floorY, s.pool.halfW * 2 + 1, SANCTUM_SHELL - 1, K_OPEN, 2);

    this.markPathRect(s.x0, s.floorY - SANCTUM_IN_H, w, SANCTUM_IN_H);
    this.markPathRect(s.entryX0, top, gap, s.floorY - top);
    this.markPathRect(s.exitX0, s.floorY - 8, gap, bottom - s.floorY + 8);
  }

  /** Bands whose rest nodes could reach into a world-y range. */
  private restNodesNear(y0: number, y1: number): RestNode[] {
    const nb = this.bandOrder.length;
    const reach = REST_IN_H + REST_SHELL * 2 + 8;
    let lo = Math.floor((y0 - reach) / BAND_HEIGHT);
    let hi = Math.floor((y1 + reach) / BAND_HEIGHT);
    if (lo < 0) lo = 0;
    if (hi > nb - 1) hi = nb - 1;
    const out: RestNode[] = [];
    for (let b = lo; b <= hi; b++) {
      for (const n of this.restNodes(b)) {
        if (n.floorY + REST_SHELL >= y0 && n.ceilY - REST_SHELL <= y1) out.push(n);
      }
    }
    return out;
  }

  /**
   * The rest nodes this chunk holds a slice of — issue #6.
   *
   * Ordinary biome `SHELL`, never adamant. The material *is* the signal: a
   * sanctum is the thing nobody built and nobody can remove, a rest node is a
   * room somebody made out of what was to hand. Telling them apart should not
   * require walking in.
   */
  private restChambers(): void {
    const by = this.baseY;
    const bx = this.baseX;
    for (const n of this.restNodesNear(by, by + CHUNK_SIZE - 1)) {
      if (n.x1 + REST_SHELL + REST_APPROACH < bx || n.x0 - REST_SHELL - REST_APPROACH >= bx + CHUNK_SIZE) {
        continue;
      }
      this.carveRest(n);
    }
  }

  private carveRest(n: RestNode): void {
    const w = n.x1 - n.x0 + 1;
    const top = n.ceilY - REST_SHELL + 1;
    const h = n.floorY + REST_SHELL - top;
    this.stampRect(n.x0 - REST_SHELL, top, w + REST_SHELL * 2, h, K_SHELL, 1);
    this.stampRect(n.x0, n.floorY - REST_IN_H, w, REST_IN_H, K_OPEN, 2);

    // A doorway in the wall facing the spine, and a corridor that reaches back
    // toward it. Entered on the level, not dropped into: the floor is snapped
    // to the same terrace lattice the galleries use, so the corridor runs into
    // the gallery stack at its own height instead of ending in a slab.
    const doorY = n.floorY - REST_DOOR_H;
    const runX = n.doorDir < 0 ? n.x0 - REST_SHELL - REST_APPROACH : n.x1 + 1;
    const runW = REST_APPROACH + REST_SHELL;
    this.stampRect(runX, doorY, runW, REST_DOOR_H, K_OPEN, 1);
    this.stampRect(runX, n.floorY, runW, REST_SHELL, K_SHELL, 1);

    this.markPathRect(n.x0, n.floorY - REST_IN_H, w, REST_IN_H);
    this.markPathRect(runX, doorY, runW, REST_DOOR_H);
  }

  // -------------------------------------------------------- set pieces (#1.5)

  /** Set pieces this chunk holds a slice of. */
  private setPieceChambers(): void {
    const by = this.baseY;
    const bx = this.baseX;
    for (const p of setPiecesOverlapping(
      this.seed, this.bandOrder.length, BAND_HEIGHT, by, by + CHUNK_SIZE - 1,
    )) {
      const reach = SET_PIECE_APPROACH;
      if (p.x + p.piece.w + reach < bx || p.x - reach >= bx + CHUNK_SIZE) continue;
      this.carveSetPiece(p);
    }
  }

  /**
   * Stamp one authored piece — issue #1 phase 5.
   *
   * The ops run in declaration order and the through-corridor runs after all of
   * them, which is the whole connectivity contract in one line: whatever a
   * piece builds, the route out of it is cut afterwards and cannot be built
   * over. The approach corridors either side reach back into the terrain so the
   * corridor connects to something rather than ending in the noise, and both
   * are marked `Struct.CRITICAL_PATH` so the §3.4 verifier counts the route it
   * can actually walk.
   */
  private carveSetPiece(p: PlacedSetPiece): void {
    const s = p.piece;
    for (const op of s.ops) {
      if (op.op === 'rect') {
        this.stampRect(p.x + op.x, p.y + op.y, op.w, op.h, kindOfPiece(op.k), 1);
      } else if (op.op === 'ellipse') {
        this.stampEllipse(
          p.x + op.x - this.baseX, p.y + op.y - this.baseY,
          op.rx, op.ry, kindOfPiece(op.k), 1,
        );
      }
      // 'dress' writes cells, not kinds — see `dressSetPieces`.
    }

    // The route, last. Its floor is a slab under the corridor rather than
    // whatever the ops happened to leave, because "there is a corridor here" and
    // "there is somewhere to stand in it" are different claims and the walkable
    // run measurements in `worldshape.test.ts` are about the second one.
    const cw = s.w + SET_PIECE_APPROACH * 2;
    this.stampRect(p.x - SET_PIECE_APPROACH, p.throughTop, cw, s.throughH, K_OPEN, 2);
    this.stampRect(p.x - SET_PIECE_APPROACH, p.throughFloor, cw, 4, K_SHELL, 2);
    this.markPathRect(p.x - SET_PIECE_APPROACH, p.throughTop, cw, s.throughH);
  }

  /**
   * Dress the set pieces in this chunk with the biome's own materials.
   *
   * Runs in `furnish`, after materialisation, for the same reason the plinths
   * do: `Kind` has five members and none of them is "the biome's second
   * liquid". Roles are resolved against the block palette at the cell being
   * written, so a piece that straddles a contamination boundary wears both
   * sides of it — which is correct, and free.
   */
  private dressSetPieces(chunk: Chunk, baseY: number): void {
    for (const p of setPiecesOverlapping(
      this.seed, this.bandOrder.length, BAND_HEIGHT, baseY, baseY + CHUNK_SIZE - 1,
    )) {
      for (const op of p.piece.ops) {
        if (op.op !== 'dress') continue;
        this.dressRect(chunk, p.x + op.x, p.y + op.y, op.w, op.h, op.role);
      }
    }
  }

  private dressRect(
    chunk: Chunk, x: number, y: number, w: number, h: number, role: PieceRole,
  ): void {
    const bx = this.baseX;
    const by = this.baseY;
    const x0 = Math.max(0, x - bx);
    const x1 = Math.min(CHUNK_SIZE - 1, x + w - 1 - bx);
    const y0 = Math.max(0, y - by);
    const y1 = Math.min(CHUNK_SIZE - 1, y + h - 1 - by);
    if (x1 < x0 || y1 < y0) return;
    const mat = chunk.mat;
    const temp = chunk.temp;
    const aux = chunk.aux;
    const flags = chunk.flags;
    const air = rolePoursIntoAir(role);
    for (let ly = y0; ly <= y1; ly++) {
      const row = ly << CHUNK_SHIFT;
      const blockRow = (ly >> BLOCK_SHIFT) << 4;
      for (let lx = x0; lx <= x1; lx++) {
        const i = row + lx;
        // Never over the through-corridor's own protection, and never over
        // adamant: a dressing pass is the last thing that should be able to
        // close the route the piece just promised.
        if (this.protect[i] === 2) continue;
        const isAir = mat[i] === VOID;
        if (air !== isAir) continue;
        const b = blockRow | (lx >> BLOCK_SHIFT);
        const m = this.roleMaterial(role, b);
        if (m === VOID) continue;
        mat[i] = m;
        const st = M.spawnTemp[m];
        temp[i] = st === 20 ? this.blkTemp[b] : st;
        aux[i] = M.burnDuration[m];
        // A vein written into rock inherits the rock's STATIC: the piece is
        // authored, and authored geometry does not avalanche on tick one.
        if (!air) flags[i] |= Flag.STATIC;
      }
    }
  }

  private roleMaterial(role: PieceRole, b: number): number {
    switch (role) {
      case 'liquid': return this.blkLiquid[b];
      case 'liquid2': return this.blkLiquid2[b];
      case 'fill': return this.blkFill[b];
      case 'hazard': return this.blkHazard[b];
      case 'glow': return this.blkGlow[b];
      case 'accent': return this.blkAccent[b];
      default: return this.blkShell[b];
    }
  }

  /** Every set piece in the world, top to bottom. Diagnostics and tests. */
  setPieces(): PlacedSetPiece[] {
    return allSetPieces(this.seed, this.bandOrder.length, BAND_HEIGHT);
  }

  /** The set piece in one band. Pure in `(seed, band)`. */
  setPiece(band: number): PlacedSetPiece {
    return setPieceAt(this.seed, band, BAND_HEIGHT);
  }

  /**
   * Furniture — the plinths and the water. Runs after materialisation because
   * it writes *cells*, not kinds: `Kind` has five members and none of them is
   * runestone, and inventing a sixth for two plinths would put a branch in the
   * hottest loop in the file for eight hundred cells in the entire world.
   *
   * Made of real material and flagged STATIC for the same reason the altar and
   * the ability cache are (`weaponAltar.ts`): these are the few structures a
   * run guarantees, and they should not be eaten by the simulation before the
   * player reaches them.
   */
  private furnish(chunk: Chunk, baseY: number): void {
    for (const s of sanctumsOverlapping(
      this.seed, this.bandOrder.length, BAND_HEIGHT, baseY, baseY + CHUNK_SIZE - 1,
    )) {
      this.plinth(chunk, s.cache.x - 4, s.cache.y, 9, 3, MAT.RUNESTONE);
      this.plinth(chunk, s.altar.x - 5, s.altar.y, 11, 3, MAT.RUNESTONE);
      this.fillCells(
        chunk, s.pool.x - s.pool.halfW, s.floorY, s.pool.halfW * 2 + 1, SANCTUM_SHELL - 1,
        MAT.WATER, 0,
      );
    }
    for (const n of this.restNodesNear(baseY, baseY + CHUNK_SIZE - 1)) {
      this.plinth(chunk, n.bench.x - 6, n.bench.y, 13, 3, MAT.RUNESTONE);
    }
    this.dressSetPieces(chunk, baseY);
  }

  private plinth(chunk: Chunk, x: number, y: number, w: number, h: number, m: number): void {
    this.fillCells(chunk, x, y, w, h, m, Flag.STATIC);
  }

  private fillCells(
    chunk: Chunk, x: number, y: number, w: number, h: number, m: number, extra: number,
  ): void {
    const bx = this.baseX;
    const by = this.baseY;
    const x0 = Math.max(0, x - bx);
    const x1 = Math.min(CHUNK_SIZE - 1, x + w - 1 - bx);
    const y0 = Math.max(0, y - by);
    const y1 = Math.min(CHUNK_SIZE - 1, y + h - 1 - by);
    if (x1 < x0 || y1 < y0) return;
    const st = M.spawnTemp[m];
    const dur = M.burnDuration[m];
    for (let ly = y0; ly <= y1; ly++) {
      const row = ly << CHUNK_SHIFT;
      const brow = (ly >> BLOCK_SHIFT) << 4;
      for (let lx = x0; lx <= x1; lx++) {
        const i = row + lx;
        chunk.mat[i] = m;
        chunk.temp[i] = st === 20 ? this.blkTemp[brow | (lx >> BLOCK_SHIFT)] : st;
        chunk.aux[i] = dur;
        chunk.flags[i] = (chunk.flags[i] & ~Flag.STATIC) | extra;
      }
    }
  }

  /** Fill the pond basin. Runs after materialisation — it writes cells, not kinds. */
  private pond(chunk: Chunk, baseX: number, baseY: number): void {
    const kind = this.kind;
    const mat = chunk.mat;
    const water = MAT.WATER;
    const t = M.spawnTemp[water];
    for (let lx = 0; lx < CHUNK_SIZE; lx++) {
      const wx = baseX + lx;
      if (this.pondDepth(wx) < 3) continue;
      const top = this.surfaceHeight(wx) + 3;
      for (let ly = 0; ly < CHUNK_SIZE; ly++) {
        if (baseY + ly < top) continue;
        const i = (ly << CHUNK_SHIFT) | lx;
        if (kind[i] !== K_OPEN || mat[i] !== VOID) continue;
        mat[i] = water;
        chunk.temp[i] = t === 20 ? this.blkTemp[((ly >> BLOCK_SHIFT) << 4) | (lx >> BLOCK_SHIFT)] : t;
        chunk.aux[i] = 0;
      }
    }
  }

  /** Clipped rect in world coords, writing kinds. */
  private worldRect(x: number, y: number, w: number, h: number, v: number): void {
    const bx = this.baseX;
    const by = this.baseY;
    const x0 = Math.max(0, x - bx);
    const x1 = Math.min(CHUNK_SIZE - 1, x + w - 1 - bx);
    const y0 = Math.max(0, y - by);
    const y1 = Math.min(CHUNK_SIZE - 1, y + h - 1 - by);
    const kind = this.kind;
    for (let ly = y0; ly <= y1; ly++) {
      const row = ly << CHUNK_SHIFT;
      for (let lx = x0; lx <= x1; lx++) kind[row + lx] = v;
    }
  }

  /** Clipped filled ellipse in world coords, writing kinds. */
  private worldEllipse(cx: number, cy: number, rx: number, ry: number, v: number): void {
    const bx = this.baseX;
    const by = this.baseY;
    const kind = this.kind;
    const inv = 1 / (ry * ry);
    const y0 = Math.max(0, cy - ry - by);
    const y1 = Math.min(CHUNK_SIZE - 1, cy + ry - by);
    for (let ly = y0; ly <= y1; ly++) {
      const dy = by + ly - cy;
      const q = 1 - dy * dy * inv;
      if (q <= 0) continue;
      const span = (rx * Math.sqrt(q)) | 0;
      const x0 = Math.max(0, cx - span - bx);
      const x1 = Math.min(CHUNK_SIZE - 1, cx + span - bx);
      const row = ly << CHUNK_SHIFT;
      for (let lx = x0; lx <= x1; lx++) kind[row + lx] = v;
    }
  }

  /**
   * Traversal-gated vaults, stamped into bedrock AFTER the corridor router so
   * nothing can accidentally breach one.
   *
   * docs/01 §3.4: gating must be honest — "if you can melt it, it was never a
   * gate." So the chamber is fully enclosed in ADAMANT_SEAL, which is
   * `indestructible: true`. There is no door, no weak wall and no back way in.
   * The biome's traversal ability opens the seal or you do not get the loot.
   *
   * Because it is fully enclosed it can never affect the critical path, which
   * is why this is safe to do without re-running connectivity.
   */
  private sealedVaults(cx: number, cy: number): void {
    // Roughly one gated vault per three chunks. Keyed on the chunk so the
    // decision is deterministic without consuming the chunk RNG unevenly.
    if (hash2(cx, cy, this.seed ^ 0x7ea15ed) % 3 !== 0) return;

    const kind = this.kind;
    const prot = this.protect;
    for (let attempt = 0; attempt < 5; attempt++) {
      const rx = this.rng.int(7, 12);
      const ry = this.rng.int(5, 9);
      const mx = rx + 3;
      const my = ry + 3;
      const cxp = this.rng.int(mx + 1, CHUNK_SIZE - mx - 2);
      const cyp = this.rng.int(my + 1, CHUNK_SIZE - my - 2);

      // Never over a protected cell — that would let a corridor breach the
      // seal, and a gate with a hole in it is a lie. Otherwise it only needs
      // enough surrounding rock to read as embedded rather than floating.
      let solid = 0;
      let cells = 0;
      let clean = true;
      for (let y = cyp - my; y <= cyp + my && clean; y++) {
        const row = y << CHUNK_SHIFT;
        for (let x = cxp - mx; x <= cxp + mx; x++) {
          if (prot[row + x] !== 0) {
            clean = false;
            break;
          }
          cells++;
          if (kind[row + x] === K_SOLID) solid++;
        }
      }
      if (!clean || solid < cells * 0.6) continue;

      this.stampEllipse(cxp, cyp, rx + 3, ry + 3, Kind.GATE, 1);
      this.stampEllipse(cxp, cyp, rx, ry, K_OPEN, 1);
      return;
    }
  }

  private stampEllipse(cxp: number, cyp: number, rx: number, ry: number, v: number, p: number): void {
    const kind = this.kind;
    const prot = this.protect;
    const y0 = Math.max(0, cyp - ry);
    const y1 = Math.min(CHUNK_SIZE - 1, cyp + ry);
    const inv = 1 / (ry * ry);
    for (let y = y0; y <= y1; y++) {
      const dy = y - cyp;
      const q = 1 - dy * dy * inv;
      if (q <= 0) continue;
      const span = (rx * Math.sqrt(q)) | 0;
      const x0 = Math.max(0, cxp - span);
      const x1 = Math.min(CHUNK_SIZE - 1, cxp + span);
      const row = y << CHUNK_SHIFT;
      for (let x = x0; x <= x1; x++) {
        kind[row + x] = v;
        prot[row + x] = p;
      }
    }
  }

  // ---- 6. materialisation

  /**
   * The one genuinely hot loop in this file — 16384 cells, and the only thing
   * standing between a 1 ms chunk and a 3 ms one. Everything it touches is
   * hoisted into a local, the bilinear taps are written out rather than called,
   * and the grain hash is inlined. No property lookups, no method calls, no
   * allocation inside the loop.
   */
  private materialise(chunk: Chunk, baseX: number, baseY: number): void {
    const mat = chunk.mat;
    const temp = chunk.temp;
    const flags = chunk.flags;
    const aux = chunk.aux;
    const kind = this.kind;
    const prot = this.protect;
    const nOre = this.nOre;
    const host = this.blkHost;
    const accent = this.blkAccent;
    const oreThresh = this.blkOreThresh;
    const shell = this.blkShell;
    const platform = this.blkPlatform;
    const vein = this.blkVein;
    const glow = this.blkGlow;
    const fill = this.blkFill;
    const btemp = this.blkTemp;
    const cntOpen = this.cntOpen;
    const cntShell = this.cntShell;
    const cntGate = this.cntGate;
    const cntFloor = this.cntFloor;
    const solidTab = M.solid;
    const spawnTemp = M.spawnTemp;
    const burnDur = M.burnDuration;

    cntOpen.fill(0);
    cntShell.fill(0);
    cntGate.fill(0);
    cntFloor.fill(0);

    const adamant = MAT.ADAMANT_SEAL;
    const vshift = Flag.VARIANT_SHIFT;
    const last = CHUNK_SIZE - 1;

    for (let ly = 0; ly < CHUNK_SIZE; ly++) {
      const row = ly << CHUNK_SHIFT;
      const blockRow = (ly >> BLOCK_SHIFT) << 4;
      const wy = baseY + ly;
      const gRow = (ly >> COARSE_SHIFT) * CG;
      const fy = (ly & (COARSE - 1)) * (1 / COARSE);
      const wyh = Math.imul(wy, 0x165667b1);

      for (let lx = 0; lx < CHUNK_SIZE; lx++) {
        const i = row + lx;
        const b = blockRow | (lx >> BLOCK_SHIFT);
        const k = kind[i];

        // Stable per-cell visual noise; the renderer reads these three bits.
        let hv = Math.imul(baseX + lx, 0x27d4eb2d) ^ wyh;
        hv ^= hv >>> 15;
        hv = Math.imul(hv, 0x2c1b3c6d);
        hv ^= hv >>> 12;
        let f = (hv & 7) << vshift;
        let m: number;

        if (k === K_SOLID) {
          const j = gRow + (lx >> COARSE_SHIFT);
          const fx = (lx & (COARSE - 1)) * (1 / COARSE);
          // **Host fill.** `Kind.SOLID` is "the rock around here", and around
          // here is a region, not a cell — `hostRockOf`, issue #143. This used
          // to be a bilinear read of the strata field thresholded per cell,
          // which is what made the bulk a quilt of two rocks rather than one
          // mass with a boundary in it.
          m = host[b];

          const o0 = nOre[j];
          const oTop = o0 + (nOre[j + 1] - o0) * fx;
          const o2 = nOre[j + CG];
          const oBot = o2 + (nOre[j + CG + 1] - o2) * fx;
          // A vein repaints the rock and inherits its shape: no `kind` write,
          // no silhouette of its own. The threshold is the block biome's
          // `oreDensity` inverted through the field's CDF, so how much of the
          // rock is vein is authored per biome and how big a vein is comes from
          // ORE_WAVELENGTH — two questions that used to be one constant.
          //
          // A vein of something that is not solid — METHANE in the Rotwood,
          // VOID_MATTER in the Choir, MERCURY in the Spire — is only painted
          // where the rock **encloses** it. At an 18-cell wavelength that never
          // mattered; at 160 it does, and it broke the walkability contract the
          // first time it was measured: `worldshape` reported band 7's longest
          // walkable run down from 60+ to 54 cells, because a plasma blob that
          // reaches a ledge deletes the ledge. A sealed pocket is also what the
          // fiction says these are — "breach one and the room fills" only
          // means anything if it was not already open.
          if (oTop + (oBot - oTop) * fy > oreThresh[b]) {
            const a = accent[b];
            // The enclosure test is written out rather than called: this is the
            // hot loop, and it is only reached for the ~5% of cells that are
            // vein in a region whose accent is not solid. A cell on the chunk
            // border counts as exposed, because `kind` is chunk-local and
            // cannot see the neighbour — one cell of extra rock per 128 is
            // cheaper than a gas pocket leaking across the seam.
            if (solidTab[a] === 1
              || (lx > 0 && lx < last && ly > 0 && ly < last
                && kind[i - 1] !== K_OPEN && kind[i + 1] !== K_OPEN
                && kind[i - CHUNK_SIZE] !== K_OPEN && kind[i + CHUNK_SIZE] !== K_OPEN)) {
              m = a;
            }
          }

          // Non-rigid bulk (Meatworks flesh, contaminated sludge) would
          // avalanche on tick 1 without this. STATIC is exactly "worldgen-
          // placed, resists settling" — docs/01 §3.2 bit 7.
          if (solidTab[m] === 0) f |= Flag.STATIC;
        } else if (k === K_OPEN) {
          m = VOID;
          cntOpen[b]++;
          // Floor detection for WALKABLE_FLOOR. kind[] is fully resolved before
          // this pass, so looking one row down is safe here.
          if (ly < last && kind[i + CHUNK_SIZE] !== K_OPEN) cntFloor[b]++;
        } else if (k === K_SHELL) {
          m = shell[b];
          f |= Flag.STATIC;
          cntShell[b]++;
        } else if (k === K_PLATFORM) {
          // Not counted as SHELL: a one-way floor bounds nothing, and tagging
          // its block SHELL would tell the connectivity verifier a route it can
          // actually walk is a wall.
          m = platform[b];
          f |= Flag.STATIC;
        } else if (k === K_GATE) {
          m = adamant;
          f |= Flag.STATIC;
          cntGate[b]++;
        } else {
          // **The three authored-solid classes, and nothing but a drawing
          // writes them** — `src/world/tiles/*.pxm`, `docs/19` §8.3b. Last in
          // the chain on purpose: they are reached by roughly one cell in a
          // hundred of the world and this is the loop that decides whether a
          // chunk costs 1 ms or 3, so they pay the compares rather than making
          // the bulk pay them.
          //
          // Every one of the three names a slot some *other* pass is already
          // scattering through the same window — `deposits` lays `blkFill`,
          // `glowVeins` lays `blkGlow`, and the vein branch above lays an
          // accent. So a drawn room contributes no material to a window's
          // histogram that was not in it already, which is the property that
          // makes the vocabulary affordable at all and the one `docs/24` §12.6
          // measures against `regionOccupancy`.
          //
          // `Flag.STATIC` on all three without asking `solidTab`, unlike the
          // bulk branch: `blkFill` is LOOSE or SLUDGE in all twelve biomes and
          // `blkVein` is LOOSE in five, and a drawn mass that avalanches on
          // tick 1 is a drawing the player never sees. The bulk branch can
          // afford to ask because rock is rigid except in the Meatworks; a
          // drawing cannot, and the rule "a drawn cell is static matter" is
          // also the reason `liquid`/`liquid2`/`hazard` are not classes.
          m = k === K_VEIN ? vein[b] : k === K_GLOW ? glow[b] : fill[b];
          f |= Flag.STATIC;
          // Not counted as SHELL, for `K_PLATFORM`'s reason turned around: a
          // seam, a lamp and a silt bank are furniture inside a room, and a
          // block tagged `Struct.SHELL` tells the connectivity verifier the
          // room it is standing in is a wall.
        }

        mat[i] = m;
        flags[i] = f;
        const st = spawnTemp[m];
        temp[i] = m === VOID || st === 20 ? btemp[b] : st;
        aux[i] = burnDur[m];
      }
    }
  }

  // ---- 6b. the biome's own light

  /**
   * Veins of `palette.glow` in the exposed face of the rock — issue #31.
   *
   * #31's measurement was that at `ambient = 0.35` an unlit biome is near-black
   * and that the field was not at fault: **almost nothing in the world emits**,
   * so virtually every cell rendered at the floor. Ten of the twelve biomes had
   * no light source anywhere in them, because `M.lightSource` needs
   * `emissive >= 0.3` and only fire, lava, molten iron and radiant matter
   * cleared it — and worldgen places those in two biomes.
   *
   * THREE PLACEMENT RULES, each of which is load-bearing:
   *
   *  1. **Into solid cells that already touch open space.** A vein buried in
   *     rock lights nothing: the light field reduces an 8×8 tile to summed
   *     emission and *mean opacity*, so a fully-enclosed emitter pays the full
   *     occlusion of its own tile and never reaches the room. Replacing an
   *     exposed face costs the player no headroom, no walkable run and no
   *     collision surface — which is why this does not place into open cells,
   *     where every cluster would be an obstacle in a corridor and a step in a
   *     floor the ledge pass just spent a whole issue levelling.
   *
   *  2. **One decision per 32-cell span, not per cell.** Same argument as
   *     `deposits`: a per-cell roll is a texture nobody can read, and a vein is
   *     a *place* — somewhere brighter that you walk toward. 32 is four light
   *     tiles, so with `FALLOFF` at 0.72 a cluster's contribution has decayed
   *     to roughly a quarter by the time the next one picks up, which is what
   *     makes the room read as pools of light rather than as a flat wash.
   *
   *  3. **Nothing is placed on adamant or on a one-way floor.** Same rule
   *     `deposits` states: the seal is the one surface the world does not get
   *     to dress, and a platform is a route.
   *
   * DETERMINISM. Keyed on absolute world coordinates through `hash2`, never on
   * `this.rng`, so a chunk regenerates identically after eviction and two
   * chunks sharing a span agree without talking.
   *
   * COST. The light field's own quota (`sim/light.ts` `maxWorkPerTick`) binds,
   * so making the world emit everywhere cannot push the light system over its
   * 0.4 ms budget — it spends the same quota on more places, and pays in a few
   * ticks of fade-in. That is the property that makes this safe to do at all.
   */
  private glowVeins(chunk: Chunk): void {
    const mat = chunk.mat;
    const temp = chunk.temp;
    const aux = chunk.aux;
    const prot = this.protect;
    const glowTab = this.blkGlow;
    const densTab = this.blkGlowDensity;
    const btemp = this.blkTemp;
    const baseX = this.baseX;
    const baseY = this.baseY;
    const seed = this.seed ^ 0x91076e;

    for (let sy = 0; sy < CHUNK_SIZE; sy += GLOW_SPAN) {
      for (let sx = 0; sx < CHUNK_SIZE; sx += GLOW_SPAN) {
        const b = ((sy >> BLOCK_SHIFT) << 4) | (sx >> BLOCK_SHIFT);
        const h = hash2((baseX + sx) >> GLOW_SHIFT, (baseY + sy) >> GLOW_SHIFT, seed);
        // Scan the span from a hashed start so a vein is not pinned to the
        // span's corner on every seed — the lattice would be visible.
        const ox = (h >>> 8) & (GLOW_SPAN - 1);
        const oy = (h >>> 16) & (GLOW_SPAN - 1);
        let fx = -1;
        let fy = -1;
        for (let k = 0; k < GLOW_SPAN * GLOW_SPAN && fx < 0; k++) {
          const lx = sx + ((ox + (k % GLOW_SPAN)) & (GLOW_SPAN - 1));
          const ly = sy + ((oy + ((k / GLOW_SPAN) | 0)) & (GLOW_SPAN - 1));
          if (this.exposedFace(lx, ly)) {
            fx = lx;
            fy = ly;
          }
        }
        // A span that is all open air or all deep rock has no face to grow on,
        // which is correct: light comes from where the room meets the wall.
        if (fx < 0) continue;

        if (h / 4294967296 >= densTab[b]) continue;

        // **A bigger room needs a bigger lamp.** The span rate cannot answer
        // for a cavern: `hollow_choir` authors `glowDensity` 0.92, so every span
        // with a face in it already gets a seam and raising the rate does
        // literally nothing — measured, at 1.9x and again at 4.0x, moving the
        // reported median by zero to three decimals. What a cavern actually has
        // less of, per unit of open volume, is *wall*, so the seam gets longer
        // instead of more frequent where it faces one.
        const fi = (fy << CHUNK_SHIFT) | fx;
        const reach =
          prot[fi] === P_MASS
          || (fx > 0 && prot[fi - 1] === P_MEGA)
          || (fx < CHUNK_SIZE - 1 && prot[fi + 1] === P_MEGA)
          || (fy > 0 && prot[fi - CHUNK_SIZE] === P_MEGA)
          || (fy < CHUNK_SIZE - 1 && prot[fi + CHUNK_SIZE] === P_MEGA)
            ? GLOW_REACH_MEGA
            : GLOW_REACH;
        const m = glowTab[b];
        const st = M.spawnTemp[m];
        const dur = M.burnDuration[m];
        for (let dy = -1; dy <= 1; dy++) {
          const ly = fy + dy;
          if (ly < 0 || ly >= CHUNK_SIZE) continue;
          const row = ly << CHUNK_SHIFT;
          // A seam along the face, not a dot on it. This was +-2, i.e. a 5x3
          // box, and it produced 84-114 separate glow components per screen at
          // a **mean size of 4 to 31 cells** — the same "material with a shape
          // of its own, smaller than the player" defect #60 is about, in the
          // pass that lights the world. +-6 follows the face for up to 13
          // cells, still inside the 32-cell span so the pools-of-light spacing
          // #31 argues for is untouched.
          //
          // It is also what pays for #60's other half. Coarsening the accent
          // field removed the emissive confetti that four bands were quietly
          // lit by (the Cathedral's RADIANT_MATTER, the Reef's SIGNAL_BLOOM,
          // the Choir's CHOIRLIGHT, drawn afresh every 8 cells), and
          // `lighting.test.ts` measured all four falling under the 0.58
          // legibility floor. The light has to come from the pass that is
          // supposed to provide it.
          for (let dx = -reach; dx <= reach; dx++) {
            const lx = fx + dx;
            if (lx < 0 || lx >= CHUNK_SIZE) continue;
            if (!this.exposedFace(lx, ly)) continue;
            const i = row + lx;
            const blk = ((ly >> BLOCK_SHIFT) << 4) | (lx >> BLOCK_SHIFT);
            mat[i] = m;
            temp[i] = st === 20 ? btemp[blk] : st;
            aux[i] = dur;
          }
        }
      }
    }
  }

  /**
   * True for a wall cell with open space against it. Bulk and room shell only:
   * `Kind.GATE` is adamant and `Kind.PLATFORM` is a route, and neither is a
   * surface the world is allowed to decorate.
   */
  private exposedFace(lx: number, ly: number): boolean {
    const kind = this.kind;
    const i = (ly << CHUNK_SHIFT) | lx;
    const k = kind[i];
    if (k !== K_SOLID && k !== K_SHELL) return false;
    if (lx > 0 && kind[i - 1] === K_OPEN) return true;
    if (lx < CHUNK_SIZE - 1 && kind[i + 1] === K_OPEN) return true;
    if (ly > 0 && kind[i - CHUNK_SIZE] === K_OPEN) return true;
    if (ly < CHUNK_SIZE - 1 && kind[i + CHUNK_SIZE] === K_OPEN) return true;
    return false;
  }

  // ---- 7. scatter

  /**
   * Loose deposits, laid as drifts rather than as a per-cell lottery.
   *
   * This was the *other* half of the walkability defect, and the one the issue
   * did not name. A per-cell roll at `depositDensity` 0.1-0.4 puts a 1-3 cell
   * pile every three to ten columns, and every pile is a step, so no floor
   * anywhere in the world was level for longer than the gap between two rolls.
   * That is why the measured median run was 5-7 on every seed in both modes
   * regardless of what the carve did — the piles, not the caves, set it.
   *
   * Keying the roll on a 64-cell span instead of the cell makes a drift: one
   * decision, one depth, a flat top you can fight on, and a bank of coal dust
   * that reads as a deliberate place. Same material, same density, same
   * diggability.
   */
  private deposits(chunk: Chunk): void {
    const mat = chunk.mat;
    const temp = chunk.temp;
    const aux = chunk.aux;
    const kind = this.kind;
    const prot = this.protect;
    const fillTab = this.blkFill;
    const depTab = this.blkDeposit;
    const btemp = this.blkTemp;
    const baseX = this.baseX;
    const baseY = this.baseY;
    const seed = this.seed ^ 0xd3705;
    // Thin Veil. Hoisted, and zero-length on a plain world, so the branch below
    // is a length test rather than a struct read per drift.
    const voids = this.voidMats;
    const bleed = voids.length === 0 ? 0 : this.modifiers.voidBleed;

    for (let ly = 1; ly < CHUNK_SIZE; ly++) {
      const row = ly << CHUNK_SHIFT;
      const above = row - CHUNK_SIZE;
      const blockRow = (ly >> BLOCK_SHIFT) << 4;
      const wy = baseY + ly;
      for (let lx = 0; lx < CHUNK_SIZE; lx++) {
        // Cheapest rejects first: this fires on every cell, and only the thin
        // floor surface survives all three tests.
        // Not on a one-way floor: `M.solid` is 0 for both platform materials,
        // so a drift laid on a grating pours straight through it on tick one.
        // Worldgen placing matter on a floor it will fall through is a bug
        // whichever way you read it.
        // Nothing settles on adamant. That is a rule about the material rather
        // than about the sanctum: the seal is the one surface in the world the
        // simulation does not get to dress, so an authored chamber does not
        // wake up with three cells of coal dust across its floor.
        //
        // **`Kind.VEIN`, `GLOW` and `FILL` are deliberately NOT added to this
        // list**, and it is a measurement rather than an oversight. Restating it
        // as a whitelist — `k !== K_SOLID && k !== K_SHELL`, which is
        // `exposedFace`'s own first line one section up — reads like the tidier
        // spelling and sounds like it would keep a drift off a drawn lamp. It
        // changes **nothing**: the drift is written into the OPEN cells *above*
        // the surface (the `pile` loop below), never into the surface itself, so
        // this test selects which floors get a bank of powder laid **on** them
        // and not which cells get overwritten. Mutation-tested both ways over
        // 3 194 drawn `@vein` cells with air over them: zero cells differ. A
        // drift settling on a drawn basin floor is the same drift that settles
        // on a room shell today, and `survival()` in `tests/tileMask.test.ts`
        // already states the position — the generator furnishing a room is not
        // the generator deleting one.
        const k = kind[row + lx];
        if (k === K_OPEN || k === K_PLATFORM || k === K_GATE) continue;
        const ai = above + lx;
        // Deliberately not gated on `protect`, unlike the pools. A pool on the
        // critical path can cool into obsidian and seal it; a 1-3 cell drift of
        // powder is walked over and dug through. Gating it here made a drift
        // stop dead at every gallery edge, which put a step in the floor every
        // twenty cells — the exact defect the span keying exists to remove.
        if (kind[ai] !== K_OPEN) continue;
        // ...but not over the opening terrace. The surface around spawn is
        // authored: three cells of spore drift on top of it is the difference
        // between standing on the ground and standing in it, and a drift that
        // starts partway along puts a step in the one stretch of the world
        // that has to read as level at a glance.
        // ...nor over the Great Tree's terrace, for the identical reason and
        // one more: the landmark carves a hollow at ground level, and a drift
        // laid inside it is a cell the authored oracle says is air and the
        // world says is spore dust. That breaks the seed-invariance the whole
        // persistence model rests on (`docs/27` §3), and it is why this clause
        // is keyed on the terrace rather than on `protect` — see the note above
        // about why `protect` is deliberately not consulted here.
        if (wy < 0 && baseX + lx > -200 && baseX + lx < 200) continue;
        if (wy < 0 && baseX + lx >= TERRACE.x0 && baseX + lx <= TERRACE.x1) continue;
        // ...nor over the mountain, for the third time and the same reason
        // (`docs/27` §7 item 4). It matters more here than anywhere: the
        // mountain's gates are *rise* gates, so the height of a tread is the
        // gate, and a drift is 1-3 cells of seeded height laid on top of an
        // authored one. Keyed on the landmark's own bounds rather than on
        // `protect`, because `deposits` deliberately does not consult it.
        if (wy < 0 && baseX + lx >= MOUNTAIN_BOUNDS.x0 && baseX + lx <= MOUNTAIN_BOUNDS.x1) {
          continue;
        }
        const b = blockRow | (lx >> BLOCK_SHIFT);
        const h = hash2((baseX + lx) >> 6, wy >> 5, seed);
        if (h / 4294967296 >= depTab[b]) continue;
        let fill = fillTab[b];
        // Substituted per drift rather than per cell, for the same reason the
        // volatile fill is: a bank of void matter reads as somewhere the veil
        // is thin, and a scatter of single cells reads as a rendering bug. Its
        // own hash, so turning the modifier on does not reshuffle where the
        // drifts are — only what they are made of.
        if (bleed > 0) {
          const hv = hash2((baseX + lx) >> 6, (wy >> 5) ^ 0x5d, seed ^ 0x901d);
          if ((hv & 0xffff) / 65536 < bleed) fill = voids[(hv >>> 16) % voids.length];
        }
        const st = M.spawnTemp[fill];
        const t = st === 20 ? btemp[b] : st;
        const dur = M.burnDuration[fill];
        const pile = 1 + ((h >>> 16) % 3);
        for (let k = 0; k < pile; k++) {
          const j = ai - (k << CHUNK_SHIFT);
          if (j < 0 || kind[j] !== K_OPEN || mat[j] !== VOID) break;
          mat[j] = fill;
          temp[j] = t;
          aux[j] = dur;
        }
      }
    }
  }

  /**
   * Liquid pools, in authored pairs.
   *
   * Every biome declares two liquids whose meeting is its signature reaction
   * (biomes.ts header note 3). Placing only one meant no liquid could ever
   * reach another liquid — cross-contamination swaps `secondary`, `fill`,
   * `accent` or `hazard` and never the liquid slot — so `WATER + LAVA` was
   * unreachable in every seed of every biome.
   *
   * The second liquid goes down as a lobe *offset* from the first rather than
   * overlapping it. Overlapping would have the pair react on tick one and the
   * player would arrive to the aftermath; offset leaves a lip of rock between
   * two pools that are visibly different colours, which is a question.
   */
  private pools(chunk: Chunk): void {
    const b0 = this.biomes[this.blkBiome[(8 << 4) | 8]];
    const n = Math.round(b0.terrain.liquidLevel * 5 * this.modifiers.liquidLevelMul);
    for (let p = 0; p < n; p++) {
      const cxp = this.rng.int(16, CHUNK_SIZE - 17);
      // Pools sit low: a pool spawned at the ceiling is just a waterfall the
      // player never sees the source of.
      const cyp = this.rng.int(CHUNK_SIZE >> 1, CHUNK_SIZE - 12);
      const rx = this.rng.int(8, 22);
      const ry = this.rng.int(4, 10);
      const b = ((cyp >> BLOCK_SHIFT) << 4) | (cxp >> BLOCK_SHIFT);
      this.blobFill(chunk, cxp, cyp, rx, ry, this.blkLiquid[b], b);

      const dir = this.rng.sign();
      const ox = cxp + dir * ((rx * 3) >> 1);
      if (ox < 8 || ox > CHUNK_SIZE - 9) continue;
      const ob = ((cyp >> BLOCK_SHIFT) << 4) | (ox >> BLOCK_SHIFT);
      this.blobFill(chunk, ox, cyp, (rx * 3) >> 2, ry, this.blkLiquid2[ob], ob);
    }
  }

  /**
   * Ambient hazard pockets.
   *
   * `Math.round(hazardDensity * 6)` rounded to ZERO for the four biomes whose
   * density is below 0.084 — Sunken Vault (0.02), Emberworks (0.05), Verdigris
   * Spire (0.06) and the Cinder March (0.08) — so Tidal Flooding, Coal-Dust
   * Suspension, Relic Constructs and Ash-Fall were authored and then never
   * placed in a single chunk. Carrying the fraction as a probability honours
   * the authored rate instead of truncating it, and stays deterministic
   * because `this.rng` is reseeded per chunk from (seed, cx, cy).
   *
   * `intensity` and `ambientHazard.material` had no consumers at all. They do
   * now, and they mean different things: density is HOW OFTEN, intensity is HOW
   * BIG, and the ambientHazard material is what the biome says its hazard
   * actually is — which for three of the twelve is not the palette hazard slot
   * (Sunken Vault floods with WATER, not STEAM).
   */
  private hazardPockets(chunk: Chunk): void {
    const b0 = this.biomes[this.blkBiome[(8 << 4) | 8]];
    const haz = b0.ambientHazard;
    const rate = b0.terrain.hazardDensity * 6 * this.modifiers.hazardDensityMul;
    let n = Math.floor(rate);
    if (this.rng.float() < rate - n) n++;
    const scale = 0.6 + haz.intensity * 0.8;
    for (let p = 0; p < n; p++) {
      const cxp = this.rng.int(12, CHUNK_SIZE - 13);
      const cyp = this.rng.int(12, CHUNK_SIZE - 13);
      const r = Math.max(3, Math.round(this.rng.int(5, 13) * scale));
      const b = ((cyp >> BLOCK_SHIFT) << 4) | (cxp >> BLOCK_SHIFT);
      // Alternate: the authored ambient hazard, then the palette slot — which
      // is the one cross-contamination is allowed to swap.
      const m = (p & 1) === 0 ? haz.material : this.blkHazard[b];
      this.blobFill(chunk, cxp, cyp, r, r, m, b);
    }
  }

  /**
   * Fill an ellipse of open, unprotected cells. Skipping protected cells is
   * not cosmetic: a lava pool on the critical path can cool into obsidian and
   * seal the route without a single cell being destroyed, which is precisely
   * the failure the redundancy verifier exists to catch.
   */
  private blobFill(
    chunk: Chunk, cxp: number, cyp: number, rx: number, ry: number, m: number, blk: number,
  ): void {
    if (m === VOID) return;
    const mat = chunk.mat;
    const temp = chunk.temp;
    const aux = chunk.aux;
    const kind = this.kind;
    const prot = this.protect;
    const st = M.spawnTemp[m];
    const t = st === 20 ? this.blkTemp[blk] : st;
    const dur = M.burnDuration[m];
    const y0 = Math.max(0, cyp - ry);
    const y1 = Math.min(CHUNK_SIZE - 1, cyp + ry);
    const inv = 1 / (ry * ry);
    for (let y = y0; y <= y1; y++) {
      const dy = y - cyp;
      const q = 1 - dy * dy * inv;
      if (q <= 0) continue;
      const span = (rx * Math.sqrt(q)) | 0;
      const x0 = Math.max(0, cxp - span);
      const x1 = Math.min(CHUNK_SIZE - 1, cxp + span);
      const row = y << CHUNK_SHIFT;
      for (let x = x0; x <= x1; x++) {
        const i = row + x;
        // A mega cavern is not "protected" in the sense this gate means. The
        // gate exists so a lava pool cannot cool into obsidian across the
        // critical path (see the note above); a cavern is ordinary space that
        // happens to be off limits to the *shape* passes. Excluding it made
        // `pools` and `hazardPockets` skip a fifth of the world, which showed up
        // as `tests/biomeLookup.test.ts` reporting `liquidLevel` as a knob with
        // no effect on generation at all, and as the drowned-world modifier
        // moving liquid volume by 0.9 % against a 4 % floor.
        if (kind[i] !== K_OPEN || mat[i] !== VOID) continue;
        if (prot[i] !== 0 && prot[i] !== P_MEGA && prot[i] !== P_MASS) continue;
        mat[i] = m;
        temp[i] = t;
        aux[i] = dur;
      }
    }
  }

  // ---- 8. structural annotation

  private annotate(cx: number, cy: number): void {
    const reg = this.structure.region(cx, cy);
    for (let b = 0; b < 256; b++) {
      const open = this.cntOpen[b];
      const shell = this.cntShell[b];
      const gate = this.cntGate[b];
      const floor = this.cntFloor[b];
      let tag: number;
      // GATE only when the seal actually dominates the block. A doorway block
      // that is mostly open floor with a few seal cells at its lip is a route,
      // not a gate, and tagging it GATE would make the verifier refuse a path
      // the player can walk.
      if (gate >= 6 && open < 32) tag = Struct.GATE;
      else if (shell >= 16 && open < 32) tag = Struct.SHELL;
      else if (open >= 10 && floor >= 2) tag = Struct.WALKABLE_FLOOR;
      else if (open >= 24) tag = Struct.EMPTY;
      else tag = Struct.DECOR;
      reg[b] = tag;
    }
    // Path last: the router's promise outranks any heuristic.
    for (let b = 0; b < 256; b++) if (this.pathBlocks[b] !== 0) reg[b] = Struct.CRITICAL_PATH;
    this.pathBlocks.fill(0);
  }

  private recordAnchor(cx: number, cy: number): void {
    const key = chunkKeyOf(cx, cy);
    if (this.anchorIndex.has(key)) return;
    const rx = cx << 1;
    const ry = cy << 1;
    const x = nodeX(rx, ry, this.seed);
    const y = nodeY(rx, ry, this.seed);
    this.anchorIndex.set(key, this.anchors.length);
    this.anchors.push({ x, y, biome: this.biomeAt(x, y).id });
  }

  /** Drop all derived state. The world regenerates identically afterwards. */
  reset(): void {
    this.structure.clear();
    this.anchors.length = 0;
    this.anchorIndex.clear();
    this.repairs = 0;
  }
}

/**
 * Map a biome's `caveDensity` to a threshold on the cave field.
 *
 * The mixed fbm+ridge field is not uniform — its 20th and 80th percentiles sit
 * at 0.397 and 0.589 — so a naive threshold of 0.5 carves ~52% of the volume
 * and the world reads as one continuous void. These constants are that measured
 * distribution, inverted. The 0.48 factor leaves room for the tile bodies and
 * corridors, which carve on top of this and roughly double the open fraction.
 */
const CAVE_P20 = 0.3974;
const CAVE_SLOPE = 0.3187; // (p80 - p20) / 0.6
/**
 * `caveDensity` maps onto the open fraction the noise alone should carve. It
 * had to come down when the router started carving galleries a full shelf
 * period tall: the tile bodies and corridors now supply most of the open
 * volume, so a noise layer tuned to supply it as well left a void with debris
 * in it rather than rock with rooms in it.
 */
const CAVE_OPEN_BASE = 0.0;
const CAVE_OPEN_SPAN = 0.16;
function caveThreshold(caveDensity: number): number {
  return CAVE_P20 + (CAVE_OPEN_BASE + caveDensity * CAVE_OPEN_SPAN - 0.2) * CAVE_SLOPE;
}

/**
 * The most flammable material in a palette — what Volatile Strata substitutes
 * for a biome's ordinary deposit fill.
 *
 * `burnDuration` is the right measure and the only one: docs/03 calls it
 * combustion *fuel*, so the material that burns longest is the one that turns a
 * drift into a fuse. Read from the palette the biome already authored rather
 * than from a global accelerant list, because "everything is a fuse" should
 * mean the Emberworks' own coal and the Rotwood's own spores, not one imported
 * material laid through twelve identities.
 *
 * Falls back to the authored fill when nothing in the palette burns at all,
 * which is the correct no-op rather than a substitution that changes nothing
 * visible and everything about the biome's colour.
 */
/**
 * **The accent a drawing may place, under two clauses in this order.**
 *
 * `Kind.VEIN` is the class an authored room draws a *seam* with — the deposit
 * the room was cut for — and it is the one class in the vocabulary with a
 * *choice* to make, because `accents` is a list where every other slot is a
 * single material. So it is the one that carries the vocabulary's two rules.
 *
 *  1. **Solid.** Unlike the ore pass it has no enclosure rule to hide behind.
 *     That pass may decline to paint METHANE where the rock does not seal it,
 *     because it is choosing the cells; a drawing has already chosen them, in
 *     all twelve biomes at once. So: *a class may only name a material that can
 *     hold the shape it was drawn in.* `M.solid` rather than `Category.RIGID`,
 *     because LOOSE counts here: `materialise` writes `Flag.STATIC` on every
 *     drawn cell and `cellsim` refuses to move a STATIC cell. A bank of salt
 *     that was **drawn** is architecture; the same salt laid by `deposits` is a
 *     drift and still settles.
 *  2. **Not something another class already names.** `biomes.ts` states this
 *     rule twice already — #60 for `shell` against the bulk pair, #79 for
 *     `accents` against it — and the reason is the same both times: a mass drawn
 *     the value of the mass beside it is a mass nobody can see, and a drawing
 *     with seven classes in it that realises six materials has one fewer mass
 *     than its author drew. Without this clause `rotwood_reach` resolves `@vein`
 *     to SPORE_DUST, which is its `fill`, so the stope's seam and its spoil bed
 *     would be one material.
 *
 * Clause 2 yields to clause 1, and `tests/tileVocabulary.test.ts` records the
 * one biome where it has to: `verdigris_spire` declares two accents, CRYSTAL and
 * MERCURY, and MERCURY is a liquid — so its only drawable accent is the material
 * that is also its `glow`. Recorded two-way rather than papered over, because it
 * is a palette fact and `biomes.ts` is where it would be fixed.
 *
 * Falls back to `shell` when a biome declares no solid accent at all: the
 * correct no-op (`shell` is contractually able to hold a wall, `biomes.ts`
 * rule 1) rather than a hole. That branch is unreachable on the shipped roster
 * and the same test says so, so it stays a guard rather than a twelfth of the
 * world nobody noticed.
 */
export function drawableAccentOf(p: BiomeDef['palette']): number {
  const taken = new Set<number>([p.primary, p.secondary, p.shell, p.platform, p.fill, p.glow]);
  let solid = -1;
  for (const a of p.accents) {
    if (M.solid[a] !== 1) continue;
    if (!taken.has(a)) return a;
    if (solid < 0) solid = a;
  }
  return solid < 0 ? p.shell : solid;
}

function volatileFillOf(p: BiomeDef['palette']): number {
  let best = p.fill;
  let bestFuel = M.burnDuration[p.fill];
  const consider = (m: number): void => {
    const f = M.burnDuration[m];
    if (f > bestFuel) {
      bestFuel = f;
      best = m;
    }
  };
  consider(p.secondary);
  consider(p.hazard);
  for (const a of p.accents) consider(a);
  return best;
}

/**
 * The ore field's measured inverse CDF, sampled every 2% of area from 2% to 30%.
 *
 * `nOre` is a 3-octave fbm and the threshold that produces a given share of the
 * rock is a quantile of it, which has no closed form worth writing. So it is
 * measured: 640 000 samples of `fbm2(x/160, y/160, 3, 2, 0.5)`, sorted, read off
 * at each share. The marginal distribution of an fbm does not depend on its
 * frequency, so this table survives a change of `ORE_WAVELENGTH` — but it does
 * *not* survive a change of octaves, gain or lacunarity, and
 * `tests/materialGrain.test.ts` measures the realised share per biome so that
 * such a change fails rather than silently re-tunes twelve biomes.
 */
const ORE_CDF: readonly number[] = [
  0.768, 0.733, 0.710, 0.690, 0.674, 0.659, 0.646, 0.634,
  0.623, 0.613, 0.603, 0.594, 0.584, 0.575, 0.566,
];
const ORE_CDF_STEP = 0.02;

/**
 * How much of a region's rock its vein takes: `terrain.oreDensity`, halved when
 * the accent is not a solid.
 *
 * The half is not tidiness, it is a measured consequence of making veins large.
 * A third of the accent materials in the roster are liquid, sludge, gas or
 * plasma — MERCURY, CRUDE_TAR, MOLTEN_IRON, METHANE, BIO_SLUDGE, NANITE_SWARM,
 * RADIANT_MATTER, VOID_MATTER — and before #60 they arrived as confetti, a few
 * cells in every 8x8 block. A 160-cell vein of one at 28% of the rock is not a
 * pocket of mercury, it is a *lake*: `worldshape` measured the solid fraction
 * in band 5 at 34.8% against its own 38% floor the first time this ran. Bulk
 * rock has to stay rock.
 *
 * Paired with the enclosure rule in `materialise`, so a non-solid vein is both
 * smaller and sealed. Both halves are needed and neither is arbitrary: the cap
 * is about how much of the biome is not rock, the enclosure is about whether
 * what is left of the rock still has a floor.
 */
function veinShare(bio: BiomeDef, accent: number): number {
  return M.solid[accent] === 1 ? bio.terrain.oreDensity : bio.terrain.oreDensity * 0.5;
}

/**
 * `terrain.oreDensity` -> the threshold on `nOre` above which bulk rock becomes
 * the accent material. This is the reader the field never had (#60 item 3).
 *
 * Linear interpolation between measured quantiles, clamped at both ends: a
 * biome asking for less than 2% of its rock in veins gets the 2% threshold
 * rather than an extrapolation off the end of a measurement.
 */
function oreThreshold(density: number): number {
  const i = density / ORE_CDF_STEP - 1;
  if (i <= 0) return ORE_CDF[0];
  if (i >= ORE_CDF.length - 1) return ORE_CDF[ORE_CDF.length - 1];
  const lo = Math.floor(i);
  return lerp(ORE_CDF[lo], ORE_CDF[lo + 1], i - lo);
}

/** Horizontal half-width of a vertical shaft. */
function vRadius(edge: number): number {
  return edge === EdgeType.OPEN ? CORR_V_OPEN : CORR_V_DOOR;
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function chunkKeyOf(cx: number, cy: number): number {
  return ((cx + 32768) << 16) | (cy + 32768);
}
